import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { LessonCompletionPanel } from '../../../components/v2/teacher/LessonCompletionPanel';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import ReactMarkdown from 'react-markdown';
import { assignmentsApi, evidenceApi, lessonRunsApi, lessonStepsApi, studioProgressApi } from '../../../services/apiV2';
import { useLessonControlData } from '../../../hooks/v2/useLessonControlData';
import { useLessonOpsActions } from '../../../hooks/v2/useLessonOpsActions';
import { StepRenderer } from '../../../components/v2/StepRenderer';
import { LessonTeachHeader } from '../../../components/v2/teacher/LessonTeachHeader';
import { lessonResourceSourceLabel, lessonResourceSourceType, presentationSlideImageUrl } from '../../../utils/lessonResources';
import { ResourceOperationsPanel } from '../../../components/v2/ResourceOperationsPanel';
import { LessonOpsHeader } from '../../../components/v2/teacher/LessonOpsHeader';
import { LiveHealthStrip } from '../../../components/v2/teacher/LiveHealthStrip';
import { StepJumpControl } from '../../../components/v2/teacher/StepJumpControl';
import { HookProgressRail } from '../../../components/v2/teacher/HookProgressRail';
import { OpsAssistPanel } from '../../../components/v2/teacher/OpsAssistPanel';
import { OpsSubmissionsPanel } from '../../../components/v2/teacher/OpsSubmissionsPanel';
import { OpsAdaptivePanel } from '../../../components/v2/teacher/OpsAdaptivePanel';
import { OpsAudiencePanel } from '../../../components/v2/teacher/OpsAudiencePanel';
import { TeacherWorkspaceTabs, TeacherWorkspaceTabDefinition } from '../../../components/v2/teacher/TeacherWorkspaceTabs';
import { TeacherLiveDashboardPanel } from '../../../components/v2/teacher/TeacherLiveDashboardPanel';
import { TeacherSignalsPanel } from '../../../components/v2/teacher/TeacherSignalsPanel';
import { TeacherSubmissionsQueuePanel, countSubmissionNeedsAction } from '../../../components/v2/teacher/TeacherSubmissionsQueuePanel';
import { publishLessonRunSync, subscribeLessonRunSync } from '../../../utils/lessonRunSync';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { AppShell } from '../../../components/layout/AppShell';
import { Button } from '../../../components/ui/button';
import {
  ReadinessGateRouteKey,
  RouteStudentsToStepPayload,
  RouteStudentsToStepResponse,
  TeacherStudioBoard,
  ReadinessGateState,
} from '../../../types/v2';
import { PacingTimerCard } from '../../../components/v2/PacingTimerCard';
import { PacingTimerRuntimeState } from '../../../utils/pacingTimer';

interface ControlData {
  run: {
    id: number;
    lesson_plan: number;
    hook: number;
    section: number;
    auto_makeup_for_absent_live_lessons?: boolean;
    started_by: number;
    started_at: string;
    ended_at: string | null;
    status: 'active' | 'paused' | 'completed';
    pacing_mode: 'whole_class' | 'selective';
    delivery_phase?: 'live' | 'studio';
    is_test_mode: boolean;
    reports_included?: boolean;
    reporting_exclusion_reason?: string | null;
    student_response_count?: number;
    real_student_response_count?: number;
    class_current_step: number | null;
    current_step_started_at?: string | null;
    current_step_timer?: PacingTimerRuntimeState | null;
    studio_started_at?: string | null;
    studio_entry_step?: number | null;
    studio_step_ids?: number[];
    live_resource: object;
  };
  steps: Array<{
    id: number;
    order: number;
    title: string;
    stage_tag: string | null;
    runner_phase?: 'live' | 'studio';
    step_kind: string;
    counts_as_evidence: boolean;
    content?: any;
    signal_config?: any;
    evidence_config?: any;
    teacher_answer_key?: any;
    answer_key?: any;
  }>;
  students: Array<{
    id: number;
    name: string;
    group_name?: string;
    joined: boolean;
    has_entered_live?: boolean;
    live_entered_at?: string | null;
    live_last_seen_at?: string | null;
    participation_status?: 'absent' | 'present_no_work' | 'participated';
    daily_attendance_status?: 'present' | 'absent' | null;
    effective_attendance_status?: 'present' | 'absent' | null;
    attendance_local_date?: string;
  }>;
  joined_student_ids: number[];
  entered_live_student_ids?: number[];
  live_presence_summary?: {
    total: number;
    entered_live: number;
    not_entered_live: number;
    absent: number;
    present_no_work: number;
    participated: number;
  };
  target_step_id: number | null;
  current_step_id?: number | null;
  current_step_teacher_answer_key?: any;
  display_step_id?: number | null;
  display_step_started_at?: string | null;
  display_step_teacher_answer_key?: any;
}

interface WalkthroughSnippetSection {
  key: string;
  label: string;
  content: string;
  sendable?: boolean;
}

type ClassAiTab = 'draft' | 'ask';
type TeacherAiTab = 'walkthrough' | 'chat';
type ClassDraftMode = 'quick_explain' | 'step_by_step' | 'hint_only';
type TeacherWalkthroughMode = 'teacher_deep' | 'student_friendly';
type TeacherWalkthroughAdjustment = 'standard' | 'shorter' | 'more_scaffolding';
type LessonStepAiDraftSurface = 'class_draft' | 'teacher_walkthrough';

interface TeacherAiThreadMessage {
  id: number;
  request_text: string;
  response_md: string;
  created_at: string | null;
}

interface TeacherAiThreadResponse {
  root_event_id: number | null;
  messages: TeacherAiThreadMessage[];
}

interface TeacherWalkthroughData {
  final_answer: string;
  step_by_step: string[];
  why_students_miss_it: string;
  what_to_say_out_loud: string;
  quick_check: string;
}

interface LessonStepAiDraft {
  id: number;
  surface: LessonStepAiDraftSurface;
  title: string;
  content_md: string;
  structured_payload: any;
  is_default: boolean;
  updated_at: string | null;
  created_at: string | null;
}

const EMPTY_TEACHER_AI_THREAD: TeacherAiThreadResponse = {
  root_event_id: null,
  messages: [],
};

const EMPTY_TEACHER_WALKTHROUGH: TeacherWalkthroughData = {
  final_answer: '',
  step_by_step: [],
  why_students_miss_it: '',
  what_to_say_out_loud: '',
  quick_check: '',
};

const normalizeAssistMarkdown = (markdown: string, variant: 'default' | 'dark' | 'chat' | 'preview') => {
  let next = String(markdown || '').replace(/\r\n/g, '\n').trim();
  if (!next) return '';
  if (variant === 'chat' || variant === 'preview') {
    next = next.replace(/```(?:[\w+-]+)?\n([^\n`]{1,48})\n```/g, (_match, code) => `\`${String(code || '').trim()}\``);
    next = next.replace(/([^\n])\n(`[^`\n]{1,48}`)\n([^\n])/g, '$1 $2 $3');
  }
  return next.replace(/\n{3,}/g, '\n\n').trim();
};

const MarkdownAssistBody: React.FC<{ markdown: string; tone?: 'default' | 'dark'; variant?: 'default' | 'chat' | 'preview' }> = ({
  markdown,
  tone = 'default',
  variant = 'default',
}) => (
  <div
    className={
      tone === 'dark'
        ? 'prose prose-sm max-w-none text-slate-50 prose-headings:text-slate-50 prose-p:text-slate-50 prose-strong:text-white prose-li:text-slate-50 prose-code:text-slate-100'
        : variant === 'chat'
          ? 'prose prose-sm max-w-none text-slate-800 prose-p:leading-7 prose-li:leading-7 prose-code:text-slate-900'
          : 'prose prose-sm max-w-none text-gray-800'
    }
  >
    <ReactMarkdown
      components={{
        pre({ children }: any) {
          return <>{children}</>;
        },
        code({ inline, children, ...props }: any) {
          const raw = String(children || '').replace(/\n$/, '');
          const compactBlock = !inline && !raw.includes('\n') && raw.length <= 48;
          if (inline || compactBlock) {
            return (
              <code
                {...props}
                className={`rounded px-1.5 py-0.5 text-[0.92em] ${
                  tone === 'dark'
                    ? 'bg-slate-800 text-slate-100'
                    : variant === 'chat'
                      ? 'bg-slate-100 text-slate-900'
                      : 'bg-gray-100 text-gray-800'
                }`}
              >
                {raw}
              </code>
            );
          }
          return !inline ? (
            <pre
              className={`overflow-x-auto rounded p-4 text-xs ${
                tone === 'dark'
                  ? 'bg-slate-950 text-slate-100'
                  : 'bg-slate-900 text-slate-100'
              }`}
            >
              <code {...props}>{raw}</code>
            </pre>
          ) : null;
        },
      }}
    >
      {normalizeAssistMarkdown(markdown, tone === 'dark' ? 'dark' : variant)}
    </ReactMarkdown>
  </div>
);

const getMarkdownSections = (markdownText: string): Array<{ heading: string; body: string }> => {
  const text = String(markdownText || '').trim();
  if (!text) return [];
  const lines = text.split('\n');
  const sections: Array<{ heading: string; body: string }> = [];
  let currentHeading = '';
  let currentBody: string[] = [];

  const pushSection = () => {
    const body = currentBody.join('\n').trim();
    if (!currentHeading && !body) return;
    sections.push({
      heading: currentHeading || 'Message',
      body,
    });
  };

  lines.forEach((line) => {
    const headingMatch = line.match(/^##\s+(.+)$/);
    if (headingMatch) {
      if (currentHeading || currentBody.length > 0) pushSection();
      currentHeading = headingMatch[1].trim();
      currentBody = [];
      return;
    }
    currentBody.push(line);
  });
  pushSection();

  return sections.filter((section) => section.body.trim());
};

const sectionToneClasses = (heading: string) => {
  const normalized = heading.toLowerCase();
  if (normalized.includes('watch out') || normalized.includes('trap') || normalized.includes('miss')) {
    return 'border-amber-200 bg-amber-50 text-amber-950';
  }
  if (normalized.includes('try this') || normalized.includes('quick check') || normalized.includes('check')) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-950';
  }
  if (normalized.includes('step') || normalized.includes('work through')) {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }
  return 'border-indigo-200 bg-indigo-50 text-slate-900';
};

const DraftPreviewBody: React.FC<{ markdown: string }> = ({ markdown }) => {
  const sections = getMarkdownSections(markdown);
  if (sections.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
        Generate a draft or write one to preview it here.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {sections.map((section, index) => (
        <div
          key={`draft-preview-${section.heading}-${index}`}
          className={`rounded-2xl border px-4 py-4 ${sectionToneClasses(section.heading)}`}
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em]">
            {section.heading}
          </div>
          <div className="mt-3 text-sm">
            <MarkdownAssistBody markdown={section.body} variant="preview" />
          </div>
        </div>
      ))}
    </div>
  );
};

const cleanWalkthroughText = (value: unknown): string => String(value || '').trim();

const parseWalkthroughStepItems = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => cleanWalkthroughText(item))
      .filter(Boolean)
      .slice(0, 6);
  }
  const text = cleanWalkthroughText(value);
  if (!text) return [];
  const lineItems = text
    .split('\n')
    .map((line) => line.replace(/^\s*(?:[-*]\s*|\d+[\).\:]\s*)/, '').trim())
    .filter(Boolean);
  if (lineItems.length > 0) return lineItems.slice(0, 6);
  return text
    .split(/(?<=[.!?])\s+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 6);
};

const buildTeacherWalkthroughFromMarkdown = (markdownText: string): TeacherWalkthroughData => ({
  final_answer: cleanWalkthroughText(extractMarkdownSectionBody(markdownText, 'Final Answer')),
  step_by_step: parseWalkthroughStepItems(extractMarkdownSectionBody(markdownText, 'Step-by-Step')),
  why_students_miss_it: cleanWalkthroughText(extractMarkdownSectionBody(markdownText, 'Why Students Miss It')),
  what_to_say_out_loud: cleanWalkthroughText(extractMarkdownSectionBody(markdownText, 'What to Say Out Loud')),
  quick_check: cleanWalkthroughText(extractMarkdownSectionBody(markdownText, 'Quick Check')),
});

const normalizeTeacherWalkthrough = (payload: any, markdownText: string): TeacherWalkthroughData => {
  const structured = payload && typeof payload === 'object' ? payload : {};
  const fallback = buildTeacherWalkthroughFromMarkdown(markdownText);
  const step_by_step = parseWalkthroughStepItems(structured.step_by_step);
  return {
    final_answer: cleanWalkthroughText(structured.final_answer) || fallback.final_answer,
    step_by_step: step_by_step.length > 0 ? step_by_step : fallback.step_by_step,
    why_students_miss_it: cleanWalkthroughText(structured.why_students_miss_it) || fallback.why_students_miss_it,
    what_to_say_out_loud: cleanWalkthroughText(structured.what_to_say_out_loud) || fallback.what_to_say_out_loud,
    quick_check: cleanWalkthroughText(structured.quick_check) || fallback.quick_check,
  };
};

const teacherWalkthroughToMarkdown = (walkthrough: TeacherWalkthroughData | null): string => {
  if (!walkthrough) return '';
  const steps = walkthrough.step_by_step
    .map((item, index) => `${index + 1}. ${item}`)
    .join('\n')
    .trim();
  return [
    '## Final Answer',
    walkthrough.final_answer,
    '',
    '## Step-by-Step',
    steps,
    '',
    '## Why Students Miss It',
    walkthrough.why_students_miss_it,
    '',
    '## What to Say Out Loud',
    walkthrough.what_to_say_out_loud,
    '',
    '## Quick Check',
    walkthrough.quick_check,
  ].join('\n').trim();
};

interface UnifiedSubmissionAttempt {
  id: number;
  student_id: number;
  response?: Record<string, any> | null;
  status?: string;
  evidence_type?: string;
  content?: Record<string, any> | null;
  submitted_at: string;
}

interface ReflectionGatePayload {
  id: number;
  source_step_id: number;
  reflection_step_id: number;
  deterministic_status: 'pass' | 'review' | 'fail';
  ai_status: 'pending' | 'pass' | 'flag' | 'error' | 'skipped';
  final_status: 'allowed' | 'needs_teacher_review' | 'blocked_revision';
  reason_codes: string[];
  suggestions: string[];
  teacher_decision: 'pending' | 'allow' | 'revise';
  teacher_note?: string;
  decided_at?: string | null;
  updated_at?: string | null;
}

interface SubmissionQualityPayload {
  id: number;
  lesson_step_id: number;
  student_id: number;
  status: 'accepted' | 'provisional' | 'needs_teacher_review' | 'resolved';
  reason_codes: string[];
  suggestions: string[];
  deterministic_score?: number | null;
  ai_status?: 'pending' | 'pass' | 'flag' | 'error' | 'skipped';
  ai_confidence?: number | null;
  student_feedback?: string;
  teacher_feedback?: string;
  attempt_count?: number;
  triage_score?: number;
  teacher_decision?: 'pending' | 'clear' | 'assign_makeup' | 'group_reteach';
  teacher_note?: string;
  resolved_at?: string | null;
  updated_at?: string | null;
}

interface UnifiedStudentSubmission {
  student_id: number;
  student_name: string;
  has_submission: boolean;
  signal: {
    latest: UnifiedSubmissionAttempt | null;
    attempt_count: number;
    recent_attempts: UnifiedSubmissionAttempt[];
  };
  evidence: {
    latest: UnifiedSubmissionAttempt | null;
    attempt_count: number;
    recent_attempts: UnifiedSubmissionAttempt[];
  };
  reflection_gate?: ReflectionGatePayload | null;
  submission_quality?: SubmissionQualityPayload | null;
  triage_score?: number;
}

interface UnifiedSubmissionsPayload {
  run_id: number;
  step: {
    id: number;
    title: string;
    order: number;
    stage_tag: string | null;
    step_kind: string;
    counts_as_evidence: boolean;
  };
  summary: {
    students_total: number;
    signals_submitted: number;
    evidence_submitted: number;
    both_submitted: number;
    any_submitted: number;
    gate_allowed?: number;
    gate_review?: number;
    gate_blocked?: number;
    quality_accepted?: number;
    quality_provisional?: number;
    quality_review?: number;
    quality_resolved?: number;
  };
  submissions: UnifiedStudentSubmission[];
}

type TrackTargetScope =
  | 'needs_makeup'
  | 'all'
  | 'absent'
  | 'incomplete'
  | 'present_no_work'
  | 'participated'
  | 'custom';

type EndRunAttendanceEntry = {
  status: 'present' | 'absent';
  assignMakeup: boolean;
};

const RUN_SETTINGS_MODAL_V1 = String(process.env.REACT_APP_RUN_SETTINGS_MODAL_V1 || 'true')
  .trim()
  .toLowerCase() !== 'false';
const LESSON_CONTROL_LAYOUT_V2 = String(process.env.REACT_APP_LESSON_CONTROL_LAYOUT_V2 || 'false')
  .trim()
  .toLowerCase() !== 'false';
const LESSON_CONTROL_FOCUS_BREAKPOINT = Number(process.env.REACT_APP_LESSON_CONTROL_FOCUS_BREAKPOINT || 1280);
const parsePollingMs = (rawValue: string | undefined, fallback: number): number => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1000, Math.floor(parsed));
};
const LESSON_CONTROL_SUBMISSIONS_POLL_MS = parsePollingMs(
  process.env.REACT_APP_LESSON_CONTROL_SUBMISSIONS_POLL_MS,
  1500,
);
const parsePilotIdSet = (raw: string | undefined): Set<number> => (
  new Set(
    String(raw || '')
      .split(',')
      .map((value) => Number(String(value || '').trim()))
      .filter((value) => Number.isInteger(value) && value > 0),
  )
);
const LESSON_CONTROL_LAYOUT_V2_PILOT_RUN_IDS = parsePilotIdSet(process.env.REACT_APP_LESSON_CONTROL_LAYOUT_V2_PILOT_RUN_IDS);
const LESSON_CONTROL_LAYOUT_V2_PILOT_SECTION_IDS = parsePilotIdSet(process.env.REACT_APP_LESSON_CONTROL_LAYOUT_V2_PILOT_SECTION_IDS);
const LESSON_CONTROL_LAYOUT_V2_PILOT_TEACHER_IDS = parsePilotIdSet(process.env.REACT_APP_LESSON_CONTROL_LAYOUT_V2_PILOT_TEACHER_IDS);
const TRACK_TARGET_SCOPE_OPTIONS: Array<{ value: TrackTargetScope; label: string }> = [
  { value: 'needs_makeup', label: 'Needs makeup (absent or incomplete)' },
  { value: 'absent', label: 'Absent only' },
  { value: 'incomplete', label: 'Incomplete only' },
  { value: 'present_no_work', label: 'Entered, no work yet' },
  { value: 'participated', label: 'Participated' },
  { value: 'all', label: 'Entire class' },
  { value: 'custom', label: 'Selected students' },
];

const TRACK_SCOPE_DISPLAY_LABELS: Record<TrackTargetScope, string> = {
  needs_makeup: 'Needs makeup',
  absent: 'Absent only',
  incomplete: 'Incomplete only',
  present_no_work: 'Entered, no work yet',
  participated: 'Participated',
  all: 'Entire class',
  custom: 'Selected students',
};

const studentInBucket = (
  buckets: Record<string, number[]>,
  bucket: string,
  studentId: number,
): boolean => {
  const values = buckets[bucket];
  if (!Array.isArray(values)) return false;
  return values.some((value: any) => Number(value) === studentId);
};

const queueMembershipLabels = (
  buckets: Record<string, number[]>,
  studentId: number,
): string[] => {
  const labels: string[] = [];
  if (studentInBucket(buckets, 'absent', studentId)) labels.push('Absent');
  if (studentInBucket(buckets, 'present_no_work', studentId)) labels.push('Entered, no work');
  if (studentInBucket(buckets, 'participated', studentId)) labels.push('Participated');
  if (studentInBucket(buckets, 'incomplete', studentId)) labels.push('Incomplete');
  if (studentInBucket(buckets, 'completed', studentId)) labels.push('Completed');
  return labels;
};

const buildQueueReason = ({
  buckets,
  studentId,
  scope,
  matchesCurrentDraft,
  activeQueue,
}: {
  buckets: Record<string, number[]>;
  studentId: number;
  scope: TrackTargetScope;
  matchesCurrentDraft: boolean;
  activeQueue: boolean;
}): string => {
  const scopeLabel = TRACK_SCOPE_DISPLAY_LABELS[scope] || 'Current scope';
  let reason = '';
  if (activeQueue) {
    reason = matchesCurrentDraft
      ? `Matches current target scope (${scopeLabel}).`
      : `Still active from a previous save; does not match current target scope (${scopeLabel}). Save settings to re-sync or remove directly.`;
  } else {
    reason = scope === 'custom'
      ? 'Selected in current student list. Save settings to activate.'
      : `Will be targeted by current settings (${scopeLabel}) when you save.`;
  }
  const membership = queueMembershipLabels(buckets, studentId);
  if (membership.length > 0) {
    reason = `${reason} Live status: ${membership.join(', ')}.`;
  }
  return reason;
};

type LessonControlSurface = 'full' | 'teach' | 'ops';
type MobileOpsTab = 'signals' | 'submissions' | 'adaptive' | 'audience';
type WorkspaceTab = 'step' | 'assist' | 'signals' | 'submissions' | 'adaptive' | 'audience' | 'studio';

const STUDIO_RELEASE_ROUTE_LABELS: Record<string, string> = {
  pending: 'Start Here',
  go_test: 'Ready for Studio',
  one_more_fix: 'Support First',
  build_with_me: 'Teacher Table',
};

const studioReleaseRouteLabel = (routeKey?: string | null, fallback?: string | null): string => {
  const key = String(routeKey || '').trim().toLowerCase();
  if (key && STUDIO_RELEASE_ROUTE_LABELS[key]) return STUDIO_RELEASE_ROUTE_LABELS[key];
  if (fallback && fallback !== 'Pending') return fallback;
  return STUDIO_RELEASE_ROUTE_LABELS.pending;
};

const pathwayTypeLabel = (type?: string | null): string => {
  const key = String(type || '').trim().toLowerCase();
  if (key === 'support') return 'Support pathway';
  if (key === 'extension') return 'Extension pathway';
  if (key === 'activation') return 'Start Here pathway';
  if (key === 'teacher_table') return 'Teacher Table';
  if (key === 'core') return 'Core Studio';
  return key ? key.replace(/_/g, ' ') : 'Core Studio';
};

const pathwayMetaForStep = (step: { content?: Record<string, any> | null } | null | undefined): Record<string, any> | null => {
  const pathway = step?.content?.pathway;
  return pathway && typeof pathway === 'object' && !Array.isArray(pathway) ? pathway : null;
};

const studioTargetOptionLabel = (step: { order?: number; title?: string | null; content?: Record<string, any> | null }): string => {
  const pathway = pathwayMetaForStep(step);
  const base = String(pathway?.route_label || step.title || `Step ${Number(step.order || 0) + 1}`).trim();
  const details = [
    pathwayTypeLabel(pathway?.type),
    pathway?.teacher_routeable === true ? 'routeable' : '',
    pathway?.route_only === true ? 'hidden until routed' : '',
  ].filter(Boolean).join(', ');
  return details ? `${base} (${details})` : base;
};

const workspaceTabForAlert = (alertId: string): WorkspaceTab => {
  if (alertId === 'adaptive') return 'adaptive';
  if (alertId === 'submissions') return 'submissions';
  if (alertId === 'signals_pending') return 'signals';
  if (alertId === 'assist') return 'assist';
  return 'step';
};

interface V2LessonControlProps {
  surface?: LessonControlSurface;
  embeddedRunId?: number;
  onRunEnded?: () => void;
  onNextActivity?: () => void;
  nextActivityLabel?: string;
  transitionBusy?: boolean;
}

interface TrackAssignmentAudience {
  counts?: Record<string, number>;
  required_steps_total?: number;
  presence_summary?: {
    total?: number;
    entered_live?: number;
    not_entered_live?: number;
    absent?: number;
    present_no_work?: number;
    participated?: number;
  };
  buckets?: Record<string, number[]>;
}

const VOCAB_STOPWORDS = new Set([
  'about', 'after', 'again', 'against', 'all', 'also', 'another', 'because', 'before', 'being',
  'between', 'both', 'but', 'choose', 'class', 'current', 'define', 'during', 'each', 'from',
  'have', 'into', 'just', 'lesson', 'might', 'more', 'most', 'need', 'only', 'other', 'over',
  'same', 'should', 'some', 'step', 'students', 'system', 'than', 'that', 'their', 'there',
  'these', 'they', 'this', 'those', 'through', 'under', 'using', 'very', 'what', 'when', 'where',
  'which', 'while', 'with', 'would', 'your',
]);

const extractCandidateTerms = (text: string, maxTerms = 24): string[] => {
  const normalized = String(text || '').replace(/[`*_#>[\]{}()]/g, ' ');
  const matches = normalized.match(/\b[A-Za-z][A-Za-z\-']{2,}\b/g) || [];
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const raw of matches) {
    const token = String(raw || '').trim();
    if (!token) continue;
    const lowered = token.toLowerCase();
    if (VOCAB_STOPWORDS.has(lowered)) continue;
    if (lowered.length < 3) continue;
    if (/^\d/.test(lowered)) continue;
    if (seen.has(lowered)) continue;
    seen.add(lowered);
    const formatted = lowered.includes('-')
      ? lowered.split('-').map((part) => part ? `${part[0].toUpperCase()}${part.slice(1)}` : part).join('-')
      : `${lowered[0].toUpperCase()}${lowered.slice(1)}`;
    terms.push(formatted);
    if (terms.length >= maxTerms) break;
  }
  return terms;
};

const extractStepVocabularyCandidates = (step: any, teacherAnswerKey: any, teacherNotes: string): string[] => {
  if (!step || typeof step !== 'object') return [];
  const content = step.content && typeof step.content === 'object' ? step.content : {};
  const sourceItem = content.source_item && typeof content.source_item === 'object'
    ? content.source_item
    : (content.sourceItem && typeof content.sourceItem === 'object' ? content.sourceItem : {});
  const signalConfig = step.signal_config && typeof step.signal_config === 'object' ? step.signal_config : {};
  const rawOptions = Array.isArray(signalConfig.options) ? signalConfig.options : [];
  const parts: string[] = [];

  if (typeof step.title === 'string' && step.title.trim()) parts.push(step.title.trim());
  if (typeof content.prompt_md === 'string' && content.prompt_md.trim()) parts.push(content.prompt_md.trim());
  if (Array.isArray(content.blocks)) {
    for (const block of content.blocks) {
      if (!block || typeof block !== 'object') continue;
      const blockType = String(block.type || '').toLowerCase();
      if (blockType === 'text') {
        const text = String(block.text || '').trim();
        if (text) parts.push(text);
      }
      if (blockType === 'markdown' || blockType === 'md') {
        const text = String(block.markdown || block.text || '').trim();
        if (text) parts.push(text);
      }
    }
  }
  if (typeof sourceItem.question === 'string' && sourceItem.question.trim()) {
    parts.push(sourceItem.question.trim());
  }
  if (Array.isArray(sourceItem.left_items)) {
    parts.push(sourceItem.left_items.map((item: any) => String(item || '').trim()).join(' '));
  }
  if (Array.isArray(sourceItem.right_items)) {
    parts.push(sourceItem.right_items.map((item: any) => String(item || '').trim()).join(' '));
  }
  if (Array.isArray(sourceItem.items)) {
    parts.push(sourceItem.items.map((item: any) => String(item || '').trim()).join(' '));
  }
  if (rawOptions.length > 0) {
    parts.push(rawOptions.map((item: any) => String(item || '').trim()).join(' '));
  }
  if (teacherAnswerKey && typeof teacherAnswerKey === 'object') {
    if (teacherAnswerKey.kind === 'matching' && teacherAnswerKey.pairs && typeof teacherAnswerKey.pairs === 'object') {
      parts.push(Object.keys(teacherAnswerKey.pairs).join(' '));
    }
    if (teacherAnswerKey.kind === 'mcq_single' || teacherAnswerKey.kind === 'mcq_multi') {
      const keyOptions = Array.isArray(teacherAnswerKey.options) ? teacherAnswerKey.options : [];
      if (keyOptions.length > 0) {
        parts.push(keyOptions.map((item: any) => String(item || '').trim()).join(' '));
      }
    }
    if (teacherAnswerKey.kind === 'text' && typeof teacherAnswerKey.answer_code === 'string' && teacherAnswerKey.answer_code.trim()) {
      parts.push(teacherAnswerKey.answer_code.trim());
    }
  }
  if (teacherNotes && teacherNotes.trim()) parts.push(teacherNotes.trim());

  const terms = extractCandidateTerms(parts.filter(Boolean).join('\n'), 24);
  return terms.slice(0, 12);
};

const EXPLAIN_ACTION_PROMPT_PATTERNS = [
  /^(?:try|do|write|submit|discuss|share|check|complete|solve|pair|turn and talk|talk with|work with|start by|open)\b/i,
];

const normalizeWordTokens = (text: string): string[] => (
  String(text || '').toLowerCase().match(/[a-z0-9']+/g) || []
);

const hasConsecutiveWordOverlap = (sourceText: string, candidateText: string, minWords = 8): boolean => {
  const sourceTokens = normalizeWordTokens(sourceText);
  const candidateTokens = normalizeWordTokens(candidateText);
  if (sourceTokens.length < minWords || candidateTokens.length < minWords) return false;
  const sourceNgrams = new Set<string>();
  for (let i = 0; i <= sourceTokens.length - minWords; i += 1) {
    sourceNgrams.add(sourceTokens.slice(i, i + minWords).join(' '));
  }
  for (let i = 0; i <= candidateTokens.length - minWords; i += 1) {
    if (sourceNgrams.has(candidateTokens.slice(i, i + minWords).join(' '))) return true;
  }
  return false;
};

const isActionPromptLine = (rawLine: string): boolean => {
  let line = String(rawLine || '').trim();
  if (!line) return false;
  line = line.replace(/^\s*(?:[-*]\s*|\d+[\)\.\:]\s*)/, '').trim();
  line = line.replace(/^[A-Za-z][A-Za-z\s\-]{0,40}:\s*/, '').trim();
  if (!line) return false;
  if (/try it now/i.test(line)) return true;
  if (/\b(?:write|submit|discuss|share|check|complete|solve|open|reopen|read|choose)\b/i.test(line)) {
    if (/\byou\b/i.test(line) || /^as you\b/i.test(line) || /^when you\b/i.test(line)) return true;
  }
  return EXPLAIN_ACTION_PROMPT_PATTERNS.some((pattern) => pattern.test(line));
};

const containsNewActionPrompt = (text: string): boolean => (
  String(text || '').split('\n').some((line) => isActionPromptLine(line))
);

const validateExplainBroadcastContent = ({
  content,
  stepPromptMd,
}: {
  content: string;
  stepPromptMd: string;
}): string[] => {
  const issues: string[] = [];
  const text = String(content || '').trim();
  const stepPrompt = String(stepPromptMd || '').trim();
  if (!text) {
    issues.push('Explanation text is required.');
    return issues;
  }
  if (containsNewActionPrompt(text)) {
    issues.push('Explain content must clarify only and cannot add new student actions.');
  }
  if (stepPrompt && hasConsecutiveWordOverlap(stepPrompt, text, 8)) {
    issues.push('Explain content is too close to the step wording; paraphrase it in simpler language.');
  }
  return issues;
};

const extractMarkdownSectionBody = (markdownText: string, headingLabel: string): string => {
  const lines = String(markdownText || '').split('\n');
  const target = String(headingLabel || '').trim().toLowerCase();
  let activeHeading = '';
  let activeBody: string[] = [];
  const sections = new Map<string, string>();

  const pushSection = () => {
    if (!activeHeading) return;
    const body = activeBody.join('\n').trim();
    if (!body) return;
    sections.set(activeHeading.toLowerCase(), body);
  };

  lines.forEach((line) => {
    const headingMatch = line.match(/^##\s+(.+)$/);
    if (headingMatch) {
      pushSection();
      activeHeading = headingMatch[1].trim();
      activeBody = [];
      return;
    }
    if (!activeHeading) return;
    activeBody.push(line);
  });
  pushSection();

  return sections.get(target) || '';
};

const extractMarkdownSectionBodyAny = (markdownText: string, headingLabels: string[]): string => {
  for (const headingLabel of headingLabels) {
    const value = extractMarkdownSectionBody(markdownText, headingLabel);
    if (value) return value;
  }
  return '';
};

const buildClassDraftFromWalkthrough = (markdownText: string): string => {
  const sayOutLoud = extractMarkdownSectionBodyAny(markdownText, ['What to Say Out Loud', "Let's Focus", 'What to Say']);
  const stepByStep = extractMarkdownSectionBodyAny(markdownText, ['Step-by-Step', 'How to Work Through It']);
  const commonTrap = extractMarkdownSectionBodyAny(markdownText, ['Why Students Miss It', 'Watch Out', 'Common Trap']);
  const quickCheck = extractMarkdownSectionBodyAny(markdownText, ['Quick Check', 'Try This']);
  const parts: string[] = [];
  if (sayOutLoud) parts.push(`## Let's Focus\n${sayOutLoud}`);
  if (stepByStep) parts.push(`## Step-by-Step\n${stepByStep}`);
  if (commonTrap) parts.push(`## Watch Out\n${commonTrap}`);
  if (quickCheck) parts.push(`## Try This\n${quickCheck}`);
  return parts.join('\n\n').trim();
};

const buildClassDraftFromWalkthroughData = (walkthrough: TeacherWalkthroughData | null): string => {
  if (!walkthrough) return '';
  const steps = walkthrough.step_by_step
    .slice(0, 4)
    .map((item, index) => `${index + 1}. ${item}`);
  const parts: string[] = [];
  if (walkthrough.what_to_say_out_loud) {
    parts.push(`## Let's Focus\n${walkthrough.what_to_say_out_loud}`);
  }
  if (steps.length > 0) {
    parts.push(`## Step-by-Step\n${steps.join('\n')}`);
  }
  if (walkthrough.why_students_miss_it) {
    parts.push(`## Watch Out\n${walkthrough.why_students_miss_it}`);
  }
  if (walkthrough.quick_check) {
    parts.push(`## Try This\n${walkthrough.quick_check}`);
  }
  return parts.join('\n\n').trim();
};

const compactCriticalAlertMessage = (message: string): string => {
  const normalized = String(message || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (/start independent work/i.test(normalized) && /studio tail/i.test(normalized)) {
    return 'Start Independent Work needed.';
  }
  const pendingMatch = normalized.match(/^(\d+) students? pending signal response/i);
  if (pendingMatch) {
    return `${pendingMatch[1]} pending signal${pendingMatch[1] === '1' ? '' : 's'}.`;
  }
  if (normalized.length <= 72) return normalized;
  return `${normalized.slice(0, 69).trim()}...`;
};

const normalizeLessonStepAiDraft = (payload: any): LessonStepAiDraft | null => {
  const id = Number(payload?.id);
  const surface = String(payload?.surface || '').trim() as LessonStepAiDraftSurface;
  if (!Number.isInteger(id) || (surface !== 'class_draft' && surface !== 'teacher_walkthrough')) return null;
  return {
    id,
    surface,
    title: String(payload?.title || '').trim() || (surface === 'class_draft' ? 'Saved class draft' : 'Saved walkthrough'),
    content_md: String(payload?.content_md || ''),
    structured_payload: payload?.structured_payload && typeof payload.structured_payload === 'object'
      ? payload.structured_payload
      : {},
    is_default: Boolean(payload?.is_default),
    updated_at: payload?.updated_at ? String(payload.updated_at) : null,
    created_at: payload?.created_at ? String(payload.created_at) : null,
  };
};

export const V2LessonControl: React.FC<V2LessonControlProps> = ({ surface, embeddedRunId, onRunEnded, onNextActivity, nextActivityLabel, transitionBusy }) => {
  const { runId: routeRunId } = useParams();
  const runId = embeddedRunId ? String(embeddedRunId) : routeRunId;
  const navigate = useNavigate();
  const location = useLocation();
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_lesson_control');
  const [viewportWidth, setViewportWidth] = useState<number>(() => (
    typeof window !== 'undefined' ? window.innerWidth : LESSON_CONTROL_FOCUS_BREAKPOINT + 1
  ));
  const [stepJumpOpen, setStepJumpOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [presentationResource, setPresentationResource] = useState<any | null>(null);
  const [presentationSlideIndex, setPresentationSlideIndex] = useState(0);
  const [presentationDisplayLoading, setPresentationDisplayLoading] = useState(false);
  const [presentationDisplayError, setPresentationDisplayError] = useState('');
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState<WorkspaceTab>('step');
  const studioDefaultedRunIdRef = useRef<number | null>(null);
  const [showInlineOpsPanel, setShowInlineOpsPanel] = useState(false);
  const [inspectedStudentId, setInspectedStudentId] = useState<number | null>(null);
  const [mobileOpsDrawerOpen, setMobileOpsDrawerOpen] = useState(false);
  const [mobileOpsTab, setMobileOpsTab] = useState<MobileOpsTab>('signals');
  const [submissionsInbox, setSubmissionsInbox] = useState<UnifiedSubmissionsPayload | null>(null);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState<string | null>(null);
  const [studioBoard, setStudioBoard] = useState<TeacherStudioBoard | null>(null);
  const [studioLoading, setStudioLoading] = useState(false);
  const [studioError, setStudioError] = useState<string | null>(null);
  const [studioActionLoading, setStudioActionLoading] = useState<'enter' | 'ack' | 'resolve' | null>(null);
  const [studioDrawerStudentId, setStudioDrawerStudentId] = useState<number | null>(null);
  const [studioNotice, setStudioNotice] = useState('');
  const [readinessGate, setReadinessGate] = useState<ReadinessGateState | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState<string | null>(null);
  const [readinessSelectedIds, setReadinessSelectedIds] = useState<number[]>([]);
  const [readinessGoTestStepId, setReadinessGoTestStepId] = useState<number | null>(null);
  const [readinessFixStepId, setReadinessFixStepId] = useState<number | null>(null);
  const [readinessRouting, setReadinessRouting] = useState<ReadinessGateRouteKey | null>(null);
  const [hideAbsentInSubmissions, setHideAbsentInSubmissions] = useState(true);
  const [expandedSubmissionStudentIds, setExpandedSubmissionStudentIds] = useState<Set<number>>(new Set());
  const [redoRequested, setRedoRequested] = useState<number[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [attendanceSavingStudentIds, setAttendanceSavingStudentIds] = useState<number[]>([]);
  const [attendanceError, setAttendanceError] = useState<string | null>(null);
  const [pacingTimerLoading, setPacingTimerLoading] = useState(false);
  const [pacingTimerError, setPacingTimerError] = useState<string | null>(null);
  const [urgencyPromptLoading, setUrgencyPromptLoading] = useState<string | null>(null);
  const [urgencyPromptNotice, setUrgencyPromptNotice] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedExplain, setCopiedExplain] = useState(false);
  const submissionsRequestInFlightRef = useRef(false);
  const normalizeStudentIds = useCallback((raw: any[]): number[] => (
    Array.from(
      new Set(
        (Array.isArray(raw) ? raw : [])
          .map((value: any) => Number(value))
          .filter((value: number) => Number.isInteger(value)),
      ),
    ).sort((a, b) => a - b)
  ), []);
  const {
    controlData,
    setControlData,
    loading,
    error,
    lastRunContext,
    setLastRunContext,
    loadControl,
    trackAssignment,
    setTrackAssignment,
    trackDueAt,
    setTrackDueAt,
    trackTargetScope,
    setTrackTargetScope,
    trackTargetStudentIds,
    setTrackTargetStudentIds,
    trackAudience,
    trackAssignmentInfo,
    trackError,
    setTrackError,
    trackSummary,
    loadTrackAssignment,
    liveSignalStats,
    loadLiveSignalStats,
    liveDashboard,
    liveDashboardLoading,
    liveDashboardError,
    loadLiveDashboard,
  } = useLessonControlData({ runId, normalizeStudentIds });
  const lessonOpsActions = useLessonOpsActions({ runId });
  const [trackLastSavedAt, setTrackLastSavedAt] = useState<Date | null>(null);
  const [runSettingsOpen, setRunSettingsOpen] = useState(false);
  const [runSettingsDraftTestMode, setRunSettingsDraftTestMode] = useState(false);
  const [runSettingsDraftTrackAssignment, setRunSettingsDraftTrackAssignment] = useState(false);
  const [runSettingsDraftTargetScope, setRunSettingsDraftTargetScope] = useState<TrackTargetScope>('needs_makeup');
  const [runSettingsDraftDueAt, setRunSettingsDraftDueAt] = useState('');
  const [runSettingsDraftStudentIds, setRunSettingsDraftStudentIds] = useState<number[]>([]);
  const [runSettingsDraftReviewAccess, setRunSettingsDraftReviewAccess] = useState<'off' | 'after_end' | 'always'>('after_end');
  const [runSettingsDraftAllowCatchup, setRunSettingsDraftAllowCatchup] = useState(false);
  const [runSettingsDraftShowReviewAnswers, setRunSettingsDraftShowReviewAnswers] = useState(false);
  const [runSettingsSaving, setRunSettingsSaving] = useState(false);
  const [runSettingsError, setRunSettingsError] = useState<string | null>(null);
  const [makeupExcusingStudentIds, setMakeupExcusingStudentIds] = useState<Set<number>>(new Set());
  const [makeupRemovalSelection, setMakeupRemovalSelection] = useState<Set<number>>(new Set());
  const [trackLoading, setTrackLoading] = useState(false);
  const [testModeSaving, setTestModeSaving] = useState(false);
  const [testModeError, setTestModeError] = useState<string | null>(null);
  const [endRunDialogOpen, setEndRunDialogOpen] = useState(false);
  const [endRunCreateMakeup, setEndRunCreateMakeup] = useState(false);
  const [endRunDueAt, setEndRunDueAt] = useState('');
  const [endRunAttendance, setEndRunAttendance] = useState<Record<number, EndRunAttendanceEntry>>({});
  const [endRunSubmitting, setEndRunSubmitting] = useState(false);
  const [endRunError, setEndRunError] = useState<string | null>(null);
  const [revealSubmitting, setRevealSubmitting] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [revealSuccess, setRevealSuccess] = useState(false);
  const [activeBucket, setActiveBucket] = useState<string | null>(null);
  const [activeLiveDashboardBucket, setActiveLiveDashboardBucket] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiStepByStep, setAiStepByStep] = useState('');
  const [aiCommonMistakes, setAiCommonMistakes] = useState<string[]>([]);
  const [currentAssistFeedbackId, setCurrentAssistFeedbackId] = useState<number | null>(null);
  const [currentAssistActionType, setCurrentAssistActionType] = useState<string>('');
  const [assistUsefulnessVote, setAssistUsefulnessVote] = useState<'up' | 'down' | null>(null);
  const [assistUsedInClass, setAssistUsedInClass] = useState<boolean | null>(null);
  const [assistFeedbackSaving, setAssistFeedbackSaving] = useState(false);
  const [assistFeedbackError, setAssistFeedbackError] = useState<string | null>(null);
  const [assistRankings, setAssistRankings] = useState<any[]>([]);
  const [assistRankingsLoading, setAssistRankingsLoading] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle'|'copied'|'failed'|'empty'>('idle');
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistError, setAiAssistError] = useState<string | null>(null);
  const [aiDelayRevealTriggered, setAiDelayRevealTriggered] = useState(false);
  const [aiDelayRevealReason, setAiDelayRevealReason] = useState('');
  const [classAiModalOpen, setClassAiModalOpen] = useState(false);
  const [classAiTab, setClassAiTab] = useState<ClassAiTab>('draft');
  const [classDraftMode, setClassDraftMode] = useState<ClassDraftMode>('quick_explain');
  const [classDraft, setClassDraft] = useState('');
  const [classDraftEditorOpen, setClassDraftEditorOpen] = useState(false);
  const [classDraftLoading, setClassDraftLoading] = useState(false);
  const [classDraftSending, setClassDraftSending] = useState(false);
  const [classDraftSaving, setClassDraftSaving] = useState(false);
  const [classAiError, setClassAiError] = useState<string | null>(null);
  const [classAiSuccess, setClassAiSuccess] = useState<string | null>(null);
  const [classAskPrompt, setClassAskPrompt] = useState('');
  const [classAskSending, setClassAskSending] = useState(false);
  const [classAskThreadLoading, setClassAskThreadLoading] = useState(false);
  const [classAskThreadError, setClassAskThreadError] = useState<string | null>(null);
  const [classAskThread, setClassAskThread] = useState<TeacherAiThreadResponse>(EMPTY_TEACHER_AI_THREAD);
  const [stepAiDrafts, setStepAiDrafts] = useState<LessonStepAiDraft[]>([]);
  const [stepAiDraftsLoading, setStepAiDraftsLoading] = useState(false);
  const [activeClassDraftId, setActiveClassDraftId] = useState<number | null>(null);
  const [classDraftLibraryNotice, setClassDraftLibraryNotice] = useState<string | null>(null);
  const [teacherAiModalOpen, setTeacherAiModalOpen] = useState(false);
  const [teacherAiTab, setTeacherAiTab] = useState<TeacherAiTab>('walkthrough');
  const [teacherWalkthroughMode, setTeacherWalkthroughMode] = useState<TeacherWalkthroughMode>('teacher_deep');
  const [teacherWalkthroughAdjustment, setTeacherWalkthroughAdjustment] = useState<TeacherWalkthroughAdjustment>('standard');
  const [teacherWalkthroughLoading, setTeacherWalkthroughLoading] = useState(false);
  const [teacherWalkthroughSaving, setTeacherWalkthroughSaving] = useState(false);
  const [teacherWalkthroughError, setTeacherWalkthroughError] = useState<string | null>(null);
  const [teacherWalkthroughMd, setTeacherWalkthroughMd] = useState('');
  const [teacherWalkthrough, setTeacherWalkthrough] = useState<TeacherWalkthroughData | null>(null);
  const [activeTeacherWalkthroughDraftId, setActiveTeacherWalkthroughDraftId] = useState<number | null>(null);
  const [teacherWalkthroughLibraryNotice, setTeacherWalkthroughLibraryNotice] = useState<string | null>(null);
  const [teacherChatPrompt, setTeacherChatPrompt] = useState('');
  const [teacherChatSending, setTeacherChatSending] = useState(false);
  const [teacherChatThreadLoading, setTeacherChatThreadLoading] = useState(false);
  const [teacherChatThreadError, setTeacherChatThreadError] = useState<string | null>(null);
  const [teacherChatThread, setTeacherChatThread] = useState<TeacherAiThreadResponse>(EMPTY_TEACHER_AI_THREAD);
  const [teacherChatUseLessonContext, setTeacherChatUseLessonContext] = useState(true);
  const [stepByStepModalOpen, setStepByStepModalOpen] = useState(false);
  const [stepByStepModalTitle, setStepByStepModalTitle] = useState('Coding step-by-step');
  const [stepByStepActionType, setStepByStepActionType] = useState<'coding_step_by_step' | 'explain_step_by_step'>('coding_step_by_step');
  const [stepByStepMode, setStepByStepMode] = useState<'teacher_deep' | 'student_friendly'>('teacher_deep');
  const [stepByStepAdjustment, setStepByStepAdjustment] = useState<'standard' | 'shorter' | 'more_scaffolding'>('standard');
  const [stepByStepCopyStatus, setStepByStepCopyStatus] = useState<'idle' | 'copied_all' | 'copied_code' | 'empty' | 'failed'>('idle');
  const [snippetSectionKeys, setSnippetSectionKeys] = useState<string[]>([]);
  const [snippetDraft, setSnippetDraft] = useState('');
  const [snippetUseLoading, setSnippetUseLoading] = useState(false);
  const [snippetSendError, setSnippetSendError] = useState<string | null>(null);
  const [snippetSendSuccess, setSnippetSendSuccess] = useState<string | null>(null);
  const [adaptiveState, setAdaptiveState] = useState<any>(null);
  const [adaptiveSuggestions, setAdaptiveSuggestions] = useState<any[]>([]);
  const [adaptiveLoading, setAdaptiveLoading] = useState(false);
  const [adaptiveError, setAdaptiveError] = useState<string | null>(null);
  const [stepNavError, setStepNavError] = useState<string | null>(null);
  const [stepNavLoadingId, setStepNavLoadingId] = useState<number | null>(null);
  const [expandedAdaptiveTargets, setExpandedAdaptiveTargets] = useState<Set<number>>(new Set());
  const [showReviewed, setShowReviewed] = useState(false);
  const [lastAdaptiveApplication, setLastAdaptiveApplication] = useState<{
    repair: number;
    extend: number;
  } | null>(null);
  const teacherWalkthroughExportMd = useMemo(
    () => teacherWalkthroughMd || teacherWalkthroughToMarkdown(teacherWalkthrough),
    [teacherWalkthrough, teacherWalkthroughMd],
  );
  const classSavedDrafts = useMemo(
    () => stepAiDrafts.filter((draft) => draft.surface === 'class_draft'),
    [stepAiDrafts],
  );
  const teacherWalkthroughSavedDrafts = useMemo(
    () => stepAiDrafts.filter((draft) => draft.surface === 'teacher_walkthrough'),
    [stepAiDrafts],
  );
  const activeClassSavedDraft = useMemo(
    () => classSavedDrafts.find((draft) => draft.id === activeClassDraftId) || null,
    [activeClassDraftId, classSavedDrafts],
  );
  const activeTeacherWalkthroughSavedDraft = useMemo(
    () => teacherWalkthroughSavedDrafts.find((draft) => draft.id === activeTeacherWalkthroughDraftId) || null,
    [activeTeacherWalkthroughDraftId, teacherWalkthroughSavedDrafts],
  );

  const layoutV2Enabled = useMemo(() => {
    if (surface === 'teach' || surface === 'ops') return true;
    if (LESSON_CONTROL_LAYOUT_V2) return true;

    const parsedRouteRunId = Number(runId);
    if (Number.isInteger(parsedRouteRunId) && LESSON_CONTROL_LAYOUT_V2_PILOT_RUN_IDS.has(parsedRouteRunId)) {
      return true;
    }

    const run = controlData?.run;
    if (!run) return false;
    if (LESSON_CONTROL_LAYOUT_V2_PILOT_RUN_IDS.has(Number(run.id))) return true;
    if (LESSON_CONTROL_LAYOUT_V2_PILOT_SECTION_IDS.has(Number(run.section))) return true;
    if (LESSON_CONTROL_LAYOUT_V2_PILOT_TEACHER_IDS.has(Number(run.started_by))) return true;
    return false;
  }, [controlData?.run, runId, surface]);

  const effectiveSurface: LessonControlSurface = useMemo(() => {
    if (surface) return surface;
    return layoutV2Enabled ? 'teach' : 'full';
  }, [layoutV2Enabled, surface]);
  const isOpsSurface = effectiveSurface === 'ops';
  const isTeachSurface = effectiveSurface === 'teach';
  const isFullSurface = effectiveSurface === 'full';
  const workspaceModeEnabled = isTeachSurface && (layoutV2Enabled || Boolean(embeddedRunId));
  const isFocusViewport = viewportWidth < LESSON_CONTROL_FOCUS_BREAKPOINT;
  const realStudentResponseCount = Number(controlData?.run?.real_student_response_count || 0);
  const testModeBlockedByStudentWork = realStudentResponseCount >= 2;
  const showInlineOpsRail = isTeachSurface && layoutV2Enabled && !isFocusViewport && showInlineOpsPanel;
  const showStepListAside = !isOpsSurface && (isFullSurface || !layoutV2Enabled);
  const showTeachCards = !isOpsSurface;
  const showOpsCardsInMain = isOpsSurface || isFullSurface;
  const showOpsSidebar = isOpsSurface || isFullSurface || showInlineOpsRail;
  const showCompactStepNav = isTeachSurface && layoutV2Enabled && !workspaceModeEnabled;
  const showCompactLiveStrip = isTeachSurface && layoutV2Enabled && !workspaceModeEnabled;

  const computeMinutesUntil = useCallback((iso?: string | null) => {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    const diffMs = t - Date.now();
    const mins = Math.max(0, Math.round(diffMs / 60000));
    return mins;
  }, []);

  const validateTrackSettings = useCallback((
    scope: TrackTargetScope,
    dueAtValue: string,
    customTargetIds: number[],
  ): string | null => {
    const parsedDueAt = dueAtValue ? new Date(dueAtValue) : null;
    if (parsedDueAt && Number.isNaN(parsedDueAt.getTime())) {
      return 'Invalid due date value.';
    }
    if (scope === 'custom' && customTargetIds.length === 0) {
      return 'Select at least one student for custom assignment targeting.';
    }
    return null;
  }, []);

  const openRunSettings = useCallback((overrides?: Partial<{
    testMode: boolean;
    trackAssignment: boolean;
    targetScope: TrackTargetScope;
    dueAt: string;
    targetStudentIds: number[];
  }>) => {
    if (!controlData) return;
    setRunSettingsDraftTestMode(
      typeof overrides?.testMode === 'boolean'
        ? overrides.testMode
        : Boolean(controlData.run.is_test_mode),
    );
    setRunSettingsDraftTrackAssignment(
      typeof overrides?.trackAssignment === 'boolean'
        ? overrides.trackAssignment
        : trackAssignment,
    );
    setRunSettingsDraftTargetScope(overrides?.targetScope || trackTargetScope);
    setRunSettingsDraftDueAt(overrides?.dueAt ?? trackDueAt);
    setRunSettingsDraftStudentIds(normalizeStudentIds(overrides?.targetStudentIds ?? trackTargetStudentIds));
    const reviewAccess = String(controlData.run.review_access || 'after_end').trim().toLowerCase();
    setRunSettingsDraftReviewAccess(
      reviewAccess === 'off' || reviewAccess === 'always' ? reviewAccess : 'after_end',
    );
    setRunSettingsDraftAllowCatchup(Boolean(controlData.run.allow_catchup));
    setRunSettingsDraftShowReviewAnswers(Boolean(controlData.run.show_assessment_answers_in_review));
    setRunSettingsError(null);
    setRunSettingsOpen(true);
  }, [
    controlData,
    normalizeStudentIds,
    trackAssignment,
    trackDueAt,
    trackTargetScope,
    trackTargetStudentIds,
  ]);

  const resetRunSettingsDraft = useCallback(() => {
    setRunSettingsDraftTargetScope('needs_makeup');
    setRunSettingsDraftDueAt('');
    setRunSettingsDraftStudentIds([]);
    setRunSettingsError(null);
  }, []);

  const toggleRunSettingsStudentSelection = useCallback((studentId: number) => {
    setRunSettingsDraftStudentIds((prev) => (
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    ));
  }, []);

  useEffect(() => {
    const shouldOpenRunSettings = Boolean((location.state as any)?.open_run_settings);
    if (!shouldOpenRunSettings || !controlData || runSettingsOpen || runSettingsSaving) return;
    openRunSettings();
    navigate(location.pathname, { replace: true, state: null });
  }, [
    controlData,
    location.pathname,
    location.state,
    navigate,
    openRunSettings,
    runSettingsOpen,
    runSettingsSaving,
  ]);

  const refreshAdaptive = useCallback(async () => {
    if (!lessonOpsActions.runIdNumber) return;
    setAdaptiveError(null);
    try {
      const [state, suggestionsRes] = await Promise.all([
        lessonOpsActions.loadAdaptiveState(),
        lessonOpsActions.loadAdaptiveSuggestions(showReviewed ? undefined : 'pending'),
      ]);
      setAdaptiveState(state ?? null);
      const list = Array.isArray(suggestionsRes)
        ? suggestionsRes
        : (suggestionsRes?.suggestions ?? suggestionsRes?.results ?? []);
      setAdaptiveSuggestions(Array.isArray(list) ? list : []);
    } catch {
      setAdaptiveError('Failed to load adaptive data.');
    }
  }, [lessonOpsActions, showReviewed]);

  const applyAssistFeedbackFromPayload = useCallback((payload: any, actionTypeHint?: string) => {
    const feedback = payload?.assist_feedback && typeof payload.assist_feedback === 'object'
      ? payload.assist_feedback
      : {};
    const fallbackId = payload?.assist_feedback_id ?? payload?.assistFeedbackId ?? feedback?.id;
    const parsedId = Number(fallbackId);
    const nextId = Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
    setCurrentAssistFeedbackId(nextId);
    setCurrentAssistActionType(String(actionTypeHint || payload?.result?.action_type || payload?.action_type || '').trim());

    const rawVote = String(feedback?.usefulness_vote ?? feedback?.usefulnessVote ?? '').trim().toLowerCase();
    if (rawVote === 'up' || rawVote === 'down') {
      setAssistUsefulnessVote(rawVote as 'up' | 'down');
    } else {
      setAssistUsefulnessVote(null);
    }

    const usedRaw = feedback?.used_in_class ?? feedback?.usedInClass;
    if (typeof usedRaw === 'boolean') {
      setAssistUsedInClass(usedRaw);
    } else {
      setAssistUsedInClass(null);
    }
    setAssistFeedbackError(null);
  }, []);

  const loadAssistRankings = useCallback(async () => {
    if (!lessonOpsActions.runIdNumber) return;
    try {
      setAssistRankingsLoading(true);
      const response = await lessonOpsActions.loadAssistRankings();
      const rankings = Array.isArray(response?.rankings) ? response.rankings : [];
      setAssistRankings(rankings);
    } catch {
      // Keep rankings optional; do not block teaching flow if this fails.
      setAssistRankings([]);
    } finally {
      setAssistRankingsLoading(false);
    }
  }, [lessonOpsActions]);

  const submitAssistFeedback = useCallback(async (updates: { usefulness_vote?: 'up' | 'down' | null; used_in_class?: boolean | null }) => {
    if (!lessonOpsActions.runIdNumber || !currentAssistFeedbackId) return;
    try {
      setAssistFeedbackSaving(true);
      setAssistFeedbackError(null);
      const payload: any = {};
      if (Object.prototype.hasOwnProperty.call(updates, 'usefulness_vote')) {
        payload.usefulness_vote = updates.usefulness_vote ?? null;
      }
      if (Object.prototype.hasOwnProperty.call(updates, 'used_in_class')) {
        payload.used_in_class = updates.used_in_class ?? null;
      }
      const response = await lessonOpsActions.submitAssistFeedback(Number(currentAssistFeedbackId), payload);
      const feedback = response?.assist_feedback || {};
      const voteRaw = String(feedback?.usefulness_vote || '').trim().toLowerCase();
      if (voteRaw === 'up' || voteRaw === 'down') {
        setAssistUsefulnessVote(voteRaw as 'up' | 'down');
      } else {
        setAssistUsefulnessVote(null);
      }
      const usedRaw = feedback?.used_in_class;
      setAssistUsedInClass(typeof usedRaw === 'boolean' ? usedRaw : null);
      const rankings = Array.isArray(response?.rankings) ? response.rankings : null;
      if (rankings) {
        setAssistRankings(rankings);
      } else {
        await loadAssistRankings();
      }
    } catch (err: any) {
      setAssistFeedbackError(err?.response?.data?.error || 'Failed to save assist feedback');
    } finally {
      setAssistFeedbackSaving(false);
    }
  }, [lessonOpsActions, currentAssistFeedbackId, loadAssistRankings]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    void loadAssistRankings();
  }, [loadAssistRankings]);

  const classStepId = useMemo(() => {
    if (!controlData) return null;
    return controlData.current_step_id
      ?? controlData.target_step_id
      ?? controlData.run.class_current_step
      ?? null;
  }, [controlData]);

  const currentStepId = useMemo(() => {
    if (!controlData) return null;
    return controlData.display_step_id
      ?? classStepId
      ?? null;
  }, [classStepId, controlData]);

  const classStep = useMemo(() => {
    if (!classStepId || !controlData) return null;
    return controlData.steps.find((step) => step.id === classStepId) || null;
  }, [classStepId, controlData]);

  const currentStep = useMemo(() => {
    if (!currentStepId || !controlData) return null;
    return controlData.steps.find((step) => step.id === currentStepId) || null;
  }, [currentStepId, controlData]);
  const currentStepStartedAt = useMemo(() => {
    if (!controlData || controlData.run.status !== 'active') return null;
    const liveResource = (controlData.run.live_resource && typeof controlData.run.live_resource === 'object')
      ? controlData.run.live_resource as Record<string, any>
      : {};
    return controlData.display_step_started_at
      || controlData.run.current_step_started_at
      || liveResource.current_step_started_at
      || null;
  }, [controlData]);
  const currentStepTimer = useMemo<PacingTimerRuntimeState | null>(() => {
    if (!controlData || controlData.run.status !== 'active' || !classStep || Number(currentStep?.id) !== Number(classStep.id)) return null;
    const direct = controlData.run.current_step_timer;
    if (direct && Number(direct.step_id) === Number(classStep.id)) return direct;
    const liveResource = (controlData.run.live_resource && typeof controlData.run.live_resource === 'object')
      ? controlData.run.live_resource as Record<string, any>
      : {};
    const fallback = liveResource.pacing_timer;
    return fallback && Number(fallback.step_id) === Number(classStep.id)
      ? fallback as PacingTimerRuntimeState
      : null;
  }, [classStep, controlData, currentStep?.id]);
  const studioStepIds = useMemo<number[]>(() => {
    if (!controlData) return [];
    return controlData.steps
      .filter((step) => step.runner_phase === 'studio')
      .map((step) => Number(step.id))
      .filter((stepId) => Number.isInteger(stepId));
  }, [controlData]);
  const studioSteps = useMemo(() => (
    controlData?.steps.filter((step) => step.runner_phase === 'studio') || []
  ), [controlData]);
  const routeablePathwaySteps = useMemo(() => (
    controlData?.steps.filter((step) => {
      const pathway = step.content?.pathway;
      return Boolean(pathway && typeof pathway === 'object' && pathway.teacher_routeable === true);
    }) || []
  ), [controlData]);
  const studioAvailable = studioStepIds.length > 0;
  const currentStepIsStudio = currentStep?.runner_phase === 'studio';
  const studioEntryStepId = Number(controlData?.run.studio_entry_step || studioStepIds[0] || 0);
  const studioEntryStepIndex = controlData?.steps.findIndex((step) => Number(step.id) === studioEntryStepId) ?? -1;
  const studioBoundaryStepId = studioEntryStepIndex > 0
    ? Number(controlData?.steps[studioEntryStepIndex - 1]?.id || 0)
    : studioEntryStepId;
  const studioBoundaryReached = studioEntryStepIndex === 0 || (
    Number(classStep?.id || 0) === studioEntryStepId
    || Number(classStep?.id || 0) === studioBoundaryStepId
  );
  const defaultFixStepId = studioSteps[0]?.id ? Number(studioSteps[0].id) : null;
  const defaultGoTestStepId = useMemo(() => {
    const assessmentStep = studioSteps.find((step) => (
      String(step.step_kind || '').toLowerCase() === 'assessment'
      || String(step.title || '').toLowerCase().includes('test')
    ));
    const fallback = assessmentStep || studioSteps[studioSteps.length - 1] || studioSteps[0];
    return fallback?.id ? Number(fallback.id) : null;
  }, [studioSteps]);
  const canEnterStudio = Boolean(
    controlData
    && studioAvailable
    && controlData.run.status === 'active'
    && controlData.run.delivery_phase !== 'studio'
    && studioBoundaryReached,
  );
  const currentStepTeacherNotes = useMemo(() => {
    if (!currentStep || typeof currentStep !== 'object') return '';
    const content = currentStep.content && typeof currentStep.content === 'object' ? currentStep.content : {};
    const notesRaw = typeof content.teacher_notes === 'string'
      ? content.teacher_notes
      : (typeof content.teacherNotes === 'string' ? content.teacherNotes : '');
    return String(notesRaw || '').trim();
  }, [currentStep]);

  const getAssistPromptFromStep = useCallback((step: any): string => {
    if (!step || typeof step !== 'object') return '';
    const content = step.content && typeof step.content === 'object' ? step.content : {};
    const promptMd = typeof content.prompt_md === 'string' ? content.prompt_md.trim() : '';
    if (promptMd) return promptMd;

    const blocks = Array.isArray(content.blocks) ? content.blocks : [];
    const blockParts: string[] = [];
    blocks.forEach((block: any) => {
      if (!block || typeof block !== 'object') return;
      const blockType = String(block.type || '').toLowerCase();
      if (blockType === 'text') {
        const text = String(block.text || '').trim();
        if (text) blockParts.push(text);
        return;
      }
      if (blockType === 'code') {
        const code = String(block.code || '').trim();
        if (!code) return;
        const language = String(block.language || '').trim();
        const fence = language ? `\`\`\`${language}` : '```';
        blockParts.push(`${fence}\n${code}\n\`\`\``);
      }
    });
    if (blockParts.length > 0) return blockParts.join('\n\n').trim();

    const sourceItem = content.source_item && typeof content.source_item === 'object'
      ? content.source_item
      : (content.sourceItem && typeof content.sourceItem === 'object' ? content.sourceItem : null);
    if (sourceItem) {
      const question = String(sourceItem.question || '').trim();
      if (question) return question;
    }
    return '';
  }, []);

  const currentStepPromptMd = useMemo(
    () => getAssistPromptFromStep(currentStep),
    [currentStep, getAssistPromptFromStep],
  );

  useEffect(() => {
    if (!readinessFixStepId && defaultFixStepId) {
      setReadinessFixStepId(defaultFixStepId);
    }
  }, [defaultFixStepId, readinessFixStepId]);

  useEffect(() => {
    if (!readinessGoTestStepId && defaultGoTestStepId) {
      setReadinessGoTestStepId(defaultGoTestStepId);
    }
  }, [defaultGoTestStepId, readinessGoTestStepId]);

  const loadReadinessGate = useCallback(async (options?: { silent?: boolean }) => {
    if (!runId || !currentStepId || !studioAvailable || controlData?.run.status !== 'active') {
      setReadinessGate(null);
      return;
    }
    const silent = Boolean(options?.silent);
    if (!silent) {
      setReadinessLoading(true);
    }
    try {
      const payload = await lessonRunsApi.readinessGate(Number(runId), Number(currentStepId));
      setReadinessGate((payload && typeof payload === 'object') ? payload as ReadinessGateState : null);
      setReadinessError(null);
    } catch (err: any) {
      if (!silent) {
        setReadinessError(err?.response?.data?.error || 'Failed to load readiness gate.');
      }
    } finally {
      if (!silent) {
        setReadinessLoading(false);
      }
    }
  }, [controlData?.run.status, currentStepId, runId, studioAvailable]);

  const loadStudioBoard = useCallback(async (options?: { silent?: boolean }) => {
    if (!runId || !studioAvailable) {
      setStudioBoard(null);
      return;
    }
    const silent = Boolean(options?.silent);
    if (!silent) {
      setStudioLoading(true);
    }
    try {
      const payload = await lessonRunsApi.studioBoard(Number(runId));
      setStudioBoard((payload && typeof payload === 'object') ? payload as TeacherStudioBoard : null);
      setStudioError(null);
    } catch (err: any) {
      if (!silent) {
        setStudioError(err?.response?.data?.error || 'Failed to load independent-work board.');
      }
    } finally {
      if (!silent) {
        setStudioLoading(false);
      }
    }
  }, [runId, studioAvailable]);

  useEffect(() => {
    void loadReadinessGate();
  }, [loadReadinessGate]);

  useEffect(() => {
    if (!studioAvailable || controlData?.run.status !== 'active' || activeWorkspaceTab !== 'studio') return;
    const interval = window.setInterval(() => {
      void loadReadinessGate({ silent: true });
    }, 5000);
    return () => window.clearInterval(interval);
  }, [activeWorkspaceTab, controlData?.run.status, loadReadinessGate, studioAvailable]);

  const routeReadinessGate = useCallback(async (route: ReadinessGateRouteKey) => {
    if (!controlData || !classStepId || readinessSelectedIds.length === 0) return;
    const targetStepId = route === 'go_test'
      ? readinessGoTestStepId
      : (route === 'one_more_fix' ? readinessFixStepId : null);
    if (route !== 'build_with_me' && !targetStepId) {
      setReadinessError('Choose a Studio step for this route.');
      return;
    }
    setReadinessRouting(route);
    setReadinessError(null);
    try {
      await lessonRunsApi.routeReadinessGate(controlData.run.id, {
        student_ids: readinessSelectedIds,
        route,
        ...(targetStepId ? { target_step_id: targetStepId } : {}),
        expected_class_step_id: Number(classStepId),
        expected_gate_step_id: Number(classStepId),
      });
      setReadinessSelectedIds([]);
      await Promise.all([
        loadReadinessGate({ silent: true }),
        loadStudioBoard({ silent: true }),
        loadControl(),
      ]);
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (err: any) {
      setReadinessError(err?.response?.data?.error || 'Failed to route selected students.');
    } finally {
      setReadinessRouting(null);
    }
  }, [
    classStepId,
    controlData,
    loadControl,
    loadReadinessGate,
    loadStudioBoard,
    readinessFixStepId,
    readinessGoTestStepId,
    readinessSelectedIds,
  ]);

  useEffect(() => {
    const activeRunId = Number(controlData?.run.id || 0);
    const studioActive = controlData?.run.delivery_phase === 'studio';
    if (!studioActive) {
      studioDefaultedRunIdRef.current = null;
      return;
    }
    if (workspaceModeEnabled && studioAvailable && studioDefaultedRunIdRef.current !== activeRunId) {
      studioDefaultedRunIdRef.current = activeRunId;
      setActiveWorkspaceTab('studio');
    }
  }, [controlData?.run.delivery_phase, controlData?.run.id, studioAvailable, workspaceModeEnabled]);

  useEffect(() => {
    if (!studioAvailable) {
      setStudioBoard(null);
      if (activeWorkspaceTab === 'studio') {
        setActiveWorkspaceTab('step');
      }
      return;
    }
    void loadStudioBoard();
  }, [activeWorkspaceTab, studioAvailable, loadStudioBoard, controlData?.run.delivery_phase]);

  useEffect(() => {
    if (!studioAvailable || controlData?.run.status !== 'active' || activeWorkspaceTab !== 'studio') return;
    const interval = window.setInterval(() => {
      void loadStudioBoard({ silent: true });
    }, 4000);
    return () => window.clearInterval(interval);
  }, [activeWorkspaceTab, controlData?.run.status, loadStudioBoard, studioAvailable]);

  const applyClassDraftFromSaved = useCallback((draft: LessonStepAiDraft | null, options?: { silent?: boolean }) => {
    setActiveClassDraftId(draft?.id ?? null);
    setClassDraft(draft?.content_md || '');
    setClassAiError(null);
    setClassAiSuccess(null);
    if (!options?.silent) {
      setClassDraftLibraryNotice(draft ? `Loaded "${draft.title}".` : null);
    }
  }, []);

  const applyTeacherWalkthroughFromSaved = useCallback((draft: LessonStepAiDraft | null, options?: { silent?: boolean }) => {
    setActiveTeacherWalkthroughDraftId(draft?.id ?? null);
    if (!draft) {
      setTeacherWalkthrough(null);
      setTeacherWalkthroughMd('');
      if (!options?.silent) setTeacherWalkthroughLibraryNotice(null);
      return;
    }
    const markdown = String(draft.content_md || '').trim();
    const walkthrough = normalizeTeacherWalkthrough(draft.structured_payload, markdown);
    setTeacherWalkthrough(walkthrough);
    setTeacherWalkthroughMd(markdown || teacherWalkthroughToMarkdown(walkthrough));
    setTeacherWalkthroughError(null);
    if (!options?.silent) {
      setTeacherWalkthroughLibraryNotice(`Loaded "${draft.title}".`);
    }
  }, []);

  const loadStepAiDraftLibrary = useCallback(async (stepIdOverride?: number | null, options?: { applyDefaults?: boolean }) => {
    const targetStepId = Number(stepIdOverride ?? currentStepId);
    if (!Number.isInteger(targetStepId) || targetStepId <= 0) {
      setStepAiDrafts([]);
      setActiveClassDraftId(null);
      setActiveTeacherWalkthroughDraftId(null);
      return;
    }
    try {
      setStepAiDraftsLoading(true);
      const payload = await lessonStepsApi.aiDrafts(targetStepId);
      const drafts = Array.isArray(payload?.drafts)
        ? payload.drafts.map((item: any) => normalizeLessonStepAiDraft(item)).filter(Boolean) as LessonStepAiDraft[]
        : [];
      setStepAiDrafts(drafts);
      if (options?.applyDefaults) {
        const defaultClassDraft = drafts.find((item) => item.surface === 'class_draft' && item.is_default)
          || drafts.find((item) => item.surface === 'class_draft')
          || null;
        const defaultTeacherDraft = drafts.find((item) => item.surface === 'teacher_walkthrough' && item.is_default)
          || drafts.find((item) => item.surface === 'teacher_walkthrough')
          || null;
        applyClassDraftFromSaved(defaultClassDraft, { silent: true });
        applyTeacherWalkthroughFromSaved(defaultTeacherDraft, { silent: true });
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to load saved AI drafts.';
      setClassDraftLibraryNotice(message);
      setTeacherWalkthroughLibraryNotice(message);
      setStepAiDrafts([]);
    } finally {
      setStepAiDraftsLoading(false);
    }
  }, [applyClassDraftFromSaved, applyTeacherWalkthroughFromSaved, currentStepId]);

  const activeExplainBroadcast = useMemo(() => {
    const broadcast = (controlData as any)?.broadcast ?? null;
    if (broadcast?.kind === 'explain' && Number(broadcast?.step_id) === Number(currentStepId)) return broadcast;
    const fallback = (controlData?.run as any)?.live_resource?.broadcast ?? null;
    if (fallback?.kind === 'explain' && Number(fallback?.step_id) === Number(currentStepId)) return fallback;
    return null;
  }, [controlData, currentStepId]);

  const currentStepSignalConfig = useMemo(() => {
    if (!currentStep) return null;
    // Support both API shapes (snake_case and camelCase)
    const raw =
      (currentStep as any).signal_config ??
      (currentStep as any).signalConfig ??
      null;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return raw;
  }, [currentStep]);

  const currentStepRevealMode = useMemo(() => {
    if (!currentStepSignalConfig) return 'auto';
    return (currentStepSignalConfig as any).reveal_mode ?? (currentStepSignalConfig as any).revealMode ?? 'auto';
  }, [currentStepSignalConfig]);
  const currentStepAnswerRevealed = useMemo(() => {
    if (!currentStepId || !controlData) return false;
    const liveResource = ((controlData.run as any)?.live_resource ?? {}) as Record<string, any>;
    const revealedSteps = liveResource?.revealed_steps && typeof liveResource.revealed_steps === 'object'
      ? liveResource.revealed_steps
      : {};
    if (String(currentStepRevealMode || '').trim().toLowerCase() !== 'teacher') {
      return true;
    }
    return Boolean(revealedSteps[String(currentStepId)]);
  }, [controlData, currentStepId, currentStepRevealMode]);

  useEffect(() => {
    if (!currentStepId) {
      setStepAiDrafts([]);
      applyClassDraftFromSaved(null, { silent: true });
      applyTeacherWalkthroughFromSaved(null, { silent: true });
      return;
    }
    void loadStepAiDraftLibrary(currentStepId, { applyDefaults: true });
  }, [applyClassDraftFromSaved, applyTeacherWalkthroughFromSaved, currentStepId, loadStepAiDraftLibrary]);

  const currentStepTeacherAnswerKey = useMemo(() => {
    if (!controlData) return null;
    return controlData.display_step_teacher_answer_key
      ?? controlData.current_step_teacher_answer_key
      ?? (currentStep as any)?.teacher_answer_key
      ?? null;
  }, [controlData, currentStep]);
  const answerKey = useMemo(() => {
    return (currentStep as any)?.answer_key ?? null;
  }, [currentStep]);
  const currentStepVocabularyCandidates = useMemo(
    () => extractStepVocabularyCandidates(currentStep, currentStepTeacherAnswerKey, currentStepTeacherNotes),
    [currentStep, currentStepTeacherAnswerKey, currentStepTeacherNotes],
  );
  const stepContextLabel = useMemo(() => {
    const stage = String(currentStep?.stage_tag || '').trim();
    const kind = String(currentStep?.step_kind || '').trim();
    if (stage && kind) return `${stage.toUpperCase()} • ${kind}`;
    if (stage) return stage.toUpperCase();
    if (kind) return kind;
    return '';
  }, [currentStep?.stage_tag, currentStep?.step_kind]);
  const currentStepDisplayLabel = useMemo(() => {
    if (!currentStep) return 'Current step';
    return `Step ${currentStep.order} of ${controlData?.steps.length || 0}`;
  }, [controlData?.steps.length, currentStep]);
  const assistContextBase = useMemo(() => ({
    step_id: currentStepId ? Number(currentStepId) : null,
    currentStepId: currentStepId ?? null,
    currentStepTitle: currentStep?.title || '',
    stageTag: currentStep?.stage_tag || null,
    stepKind: currentStep?.step_kind || '',
    promptMd: currentStepPromptMd,
    teacherAnswerKey: currentStepTeacherAnswerKey,
    teacherNotes: currentStepTeacherNotes || '',
    teacher_notes: currentStepTeacherNotes || '',
    vocabularyCandidates: currentStepVocabularyCandidates,
    vocabulary_candidates: currentStepVocabularyCandidates,
    vocabularyFocus: currentStepVocabularyCandidates.slice(0, 6),
    vocabulary_focus: currentStepVocabularyCandidates.slice(0, 6),
    stepContextLabel: stepContextLabel,
    step_context_label: stepContextLabel,
  }), [
    currentStepId,
    currentStep?.title,
    currentStep?.stage_tag,
    currentStep?.step_kind,
    currentStepPromptMd,
    currentStepTeacherAnswerKey,
    currentStepTeacherNotes,
    currentStepVocabularyCandidates,
    stepContextLabel,
  ]);
  const hasAnswerKey = Boolean(answerKey?.present);
  const blanksTotal = Number(answerKey?.summary?.blanks_total ?? 0);
  const blanksKeyed = Number(answerKey?.summary?.blanks_keyed ?? 0);

  const currentStepHasSignal = useMemo(() => {
    return Boolean((currentStepSignalConfig as any)?.type);
  }, [currentStepSignalConfig]);

  const rosterNameById = useMemo(() => {
    const map = new Map<number, string>();
    if (!controlData?.students) return map;
    for (const s of controlData.students) {
      map.set(s.id, s.name);
    }
    return map;
  }, [controlData]);

  const absentStudentIdSet = useMemo(() => {
    const ids = new Set<number>();
    if (!controlData?.students) return ids;
    for (const student of controlData.students) {
      if (student.participation_status === 'absent') {
        ids.add(Number(student.id));
      }
    }
    return ids;
  }, [controlData?.students]);

  const hiddenAbsentSubmissionCount = useMemo(() => {
    if (!hideAbsentInSubmissions || !submissionsInbox || !Array.isArray(submissionsInbox.submissions)) return 0;
    return submissionsInbox.submissions.reduce((count, row) => (
      absentStudentIdSet.has(Number((row as any)?.student_id)) ? count + 1 : count
    ), 0);
  }, [absentStudentIdSet, hideAbsentInSubmissions, submissionsInbox]);

  const submissionsInboxView = useMemo<UnifiedSubmissionsPayload | null>(() => {
    if (!submissionsInbox) return null;
    if (!hideAbsentInSubmissions || !Array.isArray(submissionsInbox.submissions)) return submissionsInbox;

    const filteredSubmissions = submissionsInbox.submissions.filter(
      (row) => !absentStudentIdSet.has(Number((row as any)?.student_id)),
    );

    let signalsSubmitted = 0;
    let evidenceSubmitted = 0;
    let bothSubmitted = 0;
    let anySubmitted = 0;
    let gateAllowed = 0;
    let gateReview = 0;
    let gateBlocked = 0;
    let qualityAccepted = 0;
    let qualityProvisional = 0;
    let qualityReview = 0;
    let qualityResolved = 0;
    for (const row of filteredSubmissions) {
      const hasSignal = Boolean((row as any)?.signal?.latest);
      const hasEvidence = Boolean((row as any)?.evidence?.latest);
      const gateStatus = String((row as any)?.reflection_gate?.final_status || '').trim().toLowerCase();
      const qualityStatus = String((row as any)?.submission_quality?.status || '').trim().toLowerCase();
      if (hasSignal) signalsSubmitted += 1;
      if (hasEvidence) evidenceSubmitted += 1;
      if (hasSignal && hasEvidence) bothSubmitted += 1;
      if (hasSignal || hasEvidence) anySubmitted += 1;
      if (gateStatus === 'allowed') gateAllowed += 1;
      if (gateStatus === 'needs_teacher_review') gateReview += 1;
      if (gateStatus === 'blocked_revision') gateBlocked += 1;
      if (qualityStatus === 'accepted') qualityAccepted += 1;
      if (qualityStatus === 'provisional') qualityProvisional += 1;
      if (qualityStatus === 'needs_teacher_review') qualityReview += 1;
      if (qualityStatus === 'resolved') qualityResolved += 1;
    }

    return {
      ...submissionsInbox,
      summary: {
        students_total: filteredSubmissions.length,
        signals_submitted: signalsSubmitted,
        evidence_submitted: evidenceSubmitted,
        both_submitted: bothSubmitted,
        any_submitted: anySubmitted,
        gate_allowed: gateAllowed,
        gate_review: gateReview,
        gate_blocked: gateBlocked,
        quality_accepted: qualityAccepted,
        quality_provisional: qualityProvisional,
        quality_review: qualityReview,
        quality_resolved: qualityResolved,
      },
      submissions: filteredSubmissions,
    };
  }, [absentStudentIdSet, hideAbsentInSubmissions, submissionsInbox]);

  const bucketIds = useMemo(() => {
    if (!liveSignalStats?.bucket_student_ids || !activeBucket) return [];
    return liveSignalStats.bucket_student_ids[activeBucket] || [];
  }, [liveSignalStats, activeBucket]);

  const bucketNames = useMemo(() => {
    if (!bucketIds.length) return [];
    return bucketIds.map((id) => rosterNameById.get(id) || `Unknown (${id})`);
  }, [bucketIds, rosterNameById]);

  useEffect(() => {
    const order = liveDashboard?.suggested_priority_order || [];
    if (order.length === 0) {
      setActiveLiveDashboardBucket(null);
      return;
    }
    if (activeLiveDashboardBucket && order.includes(activeLiveDashboardBucket)) return;
    const counts = liveDashboard?.bucket_counts || {};
    const nextBucket = order.find((bucket) => Number(counts[bucket] || 0) > 0) || order[0] || null;
    if (activeLiveDashboardBucket === nextBucket) return;
    setActiveLiveDashboardBucket(nextBucket);
  }, [activeLiveDashboardBucket, liveDashboard?.bucket_counts, liveDashboard?.suggested_priority_order]);

  const criticalAlerts = useMemo(() => {
    const alerts: Array<{
      id: string;
      tone: 'rose' | 'amber';
      message: string;
      opsRelated?: boolean;
    }> = [];

    if (stepNavError) {
      alerts.push({
        id: 'step_nav',
        tone: 'rose',
        message: stepNavError,
      });
    }
    if (revealError) {
      alerts.push({
        id: 'reveal',
        tone: 'rose',
        message: `Reveal issue: ${revealError}`,
      });
    }
    if (adaptiveError) {
      alerts.push({
        id: 'adaptive',
        tone: 'rose',
        message: `Adaptive issue: ${adaptiveError}`,
        opsRelated: true,
      });
    }
    if (submissionsError) {
      alerts.push({
        id: 'submissions',
        tone: 'rose',
        message: `Submissions issue: ${submissionsError}`,
        opsRelated: true,
      });
    }
    if (aiAssistError) {
      alerts.push({
        id: 'assist',
        tone: 'rose',
        message: `Assist issue: ${aiAssistError}`,
      });
    }
    if (liveSignalStats) {
      const pendingCount = Number(liveSignalStats.step_signal_pending || 0);
      if (pendingCount > 0) {
        alerts.push({
          id: 'signals_pending',
          tone: 'amber',
          message: `${pendingCount} student${pendingCount === 1 ? '' : 's'} pending signal response for ${
            controlData?.run.delivery_phase === 'studio' && currentStepId !== classStepId
              ? 'the current class step'
              : 'this step'
          }.`,
          opsRelated: true,
        });
      }
    }

    return alerts;
  }, [adaptiveError, aiAssistError, classStepId, controlData?.run.delivery_phase, currentStepId, liveSignalStats, revealError, stepNavError, submissionsError]);

  const primaryCriticalAlert = criticalAlerts[0] || null;
  const compactAlertLabel = useMemo(() => {
    const alert = primaryCriticalAlert;
    if (!alert) return '';
    if (alert.id === 'signals_pending') {
      const pendingCount = Number(liveSignalStats?.step_signal_pending || 0);
      const counts = liveDashboard?.bucket_counts || {};
      const inactiveCount = Number(counts.inactive || 0);
      const noResponseCount = Number(counts.no_response || 0);
      if (pendingCount > 0 && inactiveCount >= pendingCount && noResponseCount === 0) {
        return `${pendingCount} student${pendingCount === 1 ? '' : 's'} not joined`;
      }
      if (pendingCount > 0 && inactiveCount > 0 && noResponseCount > 0) {
        return `${inactiveCount} not joined, ${noResponseCount} no response`;
      }
      if (pendingCount > 0) {
        return `${pendingCount} pending signal${pendingCount === 1 ? '' : 's'}`;
      }
    }
    if (criticalAlerts.length > 1) {
      return `${criticalAlerts.length} alerts`;
    }
    return compactCriticalAlertMessage(alert.message);
  }, [criticalAlerts.length, liveDashboard?.bucket_counts, liveSignalStats?.step_signal_pending, primaryCriticalAlert]);

  const formatSignalResponse = useCallback((response: Record<string, any> | null | undefined) => {
    if (!response || typeof response !== 'object') return '—';
    if (typeof response.value === 'string' || typeof response.value === 'number') {
      return String(response.value);
    }
    if (Array.isArray(response.selected_indices)) {
      return `Selected indices: ${response.selected_indices.join(', ')}`;
    }
    if (Array.isArray(response.ordering)) {
      return `Ordering: ${response.ordering.map((item: any) => String(item)).join(' -> ')}`;
    }
    if (response.pairs && typeof response.pairs === 'object') {
      const pairs = Object.entries(response.pairs as Record<string, any>)
        .map(([left, right]) => `${left} -> ${String(right)}`);
      return pairs.length > 0 ? pairs.join(' | ') : '—';
    }
    if (Array.isArray(response.placed_tokens)) {
      return `Placed: ${response.placed_tokens.map((item: any) => String(item ?? '')).join(' | ')}`;
    }
    return JSON.stringify(response);
  }, []);

  const previousStep = useMemo(() => {
    if (!currentStepId || !controlData) return null;
    const currentIndex = controlData.steps.findIndex((item) => item.id === currentStepId);
    if (currentIndex <= 0) return null;
    return controlData.steps[currentIndex - 1] || null;
  }, [currentStepId, controlData]);

  const loadSubmissionsInbox = useCallback(async (options?: { silent?: boolean }) => {
    if (!classStepId || !lessonOpsActions.runIdNumber) return;
    if (submissionsRequestInFlightRef.current) return;
    const silent = Boolean(options?.silent);
    submissionsRequestInFlightRef.current = true;
    try {
      if (!silent) {
        setSubmissionsLoading(true);
      }
      setSubmissionsError(null);
      const payload = await lessonOpsActions.loadSubmissions(classStepId);
      setSubmissionsInbox(payload || null);
    } catch (err: any) {
      setSubmissionsInbox(null);
      setSubmissionsError(err?.response?.data?.error || 'Failed to load submissions inbox');
    } finally {
      submissionsRequestInFlightRef.current = false;
      if (!silent) {
        setSubmissionsLoading(false);
      }
    }
  }, [classStepId, lessonOpsActions]);

  useEffect(() => {
    void loadSubmissionsInbox();
  }, [loadSubmissionsInbox]);

  const refreshRealtimePanels = useCallback((options?: { includeAdaptive?: boolean }) => {
    const tasks: Array<Promise<any>> = [
      loadControl({ silent: true }),
      loadLiveDashboard({ silent: true }),
      loadLiveSignalStats(),
      loadSubmissionsInbox({ silent: true }),
    ];
    if (options?.includeAdaptive) {
      tasks.push(refreshAdaptive());
    }
    return Promise.all(tasks);
  }, [loadControl, loadLiveDashboard, loadLiveSignalStats, loadSubmissionsInbox, refreshAdaptive]);

  useEffect(() => {
    if (controlData?.run.status !== 'active' || !classStepId) return;
    const interval = window.setInterval(() => {
      void loadSubmissionsInbox({ silent: true });
    }, LESSON_CONTROL_SUBMISSIONS_POLL_MS);
    return () => window.clearInterval(interval);
  }, [classStepId, controlData?.run.status, loadSubmissionsInbox]);

  useEffect(() => {
    const runIdNumber = lessonOpsActions.runIdNumber;
    if (!runIdNumber) return;
    return subscribeLessonRunSync(runIdNumber, (event) => {
      if (event.type === 'adaptive_changed') {
        void refreshRealtimePanels({ includeAdaptive: true });
        return;
      }
      if (event.type === 'submissions_changed') {
        void Promise.all([
          loadLiveDashboard({ silent: true }),
          loadLiveSignalStats(),
          loadSubmissionsInbox({ silent: true }),
        ]);
        return;
      }
      if (event.type === 'broadcast_changed') {
        void loadControl({ silent: true });
        return;
      }
      // control_changed + step_changed
      void refreshRealtimePanels();
    });
  }, [lessonOpsActions.runIdNumber, loadControl, loadLiveDashboard, loadLiveSignalStats, loadSubmissionsInbox, refreshRealtimePanels]);

  useEffect(() => {
    if (!classStepId || !liveSignalStats) return;
    // When live signal counters change, refresh submissions immediately instead of waiting for next interval.
    void loadSubmissionsInbox({ silent: true });
  }, [
    classStepId,
    liveSignalStats?.step_signal_submitted,
    liveSignalStats?.step_signal_pending,
    liveSignalStats?.missing_reasoning,
    liveSignalStats?.missing_confidence,
    loadSubmissionsInbox,
  ]);

  useEffect(() => {
    if (controlData?.run.status !== 'active' || !classStepId) return;
    const refreshOnFocus = () => {
      void loadSubmissionsInbox({ silent: true });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void loadSubmissionsInbox({ silent: true });
    };
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [classStepId, controlData?.run.status, loadSubmissionsInbox]);

  useEffect(() => {
    void refreshAdaptive();
  }, [refreshAdaptive]);

  useEffect(() => {
    setExpandedAdaptiveTargets((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(adaptiveSuggestions.map((item: any) => Number(item?.id)));
      const next = new Set<number>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [adaptiveSuggestions]);

  useEffect(() => {
    setRevealSuccess(false);
    setRevealError(null);
    setExpandedSubmissionStudentIds(new Set());
  }, [currentStepId]);

  useEffect(() => {
    setAiSuggestion('');
    setAiStepByStep('');
    setAiCommonMistakes([]);
    setCopyStatus('idle');
    setAiDelayRevealTriggered(false);
    setAiDelayRevealReason('');
    setAiAssistError(null);
  }, [currentStepId]);

  useEffect(() => {
    setClassDraft('');
    setClassAiError(null);
    setClassAiSuccess(null);
    setClassAskPrompt('');
    setClassAskThread(EMPTY_TEACHER_AI_THREAD);
    setClassAskThreadError(null);
    setTeacherWalkthroughMd('');
    setTeacherWalkthrough(null);
    setTeacherWalkthroughError(null);
    setCopiedExplain(false);
  }, [currentStepId]);

  const handleRevealAnswers = useCallback(async () => {
    if (!runId || !currentStep?.id) return;
    try {
      setRevealSubmitting(true);
      setRevealError(null);
      setRevealSuccess(false);
      const response = await lessonRunsApi.reveal(Number(runId), { step_id: currentStep.id });
      if (!response || response.status !== 200) {
        setRevealError('Failed to reveal answers.');
        return;
      }
      setRevealSuccess(true);
      void loadLiveDashboard({ silent: true });
      void loadLiveSignalStats();
      publishLessonRunSync(Number(runId), 'control_changed');
    } catch {
      setRevealError('Failed to reveal answers.');
    } finally {
      setRevealSubmitting(false);
    }
  }, [currentStep?.id, loadLiveDashboard, loadLiveSignalStats, runId]);

  const handleGenerateAdaptive = async () => {
    if (!lessonOpsActions.runIdNumber) return;
    setAdaptiveLoading(true);
    setAdaptiveError(null);
    setLastAdaptiveApplication(null);
    try {
      await lessonOpsActions.generateAdaptiveSuggestions();
      await refreshAdaptive();
      publishLessonRunSync(lessonOpsActions.runIdNumber, 'adaptive_changed');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 429) {
        setAdaptiveError('Rate limited. Try again in a minute.');
      } else {
        setAdaptiveError('Failed to generate suggestions.');
      }
    } finally {
      setAdaptiveLoading(false);
    }
  };

  const handleApproveApplySuggestion = async (suggestionId: number, apply: boolean) => {
    if (!lessonOpsActions.runIdNumber) return;
    setAdaptiveLoading(true);
    setAdaptiveError(null);
    try {
      const res = await lessonOpsActions.approveAdaptiveSuggestion(suggestionId, { apply });
      if (apply) {
        const payload = (res as any)?.data ?? res;
        const app = payload?.application;
        if (app && (app?.repair_students_updated !== undefined || app?.extend_students_updated !== undefined)) {
          setLastAdaptiveApplication({
            repair: Number(app?.repair_students_updated ?? 0),
            extend: Number(app?.extend_students_updated ?? 0),
          });
        }
      }
      await refreshAdaptive();
      await Promise.all([loadControl(), loadLiveDashboard(), loadLiveSignalStats()]);
      publishLessonRunSync(lessonOpsActions.runIdNumber, 'adaptive_changed');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setAdaptiveError('Suggestion is stale or conflicted. Refresh and try again.');
      } else {
        setAdaptiveError('Failed to approve/apply suggestion.');
      }
    } finally {
      setAdaptiveLoading(false);
    }
  };

  const handleRejectSuggestion = async (suggestionId: number) => {
    if (!lessonOpsActions.runIdNumber) return;
    setAdaptiveLoading(true);
    setAdaptiveError(null);
    try {
      await lessonOpsActions.rejectAdaptiveSuggestion(suggestionId, {});
      await refreshAdaptive();
      publishLessonRunSync(lessonOpsActions.runIdNumber, 'adaptive_changed');
    } catch {
      setAdaptiveError('Failed to reject suggestion.');
    } finally {
      setAdaptiveLoading(false);
    }
  };

  const handleApplyApprovedSuggestion = async (suggestionId: number) => {
    if (!lessonOpsActions.runIdNumber) return;
    setAdaptiveLoading(true);
    setAdaptiveError(null);
    try {
      const res = await lessonOpsActions.applyAdaptiveSuggestion(suggestionId, {});
      const payload = (res as any)?.data ?? res;
      const app = payload?.application;
      if (app && (app?.repair_students_updated !== undefined || app?.extend_students_updated !== undefined)) {
        setLastAdaptiveApplication({
          repair: Number(app?.repair_students_updated ?? 0),
          extend: Number(app?.extend_students_updated ?? 0),
        });
      }
      await refreshAdaptive();
      await Promise.all([loadControl(), loadLiveDashboard(), loadLiveSignalStats()]);
      publishLessonRunSync(lessonOpsActions.runIdNumber, 'adaptive_changed');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setAdaptiveError('Suggestion is stale or conflicted. Refresh and try again.');
      } else {
        setAdaptiveError('Failed to apply suggestion.');
      }
    } finally {
      setAdaptiveLoading(false);
    }
  };

  const handleClearOverridesToClassStep = async () => {
    if (!lessonOpsActions.runIdNumber) return;
    setAdaptiveLoading(true);
    setAdaptiveError(null);
    setLastAdaptiveApplication(null);
    try {
      await lessonOpsActions.clearStudentOverrides({ return_to: 'class_step' });
      await refreshAdaptive();
      await Promise.all([loadControl(), loadLiveDashboard(), loadLiveSignalStats()]);
      publishLessonRunSync(lessonOpsActions.runIdNumber, 'adaptive_changed');
    } catch {
      setAdaptiveError('Failed to clear overrides.');
    } finally {
      setAdaptiveLoading(false);
    }
  };

  const handleRouteStudentsToStep = async (
    payload: RouteStudentsToStepPayload,
  ): Promise<RouteStudentsToStepResponse> => {
    if (!controlData) {
      throw new Error('Lesson run is not loaded.');
    }
    const response = await lessonRunsApi.routeStudentsToStep(controlData.run.id, payload) as RouteStudentsToStepResponse;
    await refreshAdaptive();
    await Promise.all([loadControl(), loadLiveDashboard()]);
    publishLessonRunSync(controlData.run.id, 'adaptive_changed');
    publishLessonRunSync(controlData.run.id, 'control_changed');
    return response;
  };

  const advance = async () => {
    if (!controlData || transitionBusy) return;
    const currentIndex = controlData.steps.findIndex((item) => item.id === currentStepId);
    const nextStep = controlData.steps[currentIndex + 1];
    
    if (!nextStep) {
      if (onNextActivity) { onNextActivity(); return; }
      if (controlData.run.delivery_phase === 'studio') {
        setStepNavError('Display is already on the last step.');
        return;
      }
      openEndRunDialog();
      return;
    }

    setStepNavError(null);
    setStepNavLoadingId(nextStep.id);
    try {
      await lessonRunsApi.advance(controlData.run.id, { step_id: nextStep.id });
      await Promise.all([loadControl(), loadLiveDashboard(), loadLiveSignalStats()]);
      publishLessonRunSync(controlData.run.id, 'step_changed');
    } catch (err: any) {
      setStepNavError(err?.response?.data?.error || 'Failed to advance step.');
    } finally {
      setStepNavLoadingId(null);
    }
  };

  const updatePacingTimer = async (
    action: 'start' | 'restart' | 'add' | 'end',
    seconds?: number,
  ) => {
    if (!controlData) return;
    setPacingTimerLoading(true);
    setPacingTimerError(null);
    try {
      await lessonRunsApi.pacingTimer(controlData.run.id, { action, ...(seconds ? { seconds } : {}) });
      await loadControl({ silent: true });
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (err: any) {
      setPacingTimerError(err?.response?.data?.error || 'Failed to update the pacing timer.');
    } finally {
      setPacingTimerLoading(false);
    }
  };

  const enterStudio = async () => {
    if (!controlData || !runId) return;
    setStudioActionLoading('enter');
    setStudioNotice('');
    setStudioError(null);
    try {
      const payload = await lessonRunsApi.enterStudio(controlData.run.id);
      if (payload?.studio_board) {
        setStudioBoard(payload.studio_board as TeacherStudioBoard);
      }
      setStudioNotice('Independent work started. Students can continue the Studio tail at their own pace.');
      setActiveWorkspaceTab('studio');
      await Promise.all([loadControl(), loadLiveDashboard(), loadLiveSignalStats()]);
      publishLessonRunSync(controlData.run.id, 'step_changed');
    } catch (err: any) {
      setStudioError(err?.response?.data?.error || 'Failed to start independent work.');
    } finally {
      setStudioActionLoading(null);
    }
  };

  const acknowledgeStudioHelp = async (progressId?: number | null) => {
    if (!progressId) return;
    setStudioActionLoading('ack');
    try {
      await studioProgressApi.acknowledgeHelp(progressId);
      await loadStudioBoard();
    } catch (err: any) {
      setStudioError(err?.response?.data?.error || 'Failed to acknowledge help request.');
    } finally {
      setStudioActionLoading(null);
    }
  };

  const resolveStudioHelp = async (progressId?: number | null) => {
    if (!progressId) return;
    setStudioActionLoading('resolve');
    try {
      await studioProgressApi.resolveHelp(progressId);
      await loadStudioBoard();
    } catch (err: any) {
      setStudioError(err?.response?.data?.error || 'Failed to resolve help request.');
    } finally {
      setStudioActionLoading(null);
    }
  };

  const goPreviousStep = async () => {
    if (!controlData || !previousStep) return;
    setStepNavError(null);
    setStepNavLoadingId(previousStep.id);
    try {
      await lessonRunsApi.advance(controlData.run.id, { step_id: previousStep.id });
      await Promise.all([loadControl(), loadLiveDashboard(), loadLiveSignalStats()]);
      publishLessonRunSync(controlData.run.id, 'step_changed');
    } catch (err: any) {
      setStepNavError(err?.response?.data?.error || 'Failed to move to previous step.');
    } finally {
      setStepNavLoadingId(null);
    }
  };

  const jumpToStep = async (targetStep: ControlData['steps'][number]) => {
    if (!controlData || !targetStep || targetStep.id === currentStepId || stepNavLoadingId !== null) return;
    const currentIndex = controlData.steps.findIndex((item) => item.id === currentStepId);
    const targetIndex = controlData.steps.findIndex((item) => item.id === targetStep.id);
    if (targetIndex < 0) return;

    if (currentIndex >= 0) {
      const isSkippingAhead = targetIndex > currentIndex + 1;
      const isJumpingBack = targetIndex < currentIndex;
      if (isSkippingAhead || isJumpingBack) {
        const currentOrder = controlData.steps[currentIndex]?.order ?? currentIndex + 1;
        const targetOrder = targetStep.order ?? targetIndex + 1;
        const message = controlData.run.delivery_phase === 'studio'
          ? (
            isSkippingAhead
              ? `Jump the display ahead from Step ${currentOrder} to Step ${targetOrder}? Students stay in Studio mode.`
              : `Move the display back from Step ${currentOrder} to Step ${targetOrder}? Students stay in Studio mode.`
          )
          : (
            isSkippingAhead
              ? `Jump ahead from Step ${currentOrder} to Step ${targetOrder}? Students move immediately.`
              : `Go back from Step ${currentOrder} to Step ${targetOrder}? This can confuse students who already advanced.`
          );
        const confirmed = window.confirm(message);
        if (!confirmed) return;
      }
    }

    setStepNavError(null);
    setStepNavLoadingId(targetStep.id);
    try {
      await lessonRunsApi.advance(controlData.run.id, { step_id: targetStep.id });
      await Promise.all([loadControl(), loadLiveDashboard(), loadLiveSignalStats()]);
      publishLessonRunSync(controlData.run.id, 'step_changed');
    } catch (err: any) {
      setStepNavError(err?.response?.data?.error || 'Failed to jump to selected step.');
    } finally {
      setStepNavLoadingId(null);
    }
  };

  const endRun = () => {
    if (!controlData) return;
    openEndRunDialog();
  };

  const openDisplay = () => {
    if (!controlData) return;
    window.open(embeddedRunId ? `/v2/teacher/classroom/${controlData.run.section}/display?source=teach` : `/v2/teacher/run/${controlData.run.id}/display?source=lesson-control`, embeddedRunId ? `classroom-display-${controlData.run.section}` : '_blank');
  };

  const updatePresentationDisplay = async (active: boolean, slideIndex = presentationSlideIndex, target: 'display' | 'students' | 'all' | 'sync' = 'display') => {
    if (!controlData || !presentationResource) return;
    setPresentationDisplayLoading(true);
    setPresentationDisplayError('');
    try {
      await lessonRunsApi.setPresentation(controlData.run.id, active ? {
        active: true,
        target,
        resource_key: presentationResource.resource_key,
        src: presentationResource.src,
        title: presentationResource.title,
        slide_index: slideIndex,
      } : { active: false, target });
      await loadControl();
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (error: any) {
      setPresentationDisplayError(error?.response?.data?.error || 'Unable to update the classroom display.');
    } finally {
      setPresentationDisplayLoading(false);
    }
  };

  const openStudentPreview = () => {
    if (!controlData) return;
    window.open(`/v2/teacher/run/${controlData.run.id}/student-preview`, '_blank', 'noopener');
  };

  const openOpsInNewTab = () => {
    if (!controlData) return;
    window.open(`/v2/teacher/run/${controlData.run.id}/ops`, '_blank', 'noopener');
  };

  const openOpsSameTab = () => {
    if (!controlData) return;
    navigate(`/v2/teacher/run/${controlData.run.id}/ops`);
  };

  const openTeachSameTab = () => {
    if (!controlData) return;
    navigate(`/v2/teacher/run/${controlData.run.id}`);
  };

  const openOpsForAlert = useCallback((alertId: string) => {
    if (!layoutV2Enabled || isOpsSurface) return;
    if (workspaceModeEnabled) {
      setActiveWorkspaceTab(workspaceTabForAlert(alertId));
      return;
    }
    if (isFocusViewport) {
      if (alertId === 'adaptive') {
        setMobileOpsTab('adaptive');
      } else if (alertId === 'submissions') {
        setMobileOpsTab('submissions');
      } else {
        setMobileOpsTab('signals');
      }
      setMobileOpsDrawerOpen(true);
      return;
    }
    openOpsSameTab();
  }, [isFocusViewport, isOpsSurface, layoutV2Enabled, openOpsSameTab, workspaceModeEnabled]);

  const focusDashboardForAlert = useCallback((alertId?: string) => {
    if (alertId === 'signals_pending') {
      const counts = liveDashboard?.bucket_counts || {};
      const inactiveCount = Number(counts.inactive || 0);
      const noResponseCount = Number(counts.no_response || 0);
      if (noResponseCount > 0) {
        setActiveLiveDashboardBucket('no_response');
        return;
      }
      if (inactiveCount > 0) {
        setActiveLiveDashboardBucket('inactive');
        return;
      }
      setActiveLiveDashboardBucket('no_response');
      return;
    }
    if (alertId) {
      openOpsForAlert(alertId);
    }
  }, [liveDashboard?.bucket_counts, openOpsForAlert]);

  const copyStudentLink = async () => {
    if (!controlData) return;
    const link = `${window.location.origin}/v2/run/${controlData.run.id}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(true);
      window.setTimeout(() => setCopiedLink(false), 1500);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };

  const copyExplain = async () => {
    const content = String(activeExplainBroadcast?.content_md || '').trim();
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopiedExplain(true);
      window.setTimeout(() => setCopiedExplain(false), 1500);
    } catch (err) {
      console.error('Failed to copy explanation', err);
    }
  };

  const requestRedo = async (studentId: number, stepId: number) => {
    if (!controlData) return;
    const confirmed = window.confirm('Reset this student to this step?');
    if (!confirmed) return;
    try {
      await lessonRunsApi.redo(controlData.run.id, { student_id: studentId, step_id: stepId });
      setRedoRequested((prev) => (prev.includes(studentId) ? prev : [...prev, studentId]));
      await loadSubmissionsInbox();
      publishLessonRunSync(controlData.run.id, 'submissions_changed');
    } catch (err: any) {
      setSubmissionsError(err?.response?.data?.error || 'Failed to request redo');
    }
  };

  const approveEvidence = async (evidenceId: number) => {
    try {
      await evidenceApi.approve(evidenceId);
      await loadSubmissionsInbox();
      if (controlData?.run.id) {
        publishLessonRunSync(controlData.run.id, 'submissions_changed');
      }
    } catch (err: any) {
      setSubmissionsError(err?.response?.data?.error || 'Failed to approve legacy evidence');
    }
  };

  const decideReflectionGate = async (
    gateId: number,
    decision: 'allow' | 'revise',
    studentId: number,
  ) => {
    if (!controlData) return;
    try {
      await lessonRunsApi.reflectionGateDecision(controlData.run.id, gateId, { decision });
      if (decision === 'revise') {
        setRedoRequested((prev) => (prev.includes(studentId) ? prev : [...prev, studentId]));
      }
      await loadSubmissionsInbox();
      await loadControl({ silent: true });
      publishLessonRunSync(controlData.run.id, 'submissions_changed');
    } catch (err: any) {
      setSubmissionsError(err?.response?.data?.error || 'Failed to update reflection gate');
    }
  };

  const decideSubmissionQuality = async (
    checkId: number,
    decision: 'clear' | 'assign_makeup' | 'group_reteach',
    studentId: number,
  ) => {
    if (!controlData) return;
    try {
      await lessonRunsApi.submissionQualityDecision(controlData.run.id, checkId, { decision });
      if (decision !== 'clear') {
        setRedoRequested((prev) => (prev.includes(studentId) ? prev : [...prev, studentId]));
      }
      await loadSubmissionsInbox();
      publishLessonRunSync(controlData.run.id, 'submissions_changed');
    } catch (err: any) {
      setSubmissionsError(err?.response?.data?.error || 'Failed to update submission quality decision');
    }
  };

  const inferDeliveryMode = useCallback((): 'replay_exam' | 'replay_lesson' => {
    if (!controlData) return 'replay_lesson';
    const steps = Array.isArray(controlData.steps) ? controlData.steps : [];
    if (steps.length === 0) return 'replay_lesson';
    let mcqLike = 0;
    steps.forEach((step) => {
      const signalConfig = (step as any)?.signal_config || {};
      const signalType = String(signalConfig.signal_type || signalConfig.type || '').toLowerCase().replace(/-/g, '_');
      const options = signalConfig.options || signalConfig.choices || [];
      if (
        signalType === 'mcq'
        || signalType === 'mcq_single'
        || signalType === 'mcq_multi'
        || (Array.isArray(options) && options.length > 0)
      ) {
        mcqLike += 1;
      }
    });
    const ratio = mcqLike / steps.length;
    return ratio >= 0.6 ? 'replay_exam' : 'replay_lesson';
  }, [controlData]);

  const resolveCustomTrackTargetIds = useCallback((source?: number[]): number[] => {
    const fallback = Array.isArray(source) ? source : trackTargetStudentIds;
    return normalizeStudentIds(fallback);
  }, [normalizeStudentIds, trackTargetStudentIds]);

  const syncTrackedAssignment = useCallback(async (options?: {
    scope?: TrackTargetScope;
    dueAt?: string;
    customTargetIds?: number[];
  }) => {
    if (!controlData) return false;
    const scope = options?.scope || trackTargetScope;
    const dueAtValue = options?.dueAt ?? trackDueAt;
    const customTargetIds = resolveCustomTrackTargetIds(options?.customTargetIds);
    const validationError = validateTrackSettings(scope, dueAtValue, customTargetIds);
    if (validationError) {
      setTrackError(validationError);
      return false;
    }
    const parsedDueAt = dueAtValue ? new Date(dueAtValue) : null;
    const isoDueAt = parsedDueAt ? parsedDueAt.toISOString() : null;
    const targetPayload = scope === 'custom'
      ? { target_scope: 'custom' as const, target_student_ids: customTargetIds }
      : { target_scope: scope };
    await lessonRunsApi.trackAssignment(controlData.run.id, {
      track: true,
      due_at: isoDueAt,
      delivery_mode: inferDeliveryMode(),
      ...targetPayload,
    });
    setTrackAssignment(true);
    setTrackTargetScope(scope);
    setTrackDueAt(dueAtValue);
    if (scope === 'custom') {
      setTrackTargetStudentIds(customTargetIds);
    }
    setTrackError(null);
    await loadTrackAssignment();
    setTrackLastSavedAt(new Date());
    publishLessonRunSync(controlData.run.id, 'control_changed');
    return true;
  }, [
    controlData,
    inferDeliveryMode,
    loadTrackAssignment,
    resolveCustomTrackTargetIds,
    trackTargetScope,
    trackDueAt,
    validateTrackSettings,
  ]);

  const hasTrackSettingsConfigured = useMemo(() => (
    Boolean(trackDueAt)
    || trackTargetScope !== 'needs_makeup'
    || trackTargetStudentIds.length > 0
  ), [trackDueAt, trackTargetScope, trackTargetStudentIds]);

  const handleTrackAssignment = async (nextValue: boolean) => {
    if (!controlData) return;
    try {
      setTrackLoading(true);
      if (nextValue) {
        const hadConfig = hasTrackSettingsConfigured;
        const synced = await syncTrackedAssignment();
        if (!synced) return;
        if (!hadConfig && RUN_SETTINGS_MODAL_V1) {
          openRunSettings({ trackAssignment: true });
        }
      } else {
        await lessonRunsApi.trackAssignment(controlData.run.id, { track: false });
        setTrackAssignment(false);
        setTrackError(null);
        await loadTrackAssignment();
        setTrackLastSavedAt(new Date());
        publishLessonRunSync(controlData.run.id, 'control_changed');
      }
    } catch (err: any) {
      setTrackError(err?.response?.data?.error || 'Failed to update assignment tracking');
    } finally {
      setTrackLoading(false);
    }
  };

  const openEndRunDialog = useCallback(() => {
    const autoPrepareMakeup = controlData?.run.auto_makeup_for_absent_live_lessons !== false;
    const trackedAudienceIds = normalizeStudentIds(trackAudience?.buckets?.all || []);
    const fallbackAudienceIds = controlData?.run.pacing_mode === 'selective'
      ? normalizeStudentIds(controlData?.joined_student_ids || [])
      : normalizeStudentIds((controlData?.students || []).map((student) => student.id));
    const audienceIds = trackedAudienceIds.length > 0 ? trackedAudienceIds : fallbackAudienceIds;
    const audienceBuckets = trackAudience?.buckets && typeof trackAudience.buckets === 'object'
      ? trackAudience.buckets as Record<string, number[]>
      : {};
    const nextAttendance: Record<number, EndRunAttendanceEntry> = {};
    const studentById = new Map(
      (controlData?.students || []).map((student) => [Number(student.id), student]),
    );
    audienceIds.forEach((studentId) => {
      const student = studentById.get(studentId);
      const dailyAttendanceStatus = student?.daily_attendance_status;
      const inferredAbsent = dailyAttendanceStatus
        ? dailyAttendanceStatus === 'absent'
        : student?.participation_status === 'absent'
          || studentInBucket(audienceBuckets, 'absent', studentId);
      nextAttendance[studentId] = {
        status: inferredAbsent ? 'absent' : 'present',
        assignMakeup: inferredAbsent && autoPrepareMakeup,
      };
    });
    const hasSuggestedMakeup = Object.values(nextAttendance).some((entry) => entry.assignMakeup);
    setEndRunAttendance(nextAttendance);
    setEndRunCreateMakeup(trackAssignment || (autoPrepareMakeup && hasSuggestedMakeup));
    setEndRunDueAt(trackDueAt);
    setEndRunError(null);
    setEndRunDialogOpen(true);
  }, [controlData?.joined_student_ids, controlData?.run.auto_makeup_for_absent_live_lessons, controlData?.run.pacing_mode, controlData?.students, normalizeStudentIds, trackAssignment, trackAudience?.buckets, trackDueAt]);

  const handleConfirmEndRun = useCallback(async () => {
    if (!controlData) return;
    try {
      setEndRunSubmitting(true);
      setEndRunError(null);
      const attendance = Object.entries(endRunAttendance).map(([studentId, entry]) => ({
        student_id: Number(studentId),
        status: entry.status,
        assign_makeup: endRunCreateMakeup && entry.status === 'absent' && entry.assignMakeup,
      }));
      const parsedDueAt = endRunDueAt ? new Date(endRunDueAt) : null;
      const response = await lessonRunsApi.end(controlData.run.id, {
        attendance,
        create_makeup: endRunCreateMakeup,
        due_at: parsedDueAt ? parsedDueAt.toISOString() : null,
        delivery_mode: inferDeliveryMode(),
      });
      const targetCount = Number(response?.data?.makeup_student_count || 0);
      const absentCount = attendance.filter((entry) => entry.status === 'absent').length;
      let teacherNotice = `Run ended. Attendance confirmed for ${attendance.length} students.`;
      if (targetCount > 0) {
        teacherNotice += ` Make-up assigned to ${targetCount} ${targetCount === 1 ? 'student' : 'students'}.`;
      } else if (absentCount > 0) {
        teacherNotice += ` ${absentCount} marked absent without make-up.`;
      }
      if (embeddedRunId) { setEndRunDialogOpen(false); onRunEnded?.(); return; }
      navigate('/v2/teacher', {
        state: {
          teacher_notice: teacherNotice,
        },
      });
    } catch (err: any) {
      setEndRunError(err?.response?.data?.error || 'Failed to end run.');
    } finally {
      setEndRunSubmitting(false);
    }
  }, [
    controlData,
    endRunAttendance,
    endRunDueAt,
    endRunCreateMakeup,
    inferDeliveryMode,
    navigate,
  ]);

  const endRunAttendanceRows = useMemo(() => {
    const studentById = new Map(
      (controlData?.students || []).map((student) => [Number(student.id), student]),
    );
    return Object.entries(endRunAttendance)
      .map(([studentId, entry]) => ({
        studentId: Number(studentId),
        name: studentById.get(Number(studentId))?.name || `Student ${studentId}`,
        inferredStatus: studentById.get(Number(studentId))?.participation_status,
        dailyAttendanceStatus: studentById.get(Number(studentId))?.daily_attendance_status,
        attendanceLocalDate: studentById.get(Number(studentId))?.attendance_local_date,
        ...entry,
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [controlData?.students, endRunAttendance]);

  const trackAudienceBuckets = useMemo<Record<string, number[]>>(() => {
    const raw = trackAudience?.buckets;
    return raw && typeof raw === 'object' ? raw as Record<string, number[]> : {};
  }, [trackAudience]);

  const runSettingsPreviewTargetIds = useMemo(() => {
    if (!runSettingsDraftTrackAssignment) return [];
    if (runSettingsDraftTargetScope === 'custom') {
      return normalizeStudentIds(runSettingsDraftStudentIds);
    }
    const bucket = trackAudienceBuckets[runSettingsDraftTargetScope];
    if (!Array.isArray(bucket)) return [];
    return normalizeStudentIds(bucket);
  }, [
    normalizeStudentIds,
    runSettingsDraftStudentIds,
    runSettingsDraftTargetScope,
    runSettingsDraftTrackAssignment,
    trackAudienceBuckets,
  ]);

  const runSettingsPreviewTargetNames = useMemo(() => {
    const byId = new Map<number, string>();
    (controlData?.students || []).forEach((student) => {
      byId.set(Number(student.id), String(student.name || `Student ${student.id}`));
    });
    return runSettingsPreviewTargetIds
      .map((studentId) => byId.get(studentId) || `Student ${studentId}`)
      .slice(0, 8);
  }, [controlData?.students, runSettingsPreviewTargetIds]);

  const runSettingsPreviewTargetIdSet = useMemo(() => (
    new Set(runSettingsPreviewTargetIds)
  ), [runSettingsPreviewTargetIds]);

  const runSettingsPreviewTargetRows = useMemo(() => (
    runSettingsPreviewTargetIds.map((studentId) => ({
      id: studentId,
      name: rosterNameById.get(studentId) || `Student ${studentId}`,
      reason: buildQueueReason({
        buckets: trackAudienceBuckets,
        studentId,
        scope: runSettingsDraftTargetScope,
        matchesCurrentDraft: true,
        activeQueue: false,
      }),
    }))
  ), [rosterNameById, runSettingsDraftTargetScope, runSettingsPreviewTargetIds, trackAudienceBuckets]);

  const activeMakeupQueueStudentIds = useMemo(() => (
    normalizeStudentIds(trackTargetStudentIds)
  ), [normalizeStudentIds, trackTargetStudentIds]);

  const activeMakeupQueueTargetRows = useMemo(() => (
    activeMakeupQueueStudentIds.map((studentId) => ({
      id: studentId,
      name: rosterNameById.get(studentId) || `Student ${studentId}`,
      reason: buildQueueReason({
        buckets: trackAudienceBuckets,
        studentId,
        scope: runSettingsDraftTargetScope,
        matchesCurrentDraft: runSettingsPreviewTargetIdSet.has(studentId),
        activeQueue: true,
      }),
    }))
  ), [
    activeMakeupQueueStudentIds,
    rosterNameById,
    runSettingsDraftTargetScope,
    runSettingsPreviewTargetIdSet,
    trackAudienceBuckets,
  ]);

  const queueOverrideTargetRows = useMemo(() => (
    trackAssignment ? activeMakeupQueueTargetRows : runSettingsPreviewTargetRows
  ), [activeMakeupQueueTargetRows, runSettingsPreviewTargetRows, trackAssignment]);

  const queueOverrideContextText = useMemo(() => {
    const runLabel = controlData?.run?.id ? `Run #${controlData.run.id}` : 'Run';
    if (!trackAssignmentInfo || !trackAssignmentInfo.id) {
      return `${runLabel} | ${trackAssignment ? 'Assignment metadata unavailable' : 'Assignment not saved yet'}`;
    }
    const sectionName = String(trackAssignmentInfo.section_name || '').trim();
    const assignmentSectionId = Number(trackAssignmentInfo.section || 0);
    const fallbackSectionId = Number(controlData?.run?.section || 0);
    const sectionValue = sectionName
      || (assignmentSectionId > 0
        ? `Section #${assignmentSectionId}`
        : (fallbackSectionId > 0 ? `Section #${fallbackSectionId}` : 'Section'));
    return `Assignment #${trackAssignmentInfo.id} | ${runLabel} | ${sectionValue}`;
  }, [controlData?.run?.id, controlData?.run?.section, trackAssignment, trackAssignmentInfo]);

  useEffect(() => {
    const availableIds = new Set(queueOverrideTargetRows.map((row) => row.id));
    setMakeupRemovalSelection((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<number>();
      prev.forEach((studentId) => {
        if (availableIds.has(studentId)) {
          next.add(studentId);
        }
      });
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [queueOverrideTargetRows]);

  const runSettingsLastSavedLabel = useMemo(() => {
    if (!trackLastSavedAt) return '';
    return trackLastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, [trackLastSavedAt]);

  const handleRunSettingsSave = async () => {
    if (!controlData) return;
    try {
      setRunSettingsSaving(true);
      setRunSettingsError(null);

      const nextTestMode = Boolean(runSettingsDraftTestMode);
      const nextTrackEnabled = Boolean(runSettingsDraftTrackAssignment);
      const nextScope = runSettingsDraftTargetScope;
      const nextDueAt = runSettingsDraftDueAt;
      const nextCustomIds = normalizeStudentIds(runSettingsDraftStudentIds);
      const nextReviewAccess = runSettingsDraftReviewAccess;
      const nextAllowCatchup = Boolean(runSettingsDraftAllowCatchup);
      const nextShowReviewAnswers = Boolean(runSettingsDraftShowReviewAnswers);

      if (nextTrackEnabled) {
        const validationError = validateTrackSettings(nextScope, nextDueAt, nextCustomIds);
        if (validationError) {
          setRunSettingsError(validationError);
          return;
        }
      }

      if (nextTestMode !== Boolean(controlData.run.is_test_mode)) {
        if (nextTestMode && testModeBlockedByStudentWork) {
          setRunSettingsError('This run has responses from multiple students, so it must stay included in reports.');
          return;
        }
        const applied = await applyTestMode(nextTestMode);
        if (!applied) {
          setRunSettingsError('Failed to update test mode.');
          return;
        }
      }

      if (nextTrackEnabled) {
        const synced = await syncTrackedAssignment({
          scope: nextScope,
          dueAt: nextDueAt,
          customTargetIds: nextCustomIds,
        });
        if (!synced) {
          setRunSettingsError('Resolve make-up assignment settings before saving.');
          return;
        }
      } else if (trackAssignment) {
        await lessonRunsApi.trackAssignment(controlData.run.id, { track: false });
        setTrackAssignment(false);
        setTrackError(null);
        await loadTrackAssignment();
        setTrackLastSavedAt(new Date());
        publishLessonRunSync(controlData.run.id, 'control_changed');
      }

      const currentReviewAccess = String(controlData.run.review_access || 'after_end').trim().toLowerCase();
      const reviewChanged = (
        currentReviewAccess !== nextReviewAccess
        || Boolean(controlData.run.allow_catchup) !== nextAllowCatchup
        || Boolean(controlData.run.show_assessment_answers_in_review) !== nextShowReviewAnswers
      );
      if (reviewChanged) {
        const reviewResult = await lessonRunsApi.reviewPolicy(controlData.run.id, {
          review_access: nextReviewAccess,
          allow_catchup: nextAllowCatchup,
          show_assessment_answers_in_review: nextShowReviewAnswers,
        });
        setControlData((prev) => (
          prev
            ? {
                ...prev,
                run: {
                  ...prev.run,
                  review_access: reviewResult.review_access,
                  allow_catchup: Boolean(reviewResult.allow_catchup),
                  show_assessment_answers_in_review: Boolean(reviewResult.show_assessment_answers_in_review),
                },
              }
            : prev
        ));
      }

      setRunSettingsOpen(false);
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (err: any) {
      setRunSettingsError(err?.response?.data?.error || 'Failed to save run settings.');
    } finally {
      setRunSettingsSaving(false);
    }
  };

  const handleExcuseStudentsFromMakeup = useCallback(async (studentIds: number[]) => {
    if (!controlData || !trackAssignment) return;
    const normalizedIds = Array.from(
      new Set(
        (Array.isArray(studentIds) ? studentIds : [])
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value)),
      ),
    );
    if (normalizedIds.length === 0) return;
    try {
      setTrackLoading(true);
      setTrackError(null);
      setMakeupExcusingStudentIds((prev) => {
        const next = new Set(prev);
        normalizedIds.forEach((studentId) => next.add(studentId));
        return next;
      });

      const trackPayload = await lessonRunsApi.getTrackAssignment(controlData.run.id);
      const assignmentId = Number(trackPayload?.assignment?.id || 0);
      if (!Number.isInteger(assignmentId) || assignmentId <= 0) {
        throw new Error('No active make-up assignment found.');
      }

      await Promise.all(
        normalizedIds.map((studentId) => assignmentsApi.excuseStudent(
          assignmentId,
          studentId,
          {
            excused: true,
            reason: 'Removed by teacher from make-up queue.',
          },
        )),
      );

      await loadTrackAssignment();
      setTrackLastSavedAt(new Date());
      publishLessonRunSync(controlData.run.id, 'control_changed');
      setRunSettingsDraftStudentIds((prev) => prev.filter((id) => !normalizedIds.includes(id)));
      setMakeupRemovalSelection((prev) => {
        const next = new Set(prev);
        normalizedIds.forEach((studentId) => next.delete(studentId));
        return next;
      });
    } catch (err: any) {
      setTrackError(err?.response?.data?.error || err?.message || 'Failed to remove student from make-up queue.');
    } finally {
      setMakeupExcusingStudentIds((prev) => {
        const next = new Set(prev);
        normalizedIds.forEach((studentId) => next.delete(studentId));
        return next;
      });
      setTrackLoading(false);
    }
  }, [controlData, loadTrackAssignment, trackAssignment]);

  const handleExcuseStudentFromMakeup = useCallback(async (studentId: number) => {
    await handleExcuseStudentsFromMakeup([studentId]);
  }, [handleExcuseStudentsFromMakeup]);

  const handleToggleMakeupRemovalSelection = useCallback((studentId: number) => {
    setMakeupRemovalSelection((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }, []);

  const handleSelectAllMakeupRemovalTargets = useCallback(() => {
    setMakeupRemovalSelection(new Set(queueOverrideTargetRows.map((row) => row.id)));
  }, [queueOverrideTargetRows]);

  const handleClearMakeupRemovalSelection = useCallback(() => {
    setMakeupRemovalSelection(new Set());
  }, []);

  const handleExcuseSelectedStudentsFromMakeup = useCallback(() => {
    void handleExcuseStudentsFromMakeup(Array.from(makeupRemovalSelection));
  }, [handleExcuseStudentsFromMakeup, makeupRemovalSelection]);

  const updatePacing = async (pacingMode: 'whole_class' | 'selective') => {
    if (!controlData) return;
    try {
      const response = await lessonRunsApi.pacing(controlData.run.id, pacingMode);
      setControlData(prev => prev ? { ...prev, run: response.data.run } : null);
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (err) {
      console.error('Failed to update pacing mode:', err);
    }
  };

  async function applyTestMode(nextMode: boolean): Promise<boolean> {
    if (!controlData) return false;
    if (nextMode && testModeBlockedByStudentWork) {
      throw new Error('This run has responses from multiple students, so it must stay included in reports.');
    }
    const response = await lessonRunsApi.setTestMode(controlData.run.id, nextMode);
    const nextRun = response?.data?.run;
    if (!nextRun) return false;
    setControlData((prev) => (prev ? { ...prev, run: nextRun } : prev));
    const nextContext = {
      section: nextRun.section,
      lesson_plan: nextRun.lesson_plan,
      is_test_mode: Boolean(nextRun.is_test_mode),
    };
    setLastRunContext(nextContext);
    if (runId) {
      localStorage.setItem(`lessonRunContext:${runId}`, JSON.stringify(nextContext));
    }
    publishLessonRunSync(controlData.run.id, 'control_changed');
    return true;
  }

  const handleTestModeToggle = async (nextMode: boolean) => {
    if (!controlData) return;
    try {
      setTestModeSaving(true);
      setTestModeError(null);
      await applyTestMode(nextMode);
    } catch (err: any) {
      setTestModeError(err?.response?.data?.error || err?.message || 'Failed to update test mode');
    } finally {
      setTestModeSaving(false);
    }
  };

  const handleMicroAssist = async (actionType: string) => {
    if (!controlData || !lessonOpsActions.runIdNumber) return;
    try {
      setAiAssistLoading(true);
      setAiAssistError(null);
      const response = await lessonOpsActions.requestMicroAssist({
        action_type: actionType,
        context: {
          ...assistContextBase,
          liveSignals: liveSignalStats
            ? {
                total_students: liveSignalStats.total_students,
                step_signal_submitted: liveSignalStats.step_signal_submitted,
                step_signal_pending: liveSignalStats.step_signal_pending,
                missing_reasoning: liveSignalStats.missing_reasoning,
                missing_confidence: liveSignalStats.missing_confidence,
              }
            : null,
        },
      });
      const payload = response?.data || {};
      setAiSuggestion(String(payload?.suggestion_text || ''));
      setAiStepByStep(String(payload?.step_by_step || ''));
      setAiCommonMistakes(Array.isArray(payload?.common_mistakes) ? payload.common_mistakes.map((item: any) => String(item)) : []);
      setAiDelayRevealTriggered(Boolean(payload?.delay_reveal_triggered));
      setAiDelayRevealReason(String(payload?.delay_reveal_reason || ''));
      applyAssistFeedbackFromPayload(payload, actionType);
    } catch (err: any) {
      setAiAssistError(err?.response?.data?.error || 'Failed to get suggestion');
      setAiStepByStep('');
      setAiCommonMistakes([]);
      setAiDelayRevealTriggered(false);
      setAiDelayRevealReason('');
    } finally {
      setAiAssistLoading(false);
    }
  };

  const loadClassAskThread = useCallback(async (stepIdOverride?: number | null) => {
    const targetStepId = Number(stepIdOverride ?? currentStepId);
    if (!lessonOpsActions.runIdNumber || !Number.isInteger(targetStepId) || targetStepId <= 0) {
      setClassAskThread(EMPTY_TEACHER_AI_THREAD);
      return;
    }
    try {
      setClassAskThreadLoading(true);
      setClassAskThreadError(null);
      const payload = await lessonOpsActions.loadTeacherAiThread({
        surface: 'class_draft_chat',
        step_id: targetStepId,
      });
      setClassAskThread(payload?.thread || EMPTY_TEACHER_AI_THREAD);
    } catch (err: any) {
      setClassAskThreadError(err?.response?.data?.error || err?.message || 'Failed to load Ask AI thread.');
      setClassAskThread(EMPTY_TEACHER_AI_THREAD);
    } finally {
      setClassAskThreadLoading(false);
    }
  }, [currentStepId, lessonOpsActions]);

  const loadTeacherChatThread = useCallback(async () => {
    if (!lessonOpsActions.runIdNumber) {
      setTeacherChatThread(EMPTY_TEACHER_AI_THREAD);
      return;
    }
    try {
      setTeacherChatThreadLoading(true);
      setTeacherChatThreadError(null);
      const payload = await lessonOpsActions.loadTeacherAiThread({
        surface: 'teacher_chat',
      });
      setTeacherChatThread(payload?.thread || EMPTY_TEACHER_AI_THREAD);
    } catch (err: any) {
      setTeacherChatThreadError(err?.response?.data?.error || err?.message || 'Failed to load teacher chat.');
      setTeacherChatThread(EMPTY_TEACHER_AI_THREAD);
    } finally {
      setTeacherChatThreadLoading(false);
    }
  }, [lessonOpsActions]);

  const handleOpenExplainToClass = useCallback((options?: { tab?: ClassAiTab; draftText?: string }) => {
    const nextTab = options?.tab || 'draft';
    setClassAiModalOpen(true);
    setClassAiTab(nextTab);
    setClassAiError(null);
    setClassAiSuccess(null);
    setCurrentAssistFeedbackId(null);
    setCurrentAssistActionType('');
    setAssistUsefulnessVote(null);
    setAssistUsedInClass(null);
    setAssistFeedbackError(null);
    if (typeof options?.draftText === 'string') {
      setActiveClassDraftId(null);
      setClassDraft(options.draftText);
      setClassDraftLibraryNotice(null);
    }
    if (nextTab === 'ask' && currentStepId) {
      void loadClassAskThread(currentStepId);
    }
  }, [currentStepId, loadClassAskThread]);

  const handleGenerateClassDraft = useCallback(async (nextMode: ClassDraftMode = classDraftMode) => {
    if (!currentStepId || !lessonOpsActions.runIdNumber) return;
    try {
      setClassDraftLoading(true);
      setClassAiError(null);
      setClassAiSuccess(null);
      const response = await lessonOpsActions.requestTeacherAi({
        surface: 'class_draft',
        step_id: Number(currentStepId),
        draft_mode: nextMode,
      });
      const payload = response?.data || {};
      const nextDraft = String(payload?.draft_md || '').trim();
      if (!nextDraft) {
        setClassAiError('AI draft failed: empty response.');
        return;
      }
      setClassDraftMode(nextMode);
      setActiveClassDraftId(null);
      setClassDraft(nextDraft);
      setClassDraftLibraryNotice(null);
      applyAssistFeedbackFromPayload(payload, 'class_draft');
    } catch (err: any) {
      setClassAiError(err?.response?.data?.error || 'Failed to generate class draft.');
    } finally {
      setClassDraftLoading(false);
    }
  }, [applyAssistFeedbackFromPayload, classDraftMode, currentStepId, lessonOpsActions]);

  const handleSendClassDraft = useCallback(async () => {
    if (!controlData || !currentStepId) return;
    const content = classDraft.trim();
    if (!content) {
      setClassAiError('Draft text is required.');
      return;
    }
    const validationIssues = validateExplainBroadcastContent({
      content,
      stepPromptMd: currentStepPromptMd,
    });
    if (validationIssues.length > 0) {
      setClassAiError(validationIssues[0]);
      return;
    }
    try {
      setClassDraftSending(true);
      setClassAiError(null);
      await lessonRunsApi.setBroadcast(controlData.run.id, {
        kind: 'explain',
        step_id: Number(currentStepId),
        content_md: content,
      });
      await loadControl();
      publishLessonRunSync(controlData.run.id, 'broadcast_changed');
      setClassAiModalOpen(false);
      setClassAiSuccess('Explanation sent to class.');
    } catch (err: any) {
      setClassAiError(err?.response?.data?.error || 'Failed to send explanation.');
    } finally {
      setClassDraftSending(false);
    }
  }, [classDraft, controlData, currentStepId, currentStepPromptMd, loadControl]);

  const handleClearClassDraft = useCallback(() => {
    setActiveClassDraftId(null);
    setClassDraft('');
    setClassAiError(null);
    setClassAiSuccess(null);
    setClassDraftLibraryNotice(null);
  }, []);

  const handleSendClassAsk = useCallback(async () => {
    if (!currentStepId || !lessonOpsActions.runIdNumber) return;
    const question = classAskPrompt.trim();
    if (!question) {
      setClassAskThreadError('A question is required.');
      return;
    }
    try {
      setClassAskSending(true);
      setClassAskThreadError(null);
      const parentId = classAskThread.messages[classAskThread.messages.length - 1]?.id;
      const response = await lessonOpsActions.requestTeacherAi({
        surface: 'class_draft_chat',
        step_id: Number(currentStepId),
        question,
        parent_event_id: parentId,
      });
      const payload = response?.data || {};
      setClassAskThread(payload?.thread || EMPTY_TEACHER_AI_THREAD);
      setClassAskPrompt('');
    } catch (err: any) {
      setClassAskThreadError(err?.response?.data?.error || 'Failed to send Ask AI message.');
    } finally {
      setClassAskSending(false);
    }
  }, [classAskPrompt, classAskThread.messages, currentStepId, lessonOpsActions]);

  const handleUseClassReplyAsDraft = useCallback((markdownText: string) => {
    const nextDraft = String(markdownText || '').trim();
    if (!nextDraft) return;
    if (classDraft.trim() && classDraft.trim() !== nextDraft) {
      const confirmed = window.confirm('Replace the current class draft with this AI reply?');
      if (!confirmed) return;
    }
    setActiveClassDraftId(null);
    setClassDraft(nextDraft);
    setClassAiTab('draft');
    setClassAiError(null);
    setClassAiSuccess(null);
    setClassDraftLibraryNotice(null);
  }, [classDraft]);

  const requestStepByStepAssist = async (
    actionType: 'coding_step_by_step' | 'explain_step_by_step',
    panelTitle: string,
    walkthroughMode: 'teacher_deep' | 'student_friendly' = stepByStepMode,
    walkthroughAdjustment: 'standard' | 'shorter' | 'more_scaffolding' = stepByStepAdjustment,
  ) => {
    if (!controlData || !currentStepId || !lessonOpsActions.runIdNumber) return;
    setStepByStepModalTitle(panelTitle);
    setStepByStepActionType(actionType);
    setStepByStepMode(walkthroughMode);
    setStepByStepAdjustment(walkthroughAdjustment);
    setStepByStepModalOpen(true);
    setAiStepByStep('');
    setAiCommonMistakes([]);
    setStepByStepCopyStatus('idle');
    try {
      setAiAssistLoading(true);
      setAiAssistError(null);
      const response = await lessonOpsActions.requestMicroAssist({
        action_type: actionType,
        context: {
          ...assistContextBase,
          responseFormat: 'markdown',
          walkthrough_mode: walkthroughMode,
          walkthroughMode: walkthroughMode,
          walkthrough_adjustment: walkthroughAdjustment,
          walkthroughAdjustment: walkthroughAdjustment,
        },
      });
      const payload = response?.data || {};
      setAiStepByStep(String(payload?.step_by_step || payload?.suggestion_text || ''));
      setAiCommonMistakes(Array.isArray(payload?.common_mistakes) ? payload.common_mistakes.map((item: any) => String(item)) : []);
      setAiSuggestion(String(payload?.suggestion_text || ''));
      applyAssistFeedbackFromPayload(payload, actionType);
    } catch (err: any) {
      setAiAssistError(err?.response?.data?.error || 'Failed to generate step-by-step');
    } finally {
      setAiAssistLoading(false);
    }
  };

  const handleCodingStepByStep = async () => {
    await requestStepByStepAssist('coding_step_by_step', 'Coding step-by-step');
  };

  const handleGenerateTeacherWalkthrough = useCallback(async (
    nextMode: TeacherWalkthroughMode = teacherWalkthroughMode,
    nextAdjustment: TeacherWalkthroughAdjustment = teacherWalkthroughAdjustment,
  ) => {
    if (!currentStepId || !lessonOpsActions.runIdNumber) return;
    try {
      setTeacherWalkthroughLoading(true);
      setTeacherWalkthroughError(null);
      const response = await lessonOpsActions.requestTeacherAi({
        surface: 'teacher_walkthrough',
        step_id: Number(currentStepId),
        walkthrough_mode: nextMode,
        walkthrough_adjustment: nextAdjustment,
      });
      const payload = response?.data || {};
      const nextWalkthroughMd = String(payload?.walkthrough_md || '').trim();
      const nextWalkthrough = normalizeTeacherWalkthrough(payload?.walkthrough, nextWalkthroughMd);
      if (
        !nextWalkthroughMd
        && !nextWalkthrough.final_answer
        && nextWalkthrough.step_by_step.length === 0
      ) {
        setTeacherWalkthroughError('Walkthrough generation returned an empty response.');
        return;
      }
      setTeacherWalkthroughMode(nextMode);
      setTeacherWalkthroughAdjustment(nextAdjustment);
      setActiveTeacherWalkthroughDraftId(null);
      setTeacherWalkthrough(nextWalkthrough);
      setTeacherWalkthroughMd(nextWalkthroughMd || teacherWalkthroughToMarkdown(nextWalkthrough));
      setTeacherWalkthroughLibraryNotice(null);
      applyAssistFeedbackFromPayload(payload, 'teacher_walkthrough');
    } catch (err: any) {
      setTeacherWalkthroughError(err?.response?.data?.error || 'Failed to generate teacher walkthrough.');
    } finally {
      setTeacherWalkthroughLoading(false);
    }
  }, [
    applyAssistFeedbackFromPayload,
    currentStepId,
    lessonOpsActions,
    teacherWalkthroughAdjustment,
    teacherWalkthroughMode,
  ]);

  const handleOpenTeacherAi = useCallback((tab: TeacherAiTab = 'walkthrough') => {
    setTeacherAiModalOpen(true);
    setTeacherAiTab(tab);
    setTeacherWalkthroughError(null);
    setTeacherChatThreadError(null);
    setCurrentAssistFeedbackId(null);
    setCurrentAssistActionType('');
    setAssistUsefulnessVote(null);
    setAssistUsedInClass(null);
    setAssistFeedbackError(null);
    if (tab === 'chat') {
      void loadTeacherChatThread();
      return;
    }
    if (!teacherWalkthrough && !teacherWalkthroughMd.trim()) {
      void handleGenerateTeacherWalkthrough();
    }
  }, [handleGenerateTeacherWalkthrough, loadTeacherChatThread, teacherWalkthrough, teacherWalkthroughMd]);

  const handleSendTeacherChat = useCallback(async () => {
    if (!lessonOpsActions.runIdNumber) return;
    const question = teacherChatPrompt.trim();
    if (!question) {
      setTeacherChatThreadError('A question is required.');
      return;
    }
    try {
      setTeacherChatSending(true);
      setTeacherChatThreadError(null);
      const parentId = teacherChatThread.messages[teacherChatThread.messages.length - 1]?.id;
      const response = await lessonOpsActions.requestTeacherAi({
        surface: 'teacher_chat',
        question,
        parent_event_id: parentId,
        include_lesson_context: teacherChatUseLessonContext,
      });
      const payload = response?.data || {};
      setTeacherChatThread(payload?.thread || EMPTY_TEACHER_AI_THREAD);
      setTeacherChatPrompt('');
    } catch (err: any) {
      setTeacherChatThreadError(err?.response?.data?.error || 'Failed to send teacher chat message.');
    } finally {
      setTeacherChatSending(false);
    }
  }, [
    lessonOpsActions,
    teacherChatPrompt,
    teacherChatThread.messages,
    teacherChatUseLessonContext,
  ]);

  const handleUseWalkthroughInClassDraft = useCallback(() => {
    const nextDraft = buildClassDraftFromWalkthroughData(teacherWalkthrough)
      || buildClassDraftFromWalkthrough(teacherWalkthroughMd);
    if (!nextDraft) {
      setTeacherWalkthroughError('No class-ready content is available in this walkthrough yet.');
      return;
    }
    if (classDraft.trim() && classDraft.trim() !== nextDraft) {
      const confirmed = window.confirm('Replace the current class draft with this walkthrough content?');
      if (!confirmed) return;
    }
    setActiveClassDraftId(null);
    setClassDraft(nextDraft);
    setClassAiModalOpen(true);
    setClassAiTab('draft');
    setClassAiError(null);
    setClassAiSuccess(null);
    setClassDraftLibraryNotice(null);
  }, [classDraft, teacherWalkthrough, teacherWalkthroughMd]);

  const promptForSavedDraftTitle = useCallback((label: string, initialValue: string) => {
    const nextTitle = window.prompt(`Name this saved ${label}.`, initialValue);
    if (nextTitle === null) return undefined;
    const trimmed = nextTitle.trim();
    return trimmed || null;
  }, []);

  const handleLoadClassSavedDraft = useCallback((draftId: number) => {
    const draft = classSavedDrafts.find((item) => item.id === draftId) || null;
    applyClassDraftFromSaved(draft);
  }, [applyClassDraftFromSaved, classSavedDrafts]);

  const handleSaveClassDraft = useCallback(async (mode: 'current' | 'new') => {
    if (!currentStepId) return;
    const content = classDraft.trim();
    if (!content) {
      setClassAiError('Draft text is required.');
      return;
    }
    const defaultTitle = activeClassSavedDraft?.title
      || (classDraftMode === 'hint_only' ? 'Hint only' : classDraftMode === 'step_by_step' ? 'Step-by-step' : 'Quick explain');
    const nextTitle = mode === 'new' || !activeClassSavedDraft
      ? promptForSavedDraftTitle('class draft', defaultTitle)
      : activeClassSavedDraft.title;
    if (typeof nextTitle === 'undefined') return;
    if (!nextTitle) {
      setClassAiError('A title is required to save this draft.');
      return;
    }
    try {
      setClassDraftSaving(true);
      setClassAiError(null);
      const payload = mode === 'current' && activeClassSavedDraft
        ? await lessonStepsApi.updateAiDraft(Number(currentStepId), activeClassSavedDraft.id, {
          title: nextTitle,
          content_md: content,
        })
        : await lessonStepsApi.createAiDraft(Number(currentStepId), {
          surface: 'class_draft',
          title: nextTitle,
          content_md: content,
        });
      const savedDraft = normalizeLessonStepAiDraft(payload?.draft);
      await loadStepAiDraftLibrary(currentStepId);
      if (savedDraft) {
        applyClassDraftFromSaved(savedDraft, { silent: true });
      }
      setClassDraftLibraryNotice(`Saved "${nextTitle}" to this step.`);
    } catch (err: any) {
      setClassAiError(err?.response?.data?.error || 'Failed to save class draft.');
    } finally {
      setClassDraftSaving(false);
    }
  }, [
    activeClassSavedDraft,
    applyClassDraftFromSaved,
    classDraft,
    classDraftMode,
    currentStepId,
    loadStepAiDraftLibrary,
    promptForSavedDraftTitle,
  ]);

  const handleSetClassDraftDefault = useCallback(async () => {
    if (!currentStepId || !activeClassSavedDraft) return;
    try {
      setClassDraftSaving(true);
      setClassAiError(null);
      await lessonStepsApi.updateAiDraft(Number(currentStepId), activeClassSavedDraft.id, {
        is_default: true,
      });
      await loadStepAiDraftLibrary(currentStepId);
      setClassDraftLibraryNotice(`"${activeClassSavedDraft.title}" is now the default class draft.`);
    } catch (err: any) {
      setClassAiError(err?.response?.data?.error || 'Failed to set the default class draft.');
    } finally {
      setClassDraftSaving(false);
    }
  }, [activeClassSavedDraft, currentStepId, loadStepAiDraftLibrary]);

  const handleDeleteClassDraft = useCallback(async () => {
    if (!currentStepId || !activeClassSavedDraft) return;
    const confirmed = window.confirm(`Delete "${activeClassSavedDraft.title}" from this step?`);
    if (!confirmed) return;
    try {
      setClassDraftSaving(true);
      await lessonStepsApi.deleteAiDraft(Number(currentStepId), activeClassSavedDraft.id);
      await loadStepAiDraftLibrary(currentStepId, { applyDefaults: true });
      setClassDraftLibraryNotice(`Deleted "${activeClassSavedDraft.title}".`);
    } catch (err: any) {
      setClassAiError(err?.response?.data?.error || 'Failed to delete the saved class draft.');
    } finally {
      setClassDraftSaving(false);
    }
  }, [activeClassSavedDraft, currentStepId, loadStepAiDraftLibrary]);

  const handleLoadTeacherWalkthroughSavedDraft = useCallback((draftId: number) => {
    const draft = teacherWalkthroughSavedDrafts.find((item) => item.id === draftId) || null;
    applyTeacherWalkthroughFromSaved(draft);
  }, [applyTeacherWalkthroughFromSaved, teacherWalkthroughSavedDrafts]);

  const handleSaveTeacherWalkthrough = useCallback(async (mode: 'current' | 'new') => {
    if (!currentStepId) return;
    const content = teacherWalkthroughExportMd.trim();
    if (!content) {
      setTeacherWalkthroughError('Generate or edit a walkthrough before saving it.');
      return;
    }
    const defaultTitle = activeTeacherWalkthroughSavedDraft?.title || 'Teacher walkthrough';
    const nextTitle = mode === 'new' || !activeTeacherWalkthroughSavedDraft
      ? promptForSavedDraftTitle('teacher walkthrough', defaultTitle)
      : activeTeacherWalkthroughSavedDraft.title;
    if (typeof nextTitle === 'undefined') return;
    if (!nextTitle) {
      setTeacherWalkthroughError('A title is required to save this walkthrough.');
      return;
    }
    try {
      setTeacherWalkthroughSaving(true);
      setTeacherWalkthroughError(null);
      const payload = mode === 'current' && activeTeacherWalkthroughSavedDraft
        ? await lessonStepsApi.updateAiDraft(Number(currentStepId), activeTeacherWalkthroughSavedDraft.id, {
          title: nextTitle,
          content_md: content,
          structured_payload: teacherWalkthrough || {},
        })
        : await lessonStepsApi.createAiDraft(Number(currentStepId), {
          surface: 'teacher_walkthrough',
          title: nextTitle,
          content_md: content,
          structured_payload: teacherWalkthrough || {},
        });
      const savedDraft = normalizeLessonStepAiDraft(payload?.draft);
      await loadStepAiDraftLibrary(currentStepId);
      if (savedDraft) {
        applyTeacherWalkthroughFromSaved(savedDraft, { silent: true });
      }
      setTeacherWalkthroughLibraryNotice(`Saved "${nextTitle}" to this step.`);
    } catch (err: any) {
      setTeacherWalkthroughError(err?.response?.data?.error || 'Failed to save this walkthrough.');
    } finally {
      setTeacherWalkthroughSaving(false);
    }
  }, [
    activeTeacherWalkthroughSavedDraft,
    applyTeacherWalkthroughFromSaved,
    currentStepId,
    loadStepAiDraftLibrary,
    promptForSavedDraftTitle,
    teacherWalkthrough,
    teacherWalkthroughExportMd,
  ]);

  const handleSetTeacherWalkthroughDefault = useCallback(async () => {
    if (!currentStepId || !activeTeacherWalkthroughSavedDraft) return;
    try {
      setTeacherWalkthroughSaving(true);
      setTeacherWalkthroughError(null);
      await lessonStepsApi.updateAiDraft(Number(currentStepId), activeTeacherWalkthroughSavedDraft.id, {
        is_default: true,
      });
      await loadStepAiDraftLibrary(currentStepId);
      setTeacherWalkthroughLibraryNotice(`"${activeTeacherWalkthroughSavedDraft.title}" is now the default walkthrough.`);
    } catch (err: any) {
      setTeacherWalkthroughError(err?.response?.data?.error || 'Failed to set the default walkthrough.');
    } finally {
      setTeacherWalkthroughSaving(false);
    }
  }, [activeTeacherWalkthroughSavedDraft, currentStepId, loadStepAiDraftLibrary]);

  const handleDeleteTeacherWalkthroughSavedDraft = useCallback(async () => {
    if (!currentStepId || !activeTeacherWalkthroughSavedDraft) return;
    const confirmed = window.confirm(`Delete "${activeTeacherWalkthroughSavedDraft.title}" from this step?`);
    if (!confirmed) return;
    try {
      setTeacherWalkthroughSaving(true);
      await lessonStepsApi.deleteAiDraft(Number(currentStepId), activeTeacherWalkthroughSavedDraft.id);
      await loadStepAiDraftLibrary(currentStepId, { applyDefaults: true });
      setTeacherWalkthroughLibraryNotice(`Deleted "${activeTeacherWalkthroughSavedDraft.title}".`);
    } catch (err: any) {
      setTeacherWalkthroughError(err?.response?.data?.error || 'Failed to delete the saved walkthrough.');
    } finally {
      setTeacherWalkthroughSaving(false);
    }
  }, [activeTeacherWalkthroughSavedDraft, currentStepId, loadStepAiDraftLibrary]);

  const handleSwitchStepByStepMode = async (nextMode: 'teacher_deep' | 'student_friendly') => {
    if (nextMode === stepByStepMode) return;
    if (!stepByStepModalOpen) {
      setStepByStepMode(nextMode);
      return;
    }
    await requestStepByStepAssist(stepByStepActionType, stepByStepModalTitle, nextMode, stepByStepAdjustment);
  };

  const handleStepByStepAdjustment = async (nextAdjustment: 'shorter' | 'more_scaffolding') => {
    if (!stepByStepModalOpen || !currentStepId) return;
    await requestStepByStepAssist(stepByStepActionType, stepByStepModalTitle, stepByStepMode, nextAdjustment);
  };

  const extractCodeBlocks = useCallback((markdownText: string): string[] => {
    if (!markdownText) return [];
    const matches = markdownText.match(/```[\s\S]*?```/g);
    if (!matches) return [];
    return matches.map((item) => item.trim()).filter((item) => item.length > 0);
  }, []);

  const copyText = useCallback(async (text: string): Promise<boolean> => {
    if (!text.trim()) return false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return Boolean(ok);
    } catch {
      return false;
    }
  }, []);

  const handleCopyStepByStepAll = async () => {
    const sections: string[] = [];
    const body = String(aiStepByStep || '').trim();
    if (body) sections.push(body);
    if (aiCommonMistakes.length > 0) {
      sections.push(`Common mistakes:\n${aiCommonMistakes.map((item) => `- ${item}`).join('\n')}`);
    }
    const finalText = sections.join('\n\n').trim();
    if (!finalText) {
      setStepByStepCopyStatus('empty');
      window.setTimeout(() => setStepByStepCopyStatus('idle'), 1200);
      return;
    }
    const ok = await copyText(finalText);
    setStepByStepCopyStatus(ok ? 'copied_all' : 'failed');
    window.setTimeout(() => setStepByStepCopyStatus('idle'), 1200);
  };

  const handleCopyStepByStepCode = async () => {
    const blocks = extractCodeBlocks(String(aiStepByStep || ''));
    if (blocks.length === 0) {
      setStepByStepCopyStatus('empty');
      window.setTimeout(() => setStepByStepCopyStatus('idle'), 1200);
      return;
    }
    const ok = await copyText(blocks.join('\n\n'));
    setStepByStepCopyStatus(ok ? 'copied_code' : 'failed');
    window.setTimeout(() => setStepByStepCopyStatus('idle'), 1200);
  };

  const parseWalkthroughSections = useCallback((markdownText: string, mistakes: string[]): WalkthroughSnippetSection[] => {
    const sections: WalkthroughSnippetSection[] = [];
    const lines = String(markdownText || '').split('\n');
    let activeHeading = '';
    let activeBody: string[] = [];

    const normalizeHeading = (heading: string): { key: string; label: string; sendable: boolean } => {
      const h = heading.toLowerCase();
      if (h.includes('clarify term') || h.includes('vocabulary anchors')) {
        return { key: 'clarify_term', label: 'Clarify term', sendable: true };
      }
      if (h.includes('common confusion') || h.includes('common mistakes')) {
        return { key: 'misconception_fix', label: 'Misconception fix', sendable: true };
      }
      if (h.includes('say this') || h.includes('goal') || h.includes('step-by-step')) {
        return { key: 'clarify_concept', label: 'Clarify concept', sendable: true };
      }
      if (h.includes('look for this')) {
        return { key: 'look_for_this', label: 'Look for this (Teacher-only)', sendable: false };
      }
      if (h.includes('if students are stuck') || h.includes('scaffolded checkpoints')) {
        return { key: 'teacher_scaffold', label: 'Teacher scaffold (Teacher-only)', sendable: false };
      }
      if (h.includes('algorithm') || h.includes('code walkthrough') || h.includes('code references') || h.includes('debug checks')) {
        return { key: 'clarify_concept', label: 'Clarify concept', sendable: true };
      }
      return { key: `section_${sections.length + 1}`, label: heading || 'Section (Teacher-only)', sendable: false };
    };

    const pushSection = () => {
      const body = activeBody.join('\n').trim();
      if (!activeHeading || !body) return;
      const normalized = normalizeHeading(activeHeading);
      const content = `## ${activeHeading}\n${body}`.trim();
      const existingIdx = sections.findIndex((item) => item.key === normalized.key);
      if (existingIdx >= 0) {
        sections[existingIdx] = {
          ...sections[existingIdx],
          content: `${sections[existingIdx].content}\n\n${content}`.trim(),
          sendable: sections[existingIdx].sendable !== false || normalized.sendable,
        };
        return;
      }
      sections.push({ key: normalized.key, label: normalized.label, content, sendable: normalized.sendable });
    };

    lines.forEach((line) => {
      const headingMatch = line.match(/^##\s+(.+)$/);
      if (headingMatch) {
        pushSection();
        activeHeading = headingMatch[1].trim();
        activeBody = [];
        return;
      }
      if (!activeHeading) return;
      activeBody.push(line);
    });
    pushSection();

    if (sections.length === 0 && String(markdownText || '').trim()) {
      sections.push({
        key: 'clarify_concept',
        label: 'Clarify concept',
        content: String(markdownText || '').trim(),
        sendable: true,
      });
    }

    const hasCommonMistakes = sections.some((item) => item.key === 'misconception_fix');
    if (!hasCommonMistakes && mistakes.length > 0) {
      sections.push({
        key: 'misconception_fix',
        label: 'Misconception fix',
        content: `## Common confusion\n${mistakes.map((item) => `- Confusion: ${item}`).join('\n')}`.trim(),
        sendable: true,
      });
    }
    return sections;
  }, []);

  const walkthroughSections = useMemo(
    () => parseWalkthroughSections(aiStepByStep, aiCommonMistakes),
    [aiCommonMistakes, aiStepByStep, parseWalkthroughSections],
  );
  const sendableWalkthroughSections = useMemo(
    () => walkthroughSections.filter((section) => section.sendable !== false),
    [walkthroughSections],
  );

  const composeSnippetDraft = useCallback((keys: string[]) => {
    if (!keys.length || !sendableWalkthroughSections.length) return '';
    const selected = sendableWalkthroughSections.filter((section) => keys.includes(section.key));
    return selected.map((section) => section.content).join('\n\n').trim();
  }, [sendableWalkthroughSections]);

  useEffect(() => {
    if (!stepByStepModalOpen) return;
    const nextKeys = sendableWalkthroughSections.map((item) => item.key);
    setSnippetSectionKeys(nextKeys);
    setSnippetDraft(composeSnippetDraft(nextKeys));
    setSnippetSendError(null);
    setSnippetSendSuccess(null);
  }, [composeSnippetDraft, sendableWalkthroughSections, stepByStepModalOpen]);

  const toggleSnippetSection = (key: string) => {
    setSnippetSendError(null);
    setSnippetSendSuccess(null);
    setSnippetSectionKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key];
      setSnippetDraft(composeSnippetDraft(next));
      return next;
    });
  };

  const handleSendSelectedSnippet = async () => {
    if (!currentStepId) return;
    const content = String(snippetDraft || '').trim();
    if (!content) {
      setSnippetSendError('Select at least one section or add snippet text to use.');
      return;
    }
    const validationIssues = validateExplainBroadcastContent({
      content,
      stepPromptMd: currentStepPromptMd,
    });
    if (validationIssues.length > 0) {
      setSnippetSendError(validationIssues[0]);
      return;
    }
    if (classDraft.trim() && classDraft.trim() !== content) {
      const confirmed = window.confirm('Replace the current class draft with this selected snippet?');
      if (!confirmed) return;
    }
    try {
      setSnippetUseLoading(true);
      setSnippetSendError(null);
      setSnippetSendSuccess(null);
      setClassDraft(content);
      setClassAiModalOpen(true);
      setClassAiTab('draft');
      setClassAiError(null);
      setClassAiSuccess(null);
      setSnippetSendSuccess('Selected snippet added to the class draft.');
    } catch (err: any) {
      setSnippetSendError(err?.response?.data?.error || 'Failed to use selected snippet.');
    } finally {
      setSnippetUseLoading(false);
    }
  };

  const handleCopyAnswer = async () => {
    if (!currentStepTeacherAnswerKey) {
      setCopyStatus('empty');
      window.setTimeout(() => setCopyStatus('idle'), 1200);
      return;
    }

    const key = currentStepTeacherAnswerKey;
    let text = '';
    if (key.kind === 'text') {
      text = String(key.answer_code || '').trim();
    } else if (key.kind === 'mcq_single') {
      const idx = Number(key.correct_index);
      if (!Number.isInteger(idx) || idx < 0) {
        text = '';
      } else {
        const option = (key.options || [])[idx];
        text = option !== undefined ? String(option).trim() : `#${idx}`;
      }
    } else if (key.kind === 'mcq_multi') {
      text = (Array.isArray(key.correct_indices) ? key.correct_indices : [])
        .map((idx: any) => {
          const num = Number(idx);
          const option = (key.options || [])[num];
          return option !== undefined ? String(option).trim() : `#${num}`;
        })
        .join('\n');
    } else if (key.kind === 'matching') {
      text = Object.entries(key.pairs || {})
        .map(([left, right]) => `${left} -> ${String(right)}`)
        .join('\n');
    } else if (key.kind === 'ordering') {
      text = (Array.isArray(key.answer) ? key.answer : [])
        .map((item: any, idx: number) => `${idx + 1}) ${String(item)}`)
        .join('\n');
    } else {
      const answerText = Array.isArray(key.answer) ? key.answer.join(', ') : String(key.answer || '');
      text = `Answer: ${answerText}`;
    }

    if (!text) {
      setCopyStatus('empty');
      window.setTimeout(() => setCopyStatus('idle'), 1200);
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) {
          throw new Error('copy failed');
        }
      }
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
    window.setTimeout(() => setCopyStatus('idle'), 1200);
  };

  const handleClearExplain = async () => {
    if (!controlData) return;
    try {
      setClassDraftSending(true);
      setClassAiError(null);
      await lessonRunsApi.clearBroadcast(controlData.run.id);
      await loadControl();
      publishLessonRunSync(controlData.run.id, 'broadcast_changed');
      setClassAiModalOpen(false);
      setClassAiSuccess('Explanation cleared.');
    } catch (err: any) {
      setClassAiError(err?.response?.data?.error || 'Failed to clear explanation.');
    } finally {
      setClassDraftSending(false);
    }
  };

  useEffect(() => {
    if (!classAiModalOpen || classAiTab !== 'ask' || !currentStepId) return;
    void loadClassAskThread(currentStepId);
  }, [classAiModalOpen, classAiTab, currentStepId, loadClassAskThread]);

  useEffect(() => {
    if (!teacherAiModalOpen || teacherAiTab !== 'chat') return;
    void loadTeacherChatThread();
  }, [teacherAiModalOpen, teacherAiTab, loadTeacherChatThread]);

  useEffect(() => {
    if (!teacherAiModalOpen || teacherAiTab !== 'walkthrough' || !currentStepId) return;
    if (teacherWalkthroughMd.trim()) return;
    void handleGenerateTeacherWalkthrough();
  }, [
    currentStepId,
    handleGenerateTeacherWalkthrough,
    teacherAiModalOpen,
    teacherAiTab,
    teacherWalkthroughMd,
  ]);

  const updateAudience = async (mode: 'join' | 'leave', studentIds?: number[]) => {
    if (!controlData || !lessonOpsActions.runIdNumber) return;
    try {
      const idsToUse = studentIds || (mode === 'leave' ? controlData.joined_student_ids : selectedStudentIds);
      const response = await lessonOpsActions.updateAudience(idsToUse, mode);
      
      // Update local state with response
      setControlData(prev => {
        if (!prev) return null;
        const updatedStudents = prev.students.map(student => ({
          ...student,
          joined: response.data.joined_student_ids.includes(student.id)
        }));
        return {
          ...prev,
          students: updatedStudents,
          joined_student_ids: response.data.joined_student_ids,
          target_step_id: response.data.target_step_id
        };
      });
      
      setSelectedStudentIds([]);
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (err) {
      console.error('Failed to update audience:', err);
    }
  };

  const updateLiveAttendance = async (studentId: number, attendanceStatus: 'present' | 'absent') => {
    if (!controlData) return;
    setAttendanceSavingStudentIds((current) => (
      current.includes(studentId) ? current : [...current, studentId]
    ));
    setAttendanceError(null);
    try {
      await lessonRunsApi.updateAttendance(controlData.run.id, {
        student_id: studentId,
        status: attendanceStatus,
      });
      await Promise.all([
        loadControl({ silent: true }),
        loadLiveDashboard({ silent: true }),
        loadLiveSignalStats(),
        loadSubmissionsInbox({ silent: true }),
      ]);
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (err: any) {
      setAttendanceError(err?.response?.data?.error || 'Failed to update attendance.');
    } finally {
      setAttendanceSavingStudentIds((current) => current.filter((id) => id !== studentId));
    }
  };

  const toggleStudentSelection = (studentId: number) => {
    setSelectedStudentIds(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const replaceSelectedStudentIds = (studentIds: number[]) => {
    const nextIds = Array.from(new Set((studentIds || []).map((studentId) => Number(studentId)).filter(Number.isFinite)));
    setSelectedStudentIds(nextIds);
  };

  const teacherAnswerKeyPanel = currentStep ? (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-800">Answer Key</div>
          <p className="mt-1 text-sm text-amber-900">Teacher-only reference for this step. Use Teacher AI here if you want help turning it into instruction.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs text-amber-900 disabled:opacity-50"
            onClick={() => handleOpenTeacherAi('walkthrough')}
            disabled={teacherWalkthroughLoading}
          >
            {teacherWalkthroughLoading ? 'Opening...' : 'Teacher AI'}
          </button>
          <button
            type="button"
            className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs text-amber-900"
            onClick={() => handleOpenTeacherAi('chat')}
          >
            Ask a question
          </button>
          <button
            type="button"
            className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs text-amber-900 disabled:opacity-50"
            onClick={handleCopyAnswer}
          >
            Copy answer
          </button>
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-amber-200 bg-white/70 p-4 text-sm text-amber-950">
        {!currentStepTeacherAnswerKey ? (
          <p>No answer key available for this step.</p>
        ) : currentStepTeacherAnswerKey.kind === 'mcq_single' ? (
          <p>
            Correct option: {(currentStepTeacherAnswerKey.options || [])[Number(currentStepTeacherAnswerKey.correct_index)] ?? `#${currentStepTeacherAnswerKey.correct_index}`}
          </p>
        ) : currentStepTeacherAnswerKey.kind === 'mcq_multi' ? (
          <ul className="list-disc pl-5">
            {(currentStepTeacherAnswerKey.correct_indices || []).map((idx: number) => (
              <li key={`assist-key-${idx}`}>
                {(currentStepTeacherAnswerKey.options || [])[idx] ?? `#${idx}`}
              </li>
            ))}
          </ul>
        ) : currentStepTeacherAnswerKey.kind === 'text' ? (
          <pre className="overflow-x-auto rounded bg-slate-900 p-3 text-xs text-emerald-200">
            <code>{String(currentStepTeacherAnswerKey.answer_code || '')}</code>
          </pre>
        ) : currentStepTeacherAnswerKey.kind === 'matching' ? (
          <ul className="list-disc pl-5">
            {Object.entries(currentStepTeacherAnswerKey.pairs || {}).map(([left, right]) => (
              <li key={`assist-pair-${left}`}>{left} -&gt; {String(right)}</li>
            ))}
          </ul>
        ) : currentStepTeacherAnswerKey.kind === 'ordering' ? (
          <ol className="list-decimal pl-5">
            {(Array.isArray(currentStepTeacherAnswerKey.answer) ? currentStepTeacherAnswerKey.answer : []).map((item: any, idx: number) => (
              <li key={`assist-order-${idx}`}>{String(item)}</li>
            ))}
          </ol>
        ) : (
          <div className="space-y-2">
            <p>Answer: {Array.isArray(currentStepTeacherAnswerKey.answer) ? currentStepTeacherAnswerKey.answer.join(', ') : String(currentStepTeacherAnswerKey.answer || '')}</p>
            {currentStepTeacherAnswerKey.code ? (
              <pre className="overflow-x-auto rounded bg-slate-900 p-3 text-xs text-emerald-200">
                <code>{String(currentStepTeacherAnswerKey.code)}</code>
              </pre>
            ) : null}
            {currentStepTeacherAnswerKey.code_skeleton ? (
              <pre className="overflow-x-auto rounded bg-slate-900 p-3 text-xs text-emerald-200">
                <code>{String(currentStepTeacherAnswerKey.code_skeleton)}</code>
              </pre>
            ) : null}
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center gap-2">
        {copyStatus === 'copied' ? <span className="text-xs text-emerald-700">Copied!</span> : null}
        {copyStatus === 'failed' ? <span className="text-xs text-rose-700">Copy failed</span> : null}
        {copyStatus === 'empty' ? <span className="text-xs text-amber-700">No answer to copy.</span> : null}
      </div>
    </div>
  ) : null;

  const startNewRun = async () => {
    if (!lastRunContext) {
      // If no context available, go to dashboard
      navigate('/v2/teacher');
      return;
    }

    try {
      const response = await lessonRunsApi.start({
        lesson_plan: lastRunContext.lesson_plan,
        section: lastRunContext.section,
        is_test_mode: Boolean(lastRunContext.is_test_mode),
      });
      navigate(`/v2/teacher/run/${response.data.id}`);
    } catch (err) {
      console.error('Failed to start new run:', err);
      navigate('/v2/teacher');
    }
  };

  const adaptiveSummary = useMemo(() => {
    return adaptiveState?.divergence_summary ?? adaptiveState?.summary ?? null;
  }, [adaptiveState]);
  const submissionsNeedsActionCount = useMemo(
    () => countSubmissionNeedsAction(submissionsInboxView),
    [submissionsInboxView],
  );
  const workspaceTabs = useMemo<Array<TeacherWorkspaceTabDefinition<WorkspaceTab>>>(() => {
    const tabs: Array<TeacherWorkspaceTabDefinition<WorkspaceTab>> = [
      { key: 'step', label: 'Teaching' },
      { key: 'assist', label: 'Assist' },
      { key: 'signals', label: 'Signals', badge: Number(liveSignalStats?.step_signal_pending || 0) || null },
      { key: 'submissions', label: 'Submissions', badge: submissionsNeedsActionCount || null },
      { key: 'adaptive', label: 'Adaptive', badge: adaptiveSuggestions.length || null },
      { key: 'audience', label: 'Audience' },
    ];
    if (studioAvailable) {
      tabs.splice(2, 0, {
        key: 'studio',
        label: 'Studio',
        badge: Number(studioBoard?.summary?.help_requested || 0) || null,
      });
    }
    return tabs;
  }, [
    adaptiveSuggestions.length,
    liveSignalStats?.step_signal_pending,
    studioAvailable,
    studioBoard?.summary?.help_requested,
    submissionsNeedsActionCount,
  ]);
  const currentAssistLabel = useMemo(() => {
    const key = String(currentAssistActionType || '').trim().toLowerCase();
    if (!key) return 'Latest assist';
    const labels: Record<string, string> = {
      another_example: 'Another example',
      rephrase_grade_level: 'Rephrase for grade level',
      quick_check: 'Quick check',
      common_misconception: 'Common misconception',
      connect_to_apply: 'Connect to Apply',
      explain_step_by_step: 'Explain step-by-step',
      coding_step_by_step: 'Coding step-by-step',
      class_draft: 'Class draft',
      teacher_walkthrough: 'Teacher walkthrough',
    };
    return labels[key] || key.replace(/_/g, ' ');
  }, [currentAssistActionType]);
  const rankedAssistRows = useMemo(() => {
    return Array.isArray(assistRankings) ? assistRankings.slice(0, 5) : [];
  }, [assistRankings]);
  const selectedStudioStudent = useMemo(
    () => studioBoard?.students.find((student) => student.student_id === studioDrawerStudentId) || null,
    [studioBoard, studioDrawerStudentId],
  );
  const readinessSelectedSet = useMemo(() => new Set(readinessSelectedIds), [readinessSelectedIds]);
  const readinessRows = useMemo(() => (
    [...(readinessGate?.students || [])].sort((left, right) => {
      const leftNeedsAttention = left.needs_review || ['one_more_fix', 'build_with_me'].includes(String(left.route_key || ''));
      const rightNeedsAttention = right.needs_review || ['one_more_fix', 'build_with_me'].includes(String(right.route_key || ''));
      if (leftNeedsAttention !== rightNeedsAttention) return leftNeedsAttention ? -1 : 1;
      return String(left.student_name || '').localeCompare(String(right.student_name || ''));
    })
  ), [readinessGate?.students]);
  const readinessSelectableRows = readinessRows.filter((student) => student.is_on_gate_step);
  const renderAssistFeedbackControls = () => {
    if (!currentAssistFeedbackId) return null;
    return (
      <div className="mt-3 rounded border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-gray-600">
            Rate this assist: <span className="font-medium text-gray-800">{currentAssistLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`rounded border px-2 py-1 text-xs disabled:opacity-50 ${
                assistUsefulnessVote === 'up'
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 text-gray-700'
              }`}
              onClick={() => void submitAssistFeedback({ usefulness_vote: assistUsefulnessVote === 'up' ? null : 'up' })}
              disabled={assistFeedbackSaving}
            >
              Thumbs up
            </button>
            <button
              type="button"
              className={`rounded border px-2 py-1 text-xs disabled:opacity-50 ${
                assistUsefulnessVote === 'down'
                  ? 'border-rose-300 bg-rose-50 text-rose-700'
                  : 'border-gray-200 text-gray-700'
              }`}
              onClick={() => void submitAssistFeedback({ usefulness_vote: assistUsefulnessVote === 'down' ? null : 'down' })}
              disabled={assistFeedbackSaving}
            >
              Thumbs down
            </button>
            <label className="inline-flex items-center gap-1 text-xs text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={Boolean(assistUsedInClass)}
                onChange={(event) => void submitAssistFeedback({ used_in_class: event.target.checked })}
                disabled={assistFeedbackSaving}
              />
              Used in class
            </label>
          </div>
        </div>
        {assistFeedbackError ? (
          <div className="mt-2 text-xs text-rose-700">{assistFeedbackError}</div>
        ) : null}
      </div>
    );
  };

  if (loading && !controlData) {
    const loadingState = (
      <div className="max-w-6xl mx-auto p-6">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
    if (embeddedRunId || !redesignEnabled) return loadingState;
    return (
      <AppShell role="teacher" title="Lesson Control">
        {loadingState}
      </AppShell>
    );
  }

  if (error === 'not_found') {
    const notFoundContent = (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Lesson Run Not Available</h1>
          <p className="text-gray-600 mb-6">
            This lesson run is no longer available (it may have ended or you may not have access).
          </p>
          <div className="flex gap-3 justify-center">
            {lastRunContext && (
              redesignEnabled ? (
                <Button onClick={startNewRun}>Start Lesson Again</Button>
              ) : (
                <button 
                  className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  onClick={startNewRun}
                >
                  Start Lesson Again
                </button>
              )
            )}
            {redesignEnabled ? (
              <Button variant="secondary" onClick={() => navigate('/v2/teacher')}>
                Back to Teacher Dashboard
              </Button>
            ) : (
              <button 
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                onClick={() => navigate('/v2/teacher')}
              >
                Back to Teacher Dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    );
    if (embeddedRunId) return <p role="status">This activity has ended. Choose the next activity from the agenda.</p>;
    if (!redesignEnabled) return notFoundContent;
    return (
      <AppShell role="teacher" title="Lesson Control">
        {notFoundContent}
      </AppShell>
    );
  }

  if (error || !controlData) {
    const errorState = (
      <div className="max-w-6xl mx-auto p-6">
        <p className="text-red-500">{error || 'No data available'}</p>
      </div>
    );
    if (embeddedRunId || !redesignEnabled) return errorState;
    return (
      <AppShell role="teacher" title="Lesson Control">
        {errorState}
      </AppShell>
    );
  }

  const precisionPrompts = [
    'Can you be more specific?',
    'Why does that work?',
    'What evidence supports that?',
    'Can you say that as a complete idea?',
    'What would someone arguing the opposite say?',
  ];
  const rawScaffoldPrompts = currentStep?.content?.scaffold_prompts;
  const scaffoldPrompts = Array.isArray(rawScaffoldPrompts)
    ? rawScaffoldPrompts.map((item: any) => String(item || '').trim()).filter(Boolean)
    : [];
  const notYetHeardStudents = controlData.students.filter((student) => (
    student.participation_status !== 'participated'
    && student.participation_status !== 'absent'
  ));
  const sendUrgencyPrompt = async (prompt: string, kind: 'precision' | 'scaffold') => {
    if (!currentStepId || !prompt) return;
    setUrgencyPromptLoading(prompt);
    setUrgencyPromptNotice(null);
    try {
      await lessonRunsApi.setBroadcast(controlData.run.id, {
        kind: 'explain',
        step_id: Number(currentStepId),
        content_md: `## ${kind === 'precision' ? 'Think Deeper' : 'Next Hint'}\n${prompt}`,
      });
      await loadControl({ silent: true });
      publishLessonRunSync(controlData.run.id, 'broadcast_changed');
      setUrgencyPromptNotice(kind === 'precision' ? 'Follow-up displayed to the class.' : 'Next scaffold displayed to the class.');
    } catch (err: any) {
      setUrgencyPromptNotice(err?.response?.data?.error || 'Unable to display that prompt.');
    } finally {
      setUrgencyPromptLoading(null);
    }
  };
  const urgencyToolsPanel = currentStep && currentStep.runner_phase !== 'studio' ? (
    <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-700">Live questioning tools</p>
      <p className="mt-1 text-xs text-violet-900/80">Display a precision prompt without replacing the lesson step.</p>
      {notYetHeardStudents.length > 0 ? (
        <p className="mt-2 text-xs text-violet-900" title={notYetHeardStudents.map((student) => student.name).join(', ')}>
          Participation equity: {notYetHeardStudents.length} student{notYetHeardStudents.length === 1 ? '' : 's'} with no digital response yet this run
        </p>
      ) : (
        <p className="mt-2 text-xs text-emerald-700">Participation equity: every present student has responded during this run</p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {precisionPrompts.map((prompt) => (
          <button
            key={prompt}
            type="button"
            className="rounded-full border border-violet-200 bg-white px-3 py-1.5 text-xs font-medium text-violet-900 hover:bg-violet-100 disabled:opacity-50"
            onClick={() => void sendUrgencyPrompt(prompt, 'precision')}
            disabled={urgencyPromptLoading !== null}
          >
            {urgencyPromptLoading === prompt ? 'Displaying…' : prompt}
          </button>
        ))}
      </div>
      {scaffoldPrompts.length > 0 ? (
        <div className="mt-4 border-t border-violet-200 pt-3">
          <p className="text-xs font-semibold text-violet-900">Productive-struggle scaffold ladder</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {scaffoldPrompts.map((prompt: string, index: number) => (
              <button
                key={`${index}-${prompt}`}
                type="button"
                className="rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-950 hover:bg-amber-100 disabled:opacity-50"
                onClick={() => void sendUrgencyPrompt(prompt, 'scaffold')}
                disabled={urgencyPromptLoading !== null}
                title={prompt}
              >
                {urgencyPromptLoading === prompt ? 'Displaying…' : `Send hint ${index + 1}`}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {urgencyPromptNotice ? <p className="mt-2 text-xs text-violet-800">{urgencyPromptNotice}</p> : null}
    </div>
  ) : null;

  const workspaceStepPanel = (
    <div className="space-y-6">
      <details open={!embeddedRunId} className="rounded-lg border border-slate-200 bg-white"><summary className="cursor-pointer p-3 text-sm font-medium">Teaching tools · pacing, timer & prompts</summary>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wide text-slate-400">Pacing Mode</p>
          <div className="flex gap-2">
            <button
              className={`rounded px-3 py-1 text-sm ${
                controlData.run.pacing_mode === 'whole_class'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-200 text-slate-700'
              }`}
              onClick={() => updatePacing('whole_class')}
            >
              Whole Class
            </button>
            <button
              className={`rounded px-3 py-1 text-sm ${
                controlData.run.pacing_mode === 'selective'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-200 text-slate-700'
              }`}
              onClick={() => updatePacing('selective')}
            >
              Selective
            </button>
          </div>
        </div>
        {controlData.run.pacing_mode === 'selective' ? (
          <p className="mt-2 text-xs text-slate-500">Selective mode: advancing only pulled students</p>
        ) : null}
      </div>

      {controlData.run.status === 'active' && currentStep && currentStep.runner_phase !== 'studio' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <PacingTimerCard
              content={currentStep.content}
              startedAt={currentStepStartedAt}
              title={currentStep.title || `Step ${currentStep.order}`}
              runtimeState={currentStepTimer}
              showControls={Boolean(currentStepTimer)}
              actionLoading={pacingTimerLoading}
              onAction={(action, seconds) => void updatePacingTimer(action, seconds)}
            />
            {pacingTimerError ? <p className="mt-2 text-xs text-rose-700">{pacingTimerError}</p> : null}
        </div>
      ) : null}

      {urgencyToolsPanel}
      </details>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-xs uppercase tracking-wide text-slate-400">Current Step Preview</p>
        {currentStep ? (
          <div className="mt-4">
            <StepRenderer
              title={currentStep.title}
              stageTag={currentStep.stage_tag}
              stepKind={currentStep.step_kind}
              content={currentStep.content}
            />
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-500">Waiting for the next step.</p>
        )}
      </div>

      {currentStepTeacherNotes ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-400">Teacher Notes</p>
          <div className="mt-3 prose prose-sm max-w-none text-slate-800">
            <ReactMarkdown
              components={{
                code({ inline, children, ...props }: any) {
                  return !inline ? (
                    <pre className="overflow-x-auto rounded bg-slate-900 p-4 text-xs text-slate-100">
                      <code {...props}>{children}</code>
                    </pre>
                  ) : (
                    <code className="rounded bg-slate-100 px-1 py-0.5 text-[0.9em] text-slate-800">
                      {children}
                    </code>
                  );
                },
              }}
            >
              {currentStepTeacherNotes}
            </ReactMarkdown>
          </div>
        </div>
      ) : null}
    </div>
  );

  const workspaceAssistPanel = (
    <div className="space-y-6">
      {activeExplainBroadcast ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-slate-900">Live Explain (students see this)</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
                onClick={() => {
                  handleOpenExplainToClass({ draftText: String(activeExplainBroadcast?.content_md || '') });
                }}
              >
                Read/Edit
              </button>
              <button
                type="button"
                className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
                onClick={copyExplain}
              >
                {copiedExplain ? 'Copied ✓' : 'Copy'}
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">Preview only. Use Read/Edit for the full class draft workspace.</p>
          <div className="mt-2 max-h-40 overflow-auto rounded border border-slate-100 bg-slate-50 p-2">
            <div className="whitespace-pre-wrap text-sm text-slate-700">{String(activeExplainBroadcast?.content_md || '')}</div>
          </div>
        </div>
      ) : null}

      {teacherAnswerKeyPanel}

      <OpsAssistPanel
        aiAssistLoading={aiAssistLoading}
        aiAssistError={aiAssistError}
        aiSuggestion={aiSuggestion}
        aiDelayRevealTriggered={aiDelayRevealTriggered}
        aiDelayRevealReason={aiDelayRevealReason}
        assistRankingsLoading={assistRankingsLoading}
        rankedAssistRows={rankedAssistRows}
        currentStepId={currentStepId}
        onMicroAssist={(actionType) => {
          void handleMicroAssist(actionType);
        }}
        onOpenExplainToClass={() => handleOpenExplainToClass({ tab: 'draft' })}
        onOpenTeacherAi={() => handleOpenTeacherAi('walkthrough')}
        onCodingStepByStep={() => {
          void handleCodingStepByStep();
        }}
        renderAssistFeedbackControls={renderAssistFeedbackControls}
      />
    </div>
  );

  const workspaceStudioPanel = (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Independent Work</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-900">
              {controlData.run.status !== 'active' ? 'Independent work record' : controlData.run.delivery_phase === 'studio' ? 'Studio board is live' : 'Studio boundary is authored'}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {controlData.run.status !== 'active'
                ? 'This session is not live. The board shows recorded independent work.'
                : controlData.run.delivery_phase === 'studio'
                ? 'The full section continues independent work by default. Focus first on students who request help, need review, or require a different route.'
                : 'When the live portion is done, start Independent Work to unlock the Studio tail and shift teacher attention to the board below.'}
            </p>
            {studioNotice ? (
              <p className="mt-2 text-sm text-emerald-700">{studioNotice}</p>
            ) : null}
          </div>
          <div className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">
            {controlData.run.status !== 'active'
              ? (controlData.run.status === 'completed' ? 'Completed session' : 'Paused session')
              : controlData.run.delivery_phase === 'studio'
              ? 'Independent Work is active.'
              : studioAvailable
                ? 'Use the primary Teach action to start Independent Work.'
                : 'This lesson does not include Studio steps.'}
          </div>
        </div>
        {controlData.run.status === 'active' && controlData.run.delivery_phase === 'studio' ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
              <span className="font-semibold">Targeted live instruction:</span>{' '}
              {controlData.run.pacing_mode === 'selective' ? 'Selective' : 'Whole Class'}
              {' '}({controlData.run.pacing_mode === 'selective' ? controlData.joined_student_ids.length : controlData.students.length} targeted)
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span className="font-semibold">Studio independent work:</span> Full section ({controlData.students.length} students)
            </div>
          </div>
        ) : null}
      </div>

      {studioAvailable ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Student Routing &amp; Support</p>
              <h3 className="mt-1 text-lg font-semibold text-slate-900">
                {controlData.run.status === 'active' ? 'Students needing a next move' : 'Recorded student routing'}
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                {controlData.run.status === 'active'
                  ? 'Students continue working in their assigned Studio path unless they need support or intervention. Review highlighted exceptions first.'
                  : 'Routing and responses recorded for this session; these are not current attention requests.'}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Keep move-forward, Support First, Teacher Table, and return-to-instruction choices available for selected students only.
              </p>
              {readinessError ? (
                <p className="mt-2 text-sm text-rose-700">{readinessError}</p>
              ) : null}
            </div>
            <div className="grid gap-2 text-xs text-slate-600 sm:grid-cols-5 lg:min-w-[520px]">
              {[
                ['Start Here', readinessGate?.summary?.pending || 0],
                ['Ready for Studio', readinessGate?.summary?.go_test || 0],
                ['Support First', readinessGate?.summary?.one_more_fix || 0],
                ['Teacher Table', readinessGate?.summary?.build_with_me || 0],
                ['Review', readinessGate?.summary?.needs_review || 0],
              ].map(([label, value]) => (
                <div key={`readiness-summary-${String(label)}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="font-medium text-slate-500">{label}</div>
                  <div className="mt-1 text-xl font-semibold text-slate-900">{Number(value)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 grid gap-3 xl:grid-cols-[1fr_1fr_1fr_auto]">
            <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Ready for Studio target
              <select
                value={readinessGoTestStepId || ''}
                onChange={(event) => setReadinessGoTestStepId(Number(event.target.value) || null)}
                className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm normal-case tracking-normal text-slate-800"
              >
                {studioSteps.map((step) => (
                  <option key={`go-test-target-${step.id}`} value={step.id}>{studioTargetOptionLabel(step)}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Support First target
              <select
                value={readinessFixStepId || ''}
                onChange={(event) => setReadinessFixStepId(Number(event.target.value) || null)}
                className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm normal-case tracking-normal text-slate-800"
              >
                {studioSteps.map((step) => (
                  <option key={`fix-target-${step.id}`} value={step.id}>{studioTargetOptionLabel(step)}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Teacher Table display
              <select
                value={currentStepId || ''}
                onChange={(event) => {
                  const targetStepId = Number(event.target.value);
                  const targetStep = controlData?.steps.find((step) => step.id === targetStepId);
                  if (targetStep) void jumpToStep(targetStep);
                }}
                disabled={stepNavLoadingId !== null}
                className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm normal-case tracking-normal text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {controlData.steps.map((step) => (
                  <option key={`build-with-me-display-${step.id}`} value={step.id}>
                    {step.title || `Step ${step.order + 1}`}
                    {step.runner_phase === 'studio' ? ' · Studio' : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap items-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  const ids = readinessSelectableRows.map((student) => student.student_id);
                  setReadinessSelectedIds(ids);
                }}
                disabled={readinessSelectableRows.length === 0}
              >
                Select students needing a next move
              </Button>
              <Button variant="secondary" onClick={() => { void loadReadinessGate(); }} disabled={readinessLoading}>
                {readinessLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <div className="mr-2 flex min-h-[36px] items-center text-xs text-slate-500">
              {readinessSelectedIds.length} selected for routing or support
            </div>
            <Button
              variant="secondary"
              onClick={() => { void routeReadinessGate('go_test'); }}
              disabled={readinessRouting !== null || readinessSelectedIds.length === 0}
            >
              {readinessRouting === 'go_test' ? 'Routing…' : 'Ready for Studio'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => { void routeReadinessGate('one_more_fix'); }}
              disabled={readinessRouting !== null || readinessSelectedIds.length === 0}
            >
              {readinessRouting === 'one_more_fix' ? 'Routing…' : 'Support First'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => { void routeReadinessGate('build_with_me'); }}
              disabled={readinessRouting !== null || readinessSelectedIds.length === 0}
            >
              {readinessRouting === 'build_with_me' ? 'Routing…' : 'Teacher Table'}
            </Button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['', 'Student', 'Response', 'Route', 'Routing Status'].map((label) => (
                    <th key={`readiness-header-${label || 'select'}`} className="px-3 py-2 text-left font-medium text-slate-500">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {readinessRows.map((student) => {
                  const responseValue = typeof student.response?.value !== 'undefined'
                    ? String(student.response?.value)
                    : (student.response ? JSON.stringify(student.response) : 'No response');
                  return (
                    <tr key={`readiness-student-${student.student_id}`} className={student.needs_review ? 'bg-amber-50' : ''}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={readinessSelectedSet.has(student.student_id)}
                          aria-label={`Select ${student.student_name} for readiness routing`}
                          disabled={!student.is_on_gate_step || controlData.run.status !== 'active'}
                          onChange={(event) => {
                            setReadinessSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (event.target.checked) next.add(student.student_id);
                              else next.delete(student.student_id);
                              return Array.from(next).sort((a, b) => a - b);
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-900">{student.student_name}</td>
                      <td className="px-3 py-2 text-slate-700">{responseValue}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {studioReleaseRouteLabel(student.route_key, student.route_label)}
                        {student.needs_review ? <span className="ml-2 text-xs font-medium text-amber-700">new response</span> : null}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{student.is_on_gate_step ? 'Ready to route' : 'Already moved'}</td>
                    </tr>
                  );
                })}
                {readinessRows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-4 text-sm text-slate-500" colSpan={5}>No active students found.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {studioAvailable ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-400">Studio Summary</p>
          <div className="mt-4 grid gap-3 md:grid-cols-3 xl:grid-cols-7">
            {[
              ['Help requested', studioBoard?.summary?.help_requested || 0],
              ['Drafting', studioBoard?.summary?.drafting || 0],
              ['Draft stale', studioBoard?.summary?.draft_stale || 0],
              ['Current step submitted', studioBoard?.summary?.current_step_submitted || 0],
              ['Independent work complete', studioBoard?.summary?.independent_work_complete || 0],
              ['Artifact still needed', studioBoard?.summary?.artifact_still_needed || 0],
              ['All lesson work submitted', studioBoard?.summary?.all_lesson_work_submitted || 0],
            ].map(([label, value]) => (
              <div key={`studio-summary-${String(label)}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{Number(value)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {studioAvailable ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Studio Roster</p>
              <p className="mt-1 text-sm text-slate-600">Sorted by help requests and likely intervention needs.</p>
            </div>
            <Button variant="secondary" onClick={() => { void loadStudioBoard(); }} disabled={studioLoading}>
              {studioLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['Student', 'Route', 'Step', 'Status', 'Last activity', 'Help', 'Quality', 'Completion'].map((label) => (
                    <th key={`studio-header-${label}`} className="px-3 py-2 text-left font-medium text-slate-500">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(studioBoard?.students || []).map((student) => (
                  <tr
                    key={`studio-student-${student.student_id}`}
                    className={`cursor-pointer ${studioDrawerStudentId === student.student_id ? 'bg-sky-50' : 'hover:bg-slate-50'}`}
                    onClick={() => setStudioDrawerStudentId((prev) => (prev === student.student_id ? null : student.student_id))}
                  >
                    <td className="px-3 py-2 font-medium text-slate-900">{student.student_name}</td>
                    <td className="px-3 py-2 text-slate-700">{studioReleaseRouteLabel(student.readiness_route_key, student.readiness_route_label)}</td>
                    <td className="px-3 py-2 text-slate-700">{student.current_step_title}</td>
                    <td className="px-3 py-2 text-slate-700">{student.step_status_label}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {student.minutes_on_step !== null && student.minutes_on_step !== undefined
                        ? `${student.minutes_on_step}m`
                        : 'No activity'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {student.help_status && student.help_status !== 'none'
                        ? String(student.help_kind || student.help_status).replace(/_/g, ' ')
                        : 'None'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{student.latest_quality_status || 'None'}</td>
                    <td className="px-3 py-2 text-slate-700">{student.completion_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {selectedStudioStudent ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Student Detail</p>
              <h3 className="mt-1 text-lg font-semibold text-slate-900">{selectedStudioStudent.student_name}</h3>
              <p className="mt-1 text-sm text-slate-600">{selectedStudioStudent.current_step_title}</p>
            </div>
            <div className="flex gap-2">
              {selectedStudioStudent.help_status === 'requested' ? (
                <Button
                  variant="secondary"
                  onClick={() => { void acknowledgeStudioHelp(selectedStudioStudent.current_progress_id); }}
                  disabled={studioActionLoading !== null}
                >
                  Acknowledge help
                </Button>
              ) : null}
              {selectedStudioStudent.help_status && selectedStudioStudent.help_status !== 'none' && selectedStudioStudent.help_status !== 'resolved' ? (
                <Button
                  variant="secondary"
                  onClick={() => { void resolveStudioHelp(selectedStudioStudent.current_progress_id); }}
                  disabled={studioActionLoading !== null}
                >
                  Resolve help
                </Button>
              ) : null}
            </div>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Latest Draft Preview</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
                {selectedStudioStudent.latest_draft_preview || 'No meaningful draft snapshot yet.'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Latest Submitted Response</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
                {selectedStudioStudent.latest_response_preview || 'No submitted response yet.'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Help Note</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
                {selectedStudioStudent.help_note || 'No help note.'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Decision Support</p>
              <p className="mt-2 text-sm text-slate-800">
                Group: {selectedStudioStudent.group ? selectedStudioStudent.group.replace(/_/g, ' ') : 'None'}
              </p>
              <p className="mt-2 text-sm text-slate-700">
                Reasons: {(selectedStudioStudent.group_reasons || []).join(', ') || 'None'}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {studioAvailable ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-400">Step Summaries</p>
          <div className="mt-4 space-y-4">
            {(studioBoard?.step_summaries || []).map((summary) => (
              <div key={`studio-step-summary-${summary.step_id}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="font-medium text-slate-900">{summary.step_title}</div>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                    {Object.entries(summary.counts_by_progress_state || {}).map(([key, value]) => (
                      <span key={`studio-step-count-${summary.step_id}-${key}`} className="rounded bg-white px-2 py-1">
                        {key.replace(/_/g, ' ')}: {value}
                      </span>
                    ))}
                  </div>
                </div>
                {summary.group_buckets.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {summary.group_buckets.map((group) => (
                      <button
                        key={`studio-group-${summary.step_id}-${group.group}`}
                        type="button"
                        className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-100"
                        onClick={() => {
                          const firstStudentId = group.student_ids[0];
                          if (firstStudentId) setStudioDrawerStudentId(firstStudentId);
                        }}
                      >
                        {group.group.replace(/_/g, ' ')} ({group.count})
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );

  const workspaceMainPanel = (() => {
    if (embeddedRunId) return workspaceStepPanel;
    if (activeWorkspaceTab === 'assist') return workspaceAssistPanel;
    if (activeWorkspaceTab === 'studio') return workspaceStudioPanel;
    if (activeWorkspaceTab === 'signals') {
      return (
        <TeacherSignalsPanel
          currentStepTitle={currentStep?.title || 'Waiting for the next step'}
          liveSignalStats={liveSignalStats}
          activeBucket={activeBucket}
          bucketNames={bucketNames}
          hasAnswerKey={hasAnswerKey}
          blanksTotal={blanksTotal}
          blanksKeyed={blanksKeyed}
          currentStepHasSignal={currentStepHasSignal}
          currentStepRevealMode={String(currentStepRevealMode || 'auto')}
          revealSubmitting={revealSubmitting}
          revealSuccess={revealSuccess}
          revealError={revealError}
          onSelectBucket={setActiveBucket}
          onRevealAnswers={handleRevealAnswers}
        />
      );
    }
    if (activeWorkspaceTab === 'submissions') {
      return (
        <TeacherSubmissionsQueuePanel
          submissionsInbox={submissionsInboxView}
          submissionsError={submissionsError}
          submissionsLoading={submissionsLoading}
          hideAbsentStudents={hideAbsentInSubmissions}
          hiddenAbsentCount={hiddenAbsentSubmissionCount}
          expandedSubmissionStudentIds={expandedSubmissionStudentIds}
          redoRequested={redoRequested}
          currentStepId={currentStepId}
          isFocusViewport={isFocusViewport}
          onRefresh={() => {
            void loadSubmissionsInbox();
          }}
          onToggleHideAbsentStudents={(nextValue) => {
            setHideAbsentInSubmissions(nextValue);
          }}
          onApproveEvidence={approveEvidence}
          onGateDecision={decideReflectionGate}
          onQualityDecision={decideSubmissionQuality}
          onRequestRedo={requestRedo}
          onToggleExpanded={(studentId) => {
            setExpandedSubmissionStudentIds((prev) => {
              const next = new Set(prev);
              if (next.has(studentId)) {
                next.delete(studentId);
              } else {
                next.add(studentId);
              }
              return next;
            });
          }}
          formatSignalResponse={formatSignalResponse}
        />
      );
    }
    if (activeWorkspaceTab === 'adaptive') {
      return (
        <OpsAdaptivePanel
          showReviewed={showReviewed}
          adaptiveLoading={adaptiveLoading}
          adaptiveError={adaptiveError}
          adaptiveSummary={adaptiveSummary}
          adaptiveSuggestions={adaptiveSuggestions}
          expandedAdaptiveTargets={expandedAdaptiveTargets}
          rosterNameById={rosterNameById}
          selectedStudentIds={selectedStudentIds}
          routeablePathwaySteps={routeablePathwaySteps}
          lastAdaptiveApplication={lastAdaptiveApplication}
          computeMinutesUntil={computeMinutesUntil}
          onRouteStudentsToStep={handleRouteStudentsToStep}
          onToggleShowReviewed={(nextValue) => setShowReviewed(nextValue)}
          onGenerateAdaptive={() => {
            void handleGenerateAdaptive();
          }}
          onClearOverridesToClassStep={() => {
            void handleClearOverridesToClassStep();
          }}
          onToggleAdaptiveTargets={(suggestionId) => {
            setExpandedAdaptiveTargets((prev) => {
              const next = new Set(prev);
              if (next.has(suggestionId)) {
                next.delete(suggestionId);
              } else {
                next.add(suggestionId);
              }
              return next;
            });
          }}
          onApproveApplySuggestion={(suggestionId, apply) => {
            void handleApproveApplySuggestion(suggestionId, apply);
          }}
          onRejectSuggestion={(suggestionId) => {
            void handleRejectSuggestion(suggestionId);
          }}
          onApplyApprovedSuggestion={(suggestionId) => {
            void handleApplyApprovedSuggestion(suggestionId);
          }}
        />
      );
    }
    if (activeWorkspaceTab === 'audience') {
      return (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <OpsAudiencePanel
            joinedStudentIds={controlData.joined_student_ids}
            enteredLiveStudentIds={controlData.entered_live_student_ids || []}
            livePresenceSummary={controlData.live_presence_summary || null}
            students={controlData.students}
            selectedStudentIds={selectedStudentIds}
            attendanceSavingStudentIds={attendanceSavingStudentIds}
            attendanceError={attendanceError}
            onSetAttendance={(studentId, attendanceStatus) => {
              void updateLiveAttendance(studentId, attendanceStatus);
            }}
            onToggleStudentSelection={toggleStudentSelection}
            onSelectStudentIds={replaceSelectedStudentIds}
            onPullSelected={() => {
              void updateAudience('join');
            }}
            onReleaseSelected={() => {
              void updateAudience('leave');
            }}
            onPullStudentIds={(studentIds) => {
              void updateAudience('join', studentIds);
            }}
            onReleaseStudentIds={(studentIds) => {
              void updateAudience('leave', studentIds);
            }}
            onReleaseAll={() => {
              void updateAudience('leave');
            }}
          />
        </div>
      );
    }
    return workspaceStepPanel;
  })();

  const displayedPresentation = ((controlData.run.live_resource as Record<string, any>) || {}).presentation;
  const presentationIsOnDisplay = Boolean(
    presentationResource
    && displayedPresentation
    && displayedPresentation.src === presentationResource.src
    && displayedPresentation.display_active,
  );
  const presentationIsOnStudents = Boolean(
    presentationResource
    && displayedPresentation
    && displayedPresentation.src === presentationResource.src
    && displayedPresentation.student_active,
  );
  const movePresentation = (nextIndex: number) => {
    setPresentationSlideIndex(nextIndex);
    if (presentationIsOnDisplay || presentationIsOnStudents) void updatePresentationDisplay(true, nextIndex, 'sync');
  };

  const supportGroupPanel = location.state?.supportGroup && (() => {
    const group = location.state.supportGroup;
    const validSection = Number(group.sectionId) === Number(controlData.run.section);
    const requestedIds = Array.isArray(group.studentIds) ? group.studentIds : [];
    const students = validSection ? controlData.students.filter(student => requestedIds.includes(student.id)) : [];
    return <section aria-label="Suggested instruction group" className="mb-3 shrink-0 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
      <h2 className="font-semibold">Review suggested audience: {typeof group.label === 'string' ? group.label : 'Support group'}</h2>
      <p className="mt-1 text-sm">{students.map(student => student.name).join(', ') || 'No matching students in this run.'}</p>
      <p className="mt-1 text-sm">Only students in this run can be selected. Confirm pacing and the teaching step in the audience controls before pulling students; no one has been moved by this handoff.</p>
      <button className="mt-2 rounded border border-indigo-300 bg-white px-3 py-2 text-sm disabled:opacity-50" disabled={controlData.run.status !== 'active' || students.length === 0} onClick={() => setSelectedStudentIds(students.map(student => student.id))}>Use this selection in audience controls</button>
      {controlData.run.status !== 'active' && <p className="text-sm">This run is no longer active. Return to Teach to choose the current lesson.</p>}
    </section>;
  })();
  const controlSurface = (
    <div className={embeddedRunId ? 'min-w-0' : workspaceModeEnabled
      ? 'h-[calc(100vh-3.75rem)] overflow-hidden'
      : redesignEnabled ? 'h-[calc(100vh-10rem)] overflow-hidden' : 'h-screen overflow-hidden'}>
      <div className={embeddedRunId ? 'flex min-w-0 flex-col' : workspaceModeEnabled
        ? 'mx-auto flex h-full max-w-[1600px] flex-col overflow-hidden p-3 sm:p-4'
        : redesignEnabled ? 'max-w-6xl mx-auto flex h-full flex-col overflow-hidden p-4 sm:p-6' : 'max-w-6xl mx-auto flex h-full flex-col overflow-hidden p-6'}>
      <Dialog open={inspectedStudentId !== null} onClose={() => setInspectedStudentId(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/20" aria-hidden="true" />
        <div className="fixed inset-y-0 right-0 w-full max-w-2xl overflow-y-auto bg-white p-5 shadow-xl"><Dialog.Panel>
          <div className="mb-4 flex items-center justify-between gap-3"><Dialog.Title className="text-xl font-semibold">Student work · {controlData.students.find(s => s.id === inspectedStudentId)?.name}</Dialog.Title><button className="rounded border px-3 py-2" onClick={() => setInspectedStudentId(null)}>Close student work</button></div>
        <TeacherSubmissionsQueuePanel
          initialFilter="all" studentOnly
          submissionsInbox={submissionsInbox ? {...submissionsInbox, submissions: submissionsInbox.submissions.filter(row => row.student_id === inspectedStudentId)} : null}
          submissionsError={submissionsError}
          submissionsLoading={submissionsLoading}
          hideAbsentStudents={false}
          hiddenAbsentCount={0}
          expandedSubmissionStudentIds={expandedSubmissionStudentIds}
          redoRequested={redoRequested}
          currentStepId={currentStepId}
          isFocusViewport={isFocusViewport}
          onRefresh={() => {
            void loadSubmissionsInbox();
          }}
          onToggleHideAbsentStudents={(nextValue) => {
            setHideAbsentInSubmissions(nextValue);
          }}
          onApproveEvidence={approveEvidence}
          onGateDecision={decideReflectionGate}
          onQualityDecision={decideSubmissionQuality}
          onRequestRedo={requestRedo}
          onToggleExpanded={(studentId) => {
            setExpandedSubmissionStudentIds((prev) => {
              const next = new Set(prev);
              if (next.has(studentId)) {
                next.delete(studentId);
              } else {
                next.add(studentId);
              }
              return next;
            });
          }}
          formatSignalResponse={formatSignalResponse}
        />
        </Dialog.Panel></div>
      </Dialog>
      {supportGroupPanel}
      {isOpsSurface ? (
        <LessonOpsHeader
          currentStepLabel={currentStepDisplayLabel}
          currentStepTitle={currentStep?.title || 'Waiting for the next step'}
          onBackToTeach={openTeachSameTab}
        />
      ) : (
        <LessonTeachHeader
          isOpsSurface={isOpsSurface}
          isFocusViewport={isFocusViewport}
          lessonControlLayoutV2={layoutV2Enabled}
          embedded={Boolean(embeddedRunId)}
          advanceLabel={onNextActivity && controlData.steps.findIndex(s => s.id === currentStepId) === controlData.steps.length - 1 ? nextActivityLabel : undefined}
          workspaceModeEnabled={workspaceModeEnabled}
          isTestMode={Boolean(controlData.run.is_test_mode)}
          trackAssignment={trackAssignment}
          testModeSaving={testModeSaving}
          trackLoading={trackLoading}
          showInlineOpsPanel={showInlineOpsPanel}
          testModeError={testModeError}
          trackError={trackError}
          copiedLink={copiedLink}
          currentStepId={currentStepId}
          lessonTitle={controlData.run.lesson_plan_title || trackAssignmentInfo?.lesson_plan_title || `Lesson #${controlData.run.lesson_plan}`}
          sectionName={controlData.run.section_name || trackAssignmentInfo?.section_name || `Section #${controlData.run.section}`}
          sectionId={controlData.run.section}
          currentStage={currentStep?.stage_tag || ''}
          currentStepLabel={currentStepDisplayLabel}
          currentStepTitle={currentStep?.title || 'Waiting for the next step'}
          pacingMode={controlData.run.pacing_mode}
          targetedCount={controlData.run.pacing_mode === 'selective'
            ? controlData.joined_student_ids.length
            : controlData.students.length}
          joinedCount={controlData.entered_live_student_ids?.length || 0}
          sectionStudentCount={controlData.students.length}
          runStatus={controlData.run.status}
          explainActive={Boolean(activeExplainBroadcast)}
          explainLoading={classDraftSending}
          hasPreviousStep={Boolean(previousStep)}
          stepNavLoadingId={stepNavLoadingId}
          endRunSubmitting={endRunSubmitting || Boolean(transitionBusy)}
          studioAvailable={studioAvailable}
          canEnterStudio={canEnterStudio}
          deliveryPhase={controlData.run.delivery_phase || 'live'}
          studioActionLoading={studioActionLoading === 'enter'}
          alertCount={criticalAlerts.length}
          alertLabel={compactAlertLabel}
          responseProgressLabel={liveSignalStats && liveSignalStats.total_students > 0
            ? `${liveSignalStats.step_signal_submitted} of ${liveSignalStats.total_students} responded · ${liveSignalStats.step_signal_pending} still thinking`
            : ''}
          materialsCount={controlData.resources?.length || 0}
          onToggleTestMode={handleTestModeToggle}
          onToggleTrackAssignment={handleTrackAssignment}
          onOpenRunSettings={() => openRunSettings()}
          onOpenStepJump={() => setStepJumpOpen(true)}
          onOpenOpsInNewTab={openOpsInNewTab}
          onToggleInlineOpsPanel={() => setShowInlineOpsPanel((prev) => !prev)}
          onOpenOpsSameTab={openOpsSameTab}
          onOpenMobileOpsDrawer={() => setMobileOpsDrawerOpen(true)}
          onOpenTeachSameTab={openTeachSameTab}
          onOpenDisplay={openDisplay}
          onOpenStudentPreview={openStudentPreview}
          onOpenMaterials={() => setMaterialsOpen(true)}
          onCopyStudentLink={copyStudentLink}
          onOpenExplain={() => handleOpenExplainToClass({ tab: 'draft' })}
          onClearExplain={handleClearExplain}
          onGoPreviousStep={goPreviousStep}
          onAdvanceStep={advance}
          onEndRun={endRun}
          onEnterStudio={() => { void enterStudio(); }}
          onAlertClick={() => focusDashboardForAlert(primaryCriticalAlert?.id)}
          onAlertPanelClick={primaryCriticalAlert ? () => openOpsForAlert(primaryCriticalAlert.id) : undefined}
        />
      )}
      <LessonCompletionPanel key={controlData.run.id} runId={controlData.run.id} />
      {!isOpsSurface && presentationResource?.selected_slides?.length ? (
        <section aria-label="Presentation controls" className="mt-3 shrink-0 rounded-xl border border-violet-200 bg-violet-50 p-3">
          <div className="grid gap-3 lg:grid-cols-[minmax(240px,420px)_1fr] lg:items-center">
            <div className="overflow-hidden rounded-lg border border-violet-200 bg-slate-950">
              <img
                src={presentationSlideImageUrl(presentationResource, presentationResource.selected_slides[presentationSlideIndex])}
                alt={`${presentationResource.title}, slide ${presentationResource.selected_slides[presentationSlideIndex].slide_number}`}
                className="mx-auto max-h-56 w-full object-contain"
              />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-700">Presentation</p>
              <h2 className="mt-1 font-semibold text-slate-950">{presentationResource.title}</h2>
              <p className="mt-1 text-sm text-slate-600">
                Selected slide {presentationSlideIndex + 1} of {presentationResource.selected_slides.length} · original slide {presentationResource.selected_slides[presentationSlideIndex].slide_number}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className="rounded border border-violet-300 bg-white px-3 py-2 text-sm disabled:opacity-40" disabled={presentationSlideIndex === 0} onClick={() => movePresentation(presentationSlideIndex - 1)}>Previous</button>
                <button type="button" className="rounded border border-violet-300 bg-white px-3 py-2 text-sm disabled:opacity-40" disabled={presentationSlideIndex === presentationResource.selected_slides.length - 1} onClick={() => movePresentation(presentationSlideIndex + 1)}>Next</button>
                <button type="button" className={`rounded px-3 py-2 text-sm font-semibold text-white ${presentationIsOnDisplay ? 'bg-rose-600' : 'bg-violet-600'}`} disabled={presentationDisplayLoading} onClick={() => void updatePresentationDisplay(!presentationIsOnDisplay)}>
                  {presentationDisplayLoading ? 'Updating…' : presentationIsOnDisplay ? 'Hide from Display' : 'Show on Display'}
                </button>
                <button type="button" className={`rounded px-3 py-2 text-sm font-semibold text-white ${presentationIsOnStudents ? 'bg-rose-600' : 'bg-sky-600'}`} disabled={presentationDisplayLoading} onClick={() => void updatePresentationDisplay(!presentationIsOnStudents, presentationSlideIndex, 'students')}>
                  {presentationDisplayLoading ? 'Updating…' : presentationIsOnStudents ? 'Hide from Students' : 'Show to Students'}
                </button>
                <button type="button" className="rounded px-3 py-2 text-sm text-slate-700 hover:bg-white" onClick={() => setPresentationResource(null)}>Hide in runner</button>
              </div>
              {presentationDisplayError ? <p role="alert" className="mt-2 text-sm text-rose-700">{presentationDisplayError}</p> : null}
            </div>
          </div>
        </section>
      ) : null}
      {!isOpsSurface && studioError ? (
        <div role="alert" className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <span className="font-semibold">Independent Work:</span> {studioError}
        </div>
      ) : null}
      {!workspaceModeEnabled && criticalAlerts.length > 0 && (
        <div className="mt-2 flex min-h-[32px] flex-wrap items-center gap-2 border-y border-slate-200 bg-white/80 px-2 py-1 text-xs">
          <span className="font-semibold uppercase tracking-[0.16em] text-slate-500">Alerts</span>
          {criticalAlerts.map((alert) => (
            <span
              key={`critical-alert-${alert.id}`}
              className={`inline-flex max-w-full items-center gap-2 rounded-full border px-2.5 py-1 ${
                alert.tone === 'rose'
                  ? 'border-rose-200 bg-rose-50 text-rose-700'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
              title={alert.message}
            >
              <button
                type="button"
                className="truncate hover:underline"
                onClick={() => focusDashboardForAlert(alert.id)}
              >
                {compactCriticalAlertMessage(alert.message)}
              </button>
              {alert.opsRelated && layoutV2Enabled && !isOpsSurface ? (
                <button
                  type="button"
                  className="shrink-0 rounded border border-current/20 bg-white/80 px-1.5 py-0.5 text-[11px] font-medium hover:bg-white"
                  onClick={() => openOpsForAlert(alert.id)}
                >
                  {workspaceModeEnabled ? 'View panel' : 'Open ops'}
                </button>
              ) : null}
            </span>
          ))}
        </div>
      )}
      {showCompactStepNav && currentStep && (
        <div className="mt-3 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          <span className="font-semibold">Step {currentStep.order} of {controlData.steps.length}</span>
          <span className="mx-2 text-sky-700">•</span>
          <span>{currentStep.title || 'Untitled step'}</span>
        </div>
      )}
      {!workspaceModeEnabled && classAiSuccess && (
        <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {classAiSuccess}
        </div>
      )}
      {!workspaceModeEnabled && !isOpsSurface && studioAvailable ? (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white px-2.5 py-1 font-semibold text-amber-900">
              {controlData.run.status !== 'active' ? 'Independent work record' : controlData.run.delivery_phase === 'studio'
                ? 'Studio Active'
                : canEnterStudio ? 'Studio Ready' : 'Independent Work'}
            </span>
            <span className="text-amber-900">
              {controlData.run.status !== 'active'
                ? (controlData.run.status === 'completed' ? 'Completed session' : 'Paused session')
                : controlData.run.delivery_phase === 'studio'
                ? 'Independent work is live.'
                : canEnterStudio
                  ? 'Start Independent Work to release students.'
                  : 'Available at the Studio boundary.'}
            </span>
          </div>
          {controlData.run.delivery_phase === 'studio' ? (
            <span className="inline-flex w-fit items-center rounded-full bg-white px-2.5 py-1 font-medium text-amber-900">
              {controlData.run.status === 'active' ? 'Active' : controlData.run.status}
            </span>
          ) : canEnterStudio ? (
            <button
              type="button"
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => { void enterStudio(); }}
              disabled={!canEnterStudio || studioActionLoading === 'enter'}
            >
              {studioActionLoading === 'enter' ? 'Starting…' : 'Start Independent Work'}
            </button>
          ) : null}
        </div>
      ) : null}
      {workspaceModeEnabled ? (
        <main
          data-testid="lesson-workspace-scroller"
          className={embeddedRunId ? "mt-3 min-w-0 space-y-3" : "mt-3 min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1"}
        >
          {classAiSuccess ? (
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{classAiSuccess}</div>
          ) : null}
          {!embeddedRunId && <HookProgressRail
            steps={controlData.steps}
            currentStepId={currentStepId}
            stepNavLoadingId={stepNavLoadingId}
            onSelectStep={(step) => { void jumpToStep(step); }}
            onOpenStepJump={() => setStepJumpOpen(true)}
          />}
          {!embeddedRunId && <TeacherLiveDashboardPanel
            compact={Boolean(embeddedRunId)}
            onInspectStudent={id => { setInspectedStudentId(id); setExpandedSubmissionStudentIds(new Set([id])); void loadSubmissionsInbox(); }}
            dashboard={liveDashboard}
            loading={liveDashboardLoading}
            error={liveDashboardError}
            selectedBucket={activeLiveDashboardBucket}
            onSelectBucket={setActiveLiveDashboardBucket}
          />}
          {!embeddedRunId && <TeacherWorkspaceTabs
            activeTab={activeWorkspaceTab}
            tabs={workspaceTabs}
            onChange={setActiveWorkspaceTab}
          />}
          <div className="rounded-xl bg-slate-50/60 pb-2">
            {workspaceMainPanel}
          </div>
        </main>
      ) : null}
      {!workspaceModeEnabled && !isOpsSurface ? (
        <div className="mt-2">
          <TeacherLiveDashboardPanel
            dashboard={liveDashboard}
            loading={liveDashboardLoading}
            error={liveDashboardError}
            selectedBucket={activeLiveDashboardBucket}
            onSelectBucket={setActiveLiveDashboardBucket}
          />
        </div>
      ) : null}
      {!workspaceModeEnabled && activeExplainBroadcast && (
        <div className="mt-3 rounded border border-gray-200 bg-white px-3 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-gray-900">Live Explain (students see this)</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded"
                onClick={() => {
                  handleOpenExplainToClass({ draftText: String(activeExplainBroadcast?.content_md || '') });
                }}
              >
                Read/Edit
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded"
                onClick={copyExplain}
              >
                {copiedExplain ? 'Copied ✓' : 'Copy'}
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">Preview only. Use Read/Edit for the full class draft workspace.</p>
          <div className="mt-2 max-h-40 overflow-auto rounded border border-gray-100 bg-gray-50 p-2">
            <div className="text-sm text-gray-700 whitespace-pre-wrap">{String(activeExplainBroadcast?.content_md || '')}</div>
          </div>
        </div>
      )}

      {showCompactLiveStrip && liveSignalStats && (
        <LiveHealthStrip liveSignalStats={liveSignalStats} />
      )}

      {!workspaceModeEnabled ? (
      <div className="mt-6 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto lg:overflow-hidden">
        <div className="flex w-full min-h-0 flex-col gap-6 lg:h-full lg:flex-row lg:overflow-hidden">
        {/* Step List */}
        {showStepListAside && (
        <aside className="bg-white rounded-lg border border-gray-200 p-4 lg:w-[320px] lg:shrink-0 lg:min-h-0 lg:overflow-y-auto">
          <p className="text-xs uppercase tracking-wide text-gray-400">Step List</p>
          <p className="mt-1 text-xs text-gray-500">Click any step to jump there.</p>
          <div className="mt-3 space-y-2">
            {controlData.steps.map((step) => (
              <button
                key={step.id}
                type="button"
                onClick={() => void jumpToStep(step)}
                disabled={currentStepId === step.id || stepNavLoadingId !== null}
                className={`w-full rounded border px-3 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-70 ${
                  currentStepId === step.id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Step {step.order}</p>
                  <p className="text-xs text-gray-400">
                    {stepNavLoadingId === step.id ? 'jumping...' : step.stage_tag || 'class'}
                  </p>
                </div>
                <p className="text-sm font-semibold text-gray-900 mt-1">{step.title || 'Untitled step'}</p>
                {step.step_kind && (
                  <p className="text-xs text-blue-600 mt-1">{step.step_kind}</p>
                )}
              </button>
            ))}
          </div>
        </aside>
        )}

        {/* Main Content */}
        <section className="min-w-0 space-y-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
          {/* Pacing Control */}
          {showTeachCards && (
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-gray-400">Pacing Mode</p>
              <div className="flex gap-2">
                <button
                  className={`px-3 py-1 text-sm rounded ${controlData.run.pacing_mode === 'whole_class' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  onClick={() => updatePacing('whole_class')}
                >
                  Whole Class
                </button>
                <button
                  className={`px-3 py-1 text-sm rounded ${controlData.run.pacing_mode === 'selective' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  onClick={() => updatePacing('selective')}
                >
                  Selective
                </button>
              </div>
            </div>
            {controlData.run.pacing_mode === 'selective' && (
              <p className="text-xs text-gray-500 mt-2">Selective mode: advancing only pulled students</p>
            )}
          </div>
          )}

          {showTeachCards && (
          <>
          {/* Current Step */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Current Step</p>
            <h2 className="text-xl font-semibold text-gray-900 mt-1">
              {currentStep?.title || 'Waiting for the next step'}
            </h2>
            {currentStep && (
              <div className="flex gap-2 mt-2">
                {currentStep.stage_tag && (
                  <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                    {currentStep.stage_tag}
                  </span>
                )}
                {currentStep.step_kind && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                    {currentStep.step_kind}
                  </span>
                )}
                {currentStep.counts_as_evidence && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                    Legacy evidence
                  </span>
                )}
              </div>
            )}
            {controlData.run.status === 'active' && currentStep && currentStep.runner_phase !== 'studio' ? (
              <div className="mt-4">
                <PacingTimerCard
                  content={currentStep.content}
                  startedAt={currentStepStartedAt}
                  title={currentStep.title || `Step ${currentStep.order}`}
                  runtimeState={currentStepTimer}
                  showControls={Boolean(currentStepTimer)}
                  actionLoading={pacingTimerLoading}
                  onAction={(action, seconds) => void updatePacingTimer(action, seconds)}
                />
                {pacingTimerError ? <p className="mt-2 text-xs text-rose-700">{pacingTimerError}</p> : null}
              </div>
            ) : null}
          </div>

          {urgencyToolsPanel}

          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Current Step Preview</p>
            {currentStep ? (
              <div className="mt-4">
                <StepRenderer
                  title={currentStep.title}
                  stageTag={currentStep.stage_tag}
                  stepKind={currentStep.step_kind}
                  content={currentStep.content}
                />
              </div>
            ) : (
              <p className="mt-3 text-sm text-gray-500">Waiting for the next step.</p>
            )}
          </div>
          {currentStepTeacherNotes ? (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-xs uppercase tracking-wide text-gray-400">Teacher Notes</p>
              <div className="mt-3 prose prose-sm max-w-none text-gray-800">
                <ReactMarkdown
                  components={{
                    code({ inline, children, ...props }: any) {
                      return !inline ? (
                        <pre className="overflow-x-auto rounded bg-slate-900 p-4 text-xs text-slate-100">
                          <code {...props}>{children}</code>
                        </pre>
                      ) : (
                        <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.9em] text-gray-800">
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {currentStepTeacherNotes}
                </ReactMarkdown>
              </div>
            </div>
          ) : null}
          {teacherAnswerKeyPanel}

          <OpsAssistPanel
            aiAssistLoading={aiAssistLoading}
            aiAssistError={aiAssistError}
            aiSuggestion={aiSuggestion}
            aiDelayRevealTriggered={aiDelayRevealTriggered}
            aiDelayRevealReason={aiDelayRevealReason}
            assistRankingsLoading={assistRankingsLoading}
            rankedAssistRows={rankedAssistRows}
            currentStepId={currentStepId}
            onMicroAssist={(actionType) => {
              void handleMicroAssist(actionType);
            }}
            onOpenExplainToClass={() => handleOpenExplainToClass({ tab: 'draft' })}
            onOpenTeacherAi={() => handleOpenTeacherAi('walkthrough')}
            onCodingStepByStep={() => {
              void handleCodingStepByStep();
            }}
            renderAssistFeedbackControls={renderAssistFeedbackControls}
          />
          </>
          )}

          {showOpsCardsInMain && (
          <OpsSubmissionsPanel
            submissionsInbox={submissionsInboxView}
            submissionsError={submissionsError}
            submissionsLoading={submissionsLoading}
            hideAbsentStudents={hideAbsentInSubmissions}
            hiddenAbsentCount={hiddenAbsentSubmissionCount}
            expandedSubmissionStudentIds={expandedSubmissionStudentIds}
            redoRequested={redoRequested}
            currentStepId={currentStepId}
            onRefresh={() => {
              void loadSubmissionsInbox();
            }}
            onToggleHideAbsentStudents={(nextValue) => {
              setHideAbsentInSubmissions(nextValue);
            }}
            onApproveEvidence={approveEvidence}
            onGateDecision={decideReflectionGate}
            onQualityDecision={decideSubmissionQuality}
            onRequestRedo={requestRedo}
            onToggleExpanded={(studentId) => {
              setExpandedSubmissionStudentIds((prev) => {
                const next = new Set(prev);
                if (next.has(studentId)) {
                  next.delete(studentId);
                } else {
                  next.add(studentId);
                }
                return next;
              });
            }}
            formatSignalResponse={formatSignalResponse}
          />
          )}

        </section>

        {/* Audience Roster */}
        {showOpsSidebar && (
        <aside className="w-full min-w-0 space-y-4 lg:w-[320px] lg:shrink-0 lg:min-h-0 lg:overflow-y-auto">
          <div className="w-full min-w-0 bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-400">
              Live Signals (Current Step)
              {currentStep?.title ? (
                <span className="normal-case tracking-normal text-gray-500">{` — ${currentStep.title}`}</span>
              ) : null}
            </p>
            {liveSignalStats ? (
              <div className="mt-3 space-y-2 text-sm text-gray-700">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  title={(liveSignalStats.bucket_student_ids?.submitted || [])
                    .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                    .join(', ')}
                  onClick={() => {
                    const key = 'submitted';
                    setActiveBucket((prev) => (prev === key ? null : key));
                  }}
                >
                  <span>Submitted</span>
                  <span
                    className={`font-semibold ${liveSignalStats.step_signal_pending === 0 ? 'text-green-600' : ''}`}
                  >
                    {liveSignalStats.step_signal_submitted} / {liveSignalStats.total_students}
                  </span>
                </div>
                <div
                  className="flex items-center justify-between cursor-pointer"
                  title={(liveSignalStats.bucket_student_ids?.pending || [])
                    .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                    .join(', ')}
                  onClick={() => {
                    const key = 'pending';
                    setActiveBucket((prev) => (prev === key ? null : key));
                  }}
                >
                  <span>Pending</span>
                  <span
                    className={`font-semibold ${liveSignalStats.step_signal_pending > 0 ? 'text-amber-600' : ''}`}
                  >
                    {liveSignalStats.step_signal_pending}
                  </span>
                </div>
                <div
                  className="flex items-center justify-between cursor-pointer"
                  title={(liveSignalStats.bucket_student_ids?.missing_reasoning || [])
                    .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                    .join(', ')}
                  onClick={() => {
                    const key = 'missing_reasoning';
                    setActiveBucket((prev) => (prev === key ? null : key));
                  }}
                >
                  <span>Missing reasoning</span>
                  <span className="font-semibold">{liveSignalStats.missing_reasoning}</span>
                </div>
                <div
                  className="flex items-center justify-between cursor-pointer"
                  title={(liveSignalStats.bucket_student_ids?.missing_confidence || [])
                    .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                    .join(', ')}
                  onClick={() => {
                    const key = 'missing_confidence';
                    setActiveBucket((prev) => (prev === key ? null : key));
                  }}
                >
                  <span>Missing confidence</span>
                  <span className="font-semibold">{liveSignalStats.missing_confidence}</span>
                </div>
                {hasAnswerKey ? (
                  <>
                    <div className="pt-1">
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Answer Check</p>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs">
                        <button
                          type="button"
                          className={`rounded-full border px-2 py-1 ${activeBucket === 'correct' ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}
                          title={(liveSignalStats.bucket_student_ids?.correct || [])
                            .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                            .join(', ')}
                          onClick={() => setActiveBucket((prev) => (prev === 'correct' ? null : 'correct'))}
                        >
                          Correct: {Number(liveSignalStats.correct_count || 0)}
                        </button>
                        <button
                          type="button"
                          className={`rounded-full border px-2 py-1 ${activeBucket === 'incorrect' ? 'border-red-400 bg-red-50 text-red-700' : 'border-red-200 text-red-700 hover:bg-red-50'}`}
                          title={(liveSignalStats.bucket_student_ids?.incorrect || [])
                            .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                            .join(', ')}
                          onClick={() => setActiveBucket((prev) => (prev === 'incorrect' ? null : 'incorrect'))}
                        >
                          Incorrect: {Number(liveSignalStats.incorrect_count || 0)}
                        </button>
                        {Number(liveSignalStats.unscored_count || 0) > 0 && (
                          <button
                            type="button"
                            className={`rounded-full border px-2 py-1 ${activeBucket === 'unscored' ? 'border-gray-400 bg-gray-100 text-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                            title={(liveSignalStats.bucket_student_ids?.unscored || [])
                              .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                              .join(', ')}
                            onClick={() => setActiveBucket((prev) => (prev === 'unscored' ? null : 'unscored'))}
                          >
                            Unscored: {Number(liveSignalStats.unscored_count || 0)}
                          </button>
                        )}
                      </div>
                    </div>
                    <details className="pt-1">
                      <summary className="cursor-pointer text-xs text-gray-500">Confidence clusters</summary>
                      <div className="mt-2 space-y-2">
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          title={(liveSignalStats.bucket_student_ids?.high_confidence_wrong || [])
                            .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                            .join(', ')}
                          onClick={() => {
                            const key = 'high_confidence_wrong';
                            setActiveBucket((prev) => (prev === key ? null : key));
                          }}
                        >
                          <span className="flex items-center gap-2 text-xs">
                            {liveSignalStats.high_confidence_wrong > 0 && (
                              <span className="w-2 h-2 rounded-full bg-red-500" />
                            )}
                            High-confidence wrong
                          </span>
                          <span
                            className={`font-semibold text-xs ${liveSignalStats.high_confidence_wrong > 0 ? 'text-red-600' : ''}`}
                          >
                            {liveSignalStats.high_confidence_wrong}
                          </span>
                        </div>
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          title={(liveSignalStats.bucket_student_ids?.low_confidence_wrong || [])
                            .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                            .join(', ')}
                          onClick={() => {
                            const key = 'low_confidence_wrong';
                            setActiveBucket((prev) => (prev === key ? null : key));
                          }}
                        >
                          <span className="flex items-center gap-2 text-xs">
                            {liveSignalStats.low_confidence_wrong > 0 && (
                              <span className="w-2 h-2 rounded-full bg-amber-500" />
                            )}
                            Low-confidence wrong
                          </span>
                          <span
                            className={`font-semibold text-xs ${liveSignalStats.low_confidence_wrong > 0 ? 'text-amber-600' : ''}`}
                          >
                            {liveSignalStats.low_confidence_wrong}
                          </span>
                        </div>
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          title={(liveSignalStats.bucket_student_ids?.low_confidence_right || [])
                            .map((id) => rosterNameById.get(id) || `Unknown (${id})`)
                            .join(', ')}
                          onClick={() => {
                            const key = 'low_confidence_right';
                            setActiveBucket((prev) => (prev === key ? null : key));
                          }}
                        >
                          <span className="flex items-center gap-2 text-xs">
                            {liveSignalStats.low_confidence_right > 0 && (
                              <span className="w-2 h-2 rounded-full bg-amber-500" />
                            )}
                            Low-confidence right
                          </span>
                          <span
                            className={`font-semibold text-xs ${liveSignalStats.low_confidence_right > 0 ? 'text-amber-600' : ''}`}
                          >
                            {liveSignalStats.low_confidence_right}
                          </span>
                        </div>
                      </div>
                    </details>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">
                    Correctness clusters unavailable ({blanksTotal > 0
                      ? `Answer key incomplete (${blanksKeyed}/${blanksTotal} keyed).`
                      : 'No answer key yet.'})
                  </p>
                )}
                {activeBucket && (
                  <div className="mt-3 rounded border border-gray-200 bg-white p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">
                        Filtered: {activeBucket.split('_').join(' ')} ({bucketNames.length})
                      </div>
                      <button
                        type="button"
                        className="text-xs text-gray-600 hover:text-gray-900"
                        onClick={() => setActiveBucket(null)}
                      >
                        Clear
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {bucketNames.length ? (
                        bucketNames.map((name, index) => (
                          <span key={`${name}-${index}`} className="text-xs rounded-full bg-gray-100 px-2 py-1">
                            {name}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-500">No students in this bucket.</span>
                      )}
                    </div>
                  </div>
                )}
                {currentStepHasSignal && currentStepRevealMode === 'teacher' && (
                  <div className="pt-2 space-y-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded border border-gray-200 text-xs text-gray-700 disabled:opacity-50"
                      onClick={handleRevealAnswers}
                      disabled={revealSubmitting || revealSuccess}
                    >
                      {revealSubmitting ? 'Revealing…' : 'Reveal answers'}
                    </button>
                    {revealSuccess && !revealError && (
                      <p className="text-xs text-gray-500">Revealed.</p>
                    )}
                    {revealError && (
                      <p className="text-xs text-red-600">{revealError}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-3 text-sm text-gray-500">Loading live signals…</p>
            )}
          </div>
          <OpsAdaptivePanel
            showReviewed={showReviewed}
            adaptiveLoading={adaptiveLoading}
            adaptiveError={adaptiveError}
            adaptiveSummary={adaptiveSummary}
            adaptiveSuggestions={adaptiveSuggestions}
            expandedAdaptiveTargets={expandedAdaptiveTargets}
            rosterNameById={rosterNameById}
            selectedStudentIds={selectedStudentIds}
            routeablePathwaySteps={routeablePathwaySteps}
            lastAdaptiveApplication={lastAdaptiveApplication}
            computeMinutesUntil={computeMinutesUntil}
            onRouteStudentsToStep={handleRouteStudentsToStep}
            onToggleShowReviewed={(nextValue) => setShowReviewed(nextValue)}
            onGenerateAdaptive={() => {
              void handleGenerateAdaptive();
            }}
            onClearOverridesToClassStep={() => {
              void handleClearOverridesToClassStep();
            }}
            onToggleAdaptiveTargets={(suggestionId) => {
              setExpandedAdaptiveTargets((prev) => {
                const next = new Set(prev);
                if (next.has(suggestionId)) {
                  next.delete(suggestionId);
                } else {
                  next.add(suggestionId);
                }
                return next;
              });
            }}
            onApproveApplySuggestion={(suggestionId, apply) => {
              void handleApproveApplySuggestion(suggestionId, apply);
            }}
            onRejectSuggestion={(suggestionId) => {
              void handleRejectSuggestion(suggestionId);
            }}
            onApplyApprovedSuggestion={(suggestionId) => {
              void handleApplyApprovedSuggestion(suggestionId);
            }}
          />
          <OpsAudiencePanel
            joinedStudentIds={controlData.joined_student_ids}
            enteredLiveStudentIds={controlData.entered_live_student_ids || []}
            livePresenceSummary={controlData.live_presence_summary || null}
            students={controlData.students}
            selectedStudentIds={selectedStudentIds}
            attendanceSavingStudentIds={attendanceSavingStudentIds}
            attendanceError={attendanceError}
            onSetAttendance={(studentId, attendanceStatus) => {
              void updateLiveAttendance(studentId, attendanceStatus);
            }}
            onToggleStudentSelection={toggleStudentSelection}
            onSelectStudentIds={replaceSelectedStudentIds}
            onPullSelected={() => {
              void updateAudience('join');
            }}
            onReleaseSelected={() => {
              void updateAudience('leave');
            }}
            onPullStudentIds={(studentIds) => {
              void updateAudience('join', studentIds);
            }}
            onReleaseStudentIds={(studentIds) => {
              void updateAudience('leave', studentIds);
            }}
            onReleaseAll={() => {
              void updateAudience('leave');
            }}
          />
        </aside>
        )}
        </div>
      </div>
      ) : null}

      {!workspaceModeEnabled ? (
      <Dialog
        open={mobileOpsDrawerOpen}
        onClose={() => setMobileOpsDrawerOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-end justify-center p-3 sm:items-center sm:p-6">
          <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <Dialog.Title className="text-lg font-semibold text-gray-900">Ops Drawer</Dialog.Title>
                <button
                  type="button"
                  className="rounded border border-gray-200 px-3 py-1 text-sm text-gray-700"
                  onClick={() => setMobileOpsDrawerOpen(false)}
                >
                  Close
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {([
                  { key: 'signals', label: 'Signals' },
                  { key: 'submissions', label: 'Submissions' },
                  { key: 'adaptive', label: 'Adaptive' },
                  { key: 'audience', label: 'Audience' },
                ] as Array<{ key: MobileOpsTab; label: string }>).map((tab) => (
                  <button
                    key={`mobile-ops-tab-${tab.key}`}
                    type="button"
                    className={`rounded border px-3 py-1.5 text-sm ${
                      mobileOpsTab === tab.key
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 bg-white text-gray-700'
                    }`}
                    onClick={() => setMobileOpsTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {mobileOpsTab === 'signals' && (
                <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  {liveSignalStats ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>Submitted: <span className="font-semibold">{liveSignalStats.step_signal_submitted}/{liveSignalStats.total_students}</span></div>
                      <div>Pending: <span className="font-semibold">{liveSignalStats.step_signal_pending}</span></div>
                      <div>Missing reasoning: <span className="font-semibold">{liveSignalStats.missing_reasoning}</span></div>
                      <div>Missing confidence: <span className="font-semibold">{liveSignalStats.missing_confidence}</span></div>
                    </div>
                  ) : (
                    <div>Loading live signals...</div>
                  )}
                </div>
              )}

              {mobileOpsTab === 'submissions' && (
                <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  <div className="font-medium text-gray-900">Unified submissions</div>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={!hideAbsentInSubmissions}
                      onChange={(event) => setHideAbsentInSubmissions(!event.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Include absent students
                  </label>
                  {submissionsInboxView?.summary ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>Students: <span className="font-semibold">{Number(submissionsInboxView.summary.students_total || 0)}</span></div>
                      <div>Live responses: <span className="font-semibold">{Number(submissionsInboxView.summary.signals_submitted || 0)}</span></div>
                      <div>Saved work: <span className="font-semibold">{Number(submissionsInboxView.summary.evidence_submitted || 0)}</span></div>
                      <div>Both types: <span className="font-semibold">{Number(submissionsInboxView.summary.both_submitted || 0)}</span></div>
                      <div>Q provisional: <span className="font-semibold">{Number(submissionsInboxView.summary.quality_provisional || 0)}</span></div>
                      <div>Q review: <span className="font-semibold">{Number(submissionsInboxView.summary.quality_review || 0)}</span></div>
                    </div>
                  ) : (
                    <div>No submissions summary yet.</div>
                  )}
                  {hideAbsentInSubmissions && hiddenAbsentSubmissionCount > 0 ? (
                    <div className="text-xs text-amber-700">
                      Hidden absent students: {hiddenAbsentSubmissionCount}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm"
                      onClick={() => void loadSubmissionsInbox()}
                    >
                      Refresh submissions
                    </button>
                    <button
                      type="button"
                      className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm"
                      onClick={openOpsSameTab}
                    >
                      Open full ops page
                    </button>
                  </div>
                </div>
              )}

              {mobileOpsTab === 'adaptive' && (
                <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  <div className="font-medium text-gray-900">Adaptive</div>
                  <div>
                    Pending suggestions: <span className="font-semibold">{adaptiveSuggestions.length}</span>
                  </div>
                  {adaptiveSummary ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>Aligned: <span className="font-semibold">{adaptiveSummary.aligned ?? 0}</span></div>
                      <div>Intervention: <span className="font-semibold">{adaptiveSummary.intervention ?? 0}</span></div>
                      <div>Acceleration: <span className="font-semibold">{adaptiveSummary.acceleration ?? 0}</span></div>
                      <div>Unknown: <span className="font-semibold">{adaptiveSummary.unknown ?? 0}</span></div>
                    </div>
                  ) : (
                    <div>No adaptive summary loaded yet.</div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-60"
                      onClick={handleGenerateAdaptive}
                      disabled={adaptiveLoading}
                    >
                      {adaptiveLoading ? 'Checking...' : 'Check adaptive'}
                    </button>
                    <button
                      type="button"
                      className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm"
                      onClick={openOpsSameTab}
                    >
                      Open full ops page
                    </button>
                  </div>
                </div>
              )}

              {mobileOpsTab === 'audience' && (
                <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  <div className="font-medium text-gray-900">Audience</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>Joined: <span className="font-semibold">{controlData.joined_student_ids.length}</span></div>
                    <div>Entered live: <span className="font-semibold">{(controlData.entered_live_student_ids || []).length}</span></div>
                  </div>
                  {controlData.live_presence_summary ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>Absent: <span className="font-semibold">{controlData.live_presence_summary.absent}</span></div>
                      <div>No work: <span className="font-semibold">{controlData.live_presence_summary.present_no_work}</span></div>
                      <div>Participated: <span className="font-semibold">{controlData.live_presence_summary.participated}</span></div>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm"
                    onClick={openOpsSameTab}
                  >
                    Open full audience controls
                  </button>
                </div>
              )}

            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
      ) : null}

      <Dialog open={materialsOpen} onClose={() => { setMaterialsOpen(false); setPresentationResource(null); setPresentationSlideIndex(0); }} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4 sm:p-6">
          <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div>
                <Dialog.Title className="text-lg font-semibold text-slate-950">
                  {presentationResource ? presentationResource.title : 'Lesson Materials'}
                </Dialog.Title>
                {presentationResource ? (
                  <p className="mt-1 text-sm text-slate-600">Showing only the slides selected in the lesson plan.</p>
                ) : (
                  <p className="mt-1 text-sm text-slate-600">
                    {(controlData.resources || []).length} available
                    {controlData.resources?.some((resource) => resource.needs_printing)
                      ? ` · ${controlData.resources.filter((resource) => resource.needs_printing).length} to print`
                      : ''}
                  </p>
                )}
              </div>
              <button type="button" className="rounded border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50" onClick={() => {
                if (presentationResource) {
                  setPresentationResource(null);
                  setPresentationSlideIndex(0);
                } else {
                  setMaterialsOpen(false);
                }
              }}>
                {presentationResource ? 'Back to materials' : 'Close'}
              </button>
            </div>
            {presentationResource ? (
              <div className="space-y-4 p-5">
                <div className="overflow-hidden rounded-xl border border-slate-300 bg-slate-950">
                  <img
                    src={presentationSlideImageUrl(presentationResource, presentationResource.selected_slides[presentationSlideIndex])}
                    alt={`${presentationResource.title}, slide ${presentationResource.selected_slides[presentationSlideIndex].slide_number}`}
                    className="mx-auto max-h-[65vh] w-full object-contain"
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 disabled:opacity-40"
                    disabled={presentationSlideIndex === 0}
                    onClick={() => setPresentationSlideIndex((value) => Math.max(0, value - 1))}
                  >
                    Previous slide
                  </button>
                  <span className="text-sm text-slate-600">
                    Selected slide {presentationSlideIndex + 1} of {presentationResource.selected_slides.length} · original slide {presentationResource.selected_slides[presentationSlideIndex].slide_number}
                  </span>
                  <button
                    type="button"
                    className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 disabled:opacity-40"
                    disabled={presentationSlideIndex === presentationResource.selected_slides.length - 1}
                    onClick={() => setPresentationSlideIndex((value) => Math.min(presentationResource.selected_slides.length - 1, value + 1))}
                  >
                    Next slide
                  </button>
                </div>
                <a href={presentationResource.src} target="_blank" rel="noreferrer" className="inline-flex text-sm font-medium text-indigo-700 underline">
                  Open complete deck in Google Slides
                </a>
              </div>
            ) : <div className="max-h-[70vh] space-y-3 overflow-y-auto p-5">
              {(controlData.resources || []).map((resource, index) => (
                <article key={`${resource.src}-${index}`} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-slate-950">{resource.title}</h3>
                      {resource.description ? <p className="mt-1 text-sm text-slate-600">{resource.description}</p> : null}
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                          {lessonResourceSourceLabel(lessonResourceSourceType(resource))}
                        </span>
                        <span className="rounded-full bg-indigo-50 px-2 py-1 text-indigo-700">
                          {resource.audience === 'teacher' ? 'Teacher only' : resource.audience === 'both' ? 'Teacher and students' : 'Students'}
                        </span>
                        {resource.availability === 'step' ? (
                          <span className="rounded-full bg-violet-50 px-2 py-1 text-violet-700">Step {resource.step_order}</span>
                        ) : null}
                        {resource.needs_printing ? <span className="rounded-full bg-amber-50 px-2 py-1 text-amber-800">Needs printing</span> : null}
                      </div>
                    </div>
                    {resource.presentation_mode === 'selected_slides' && resource.selected_slides?.length ? (
                      <button
                        type="button"
                        className="rounded bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700"
                        onClick={() => { setPresentationResource(resource); setPresentationSlideIndex(0); setMaterialsOpen(false); }}
                      >
                        Present selected slides
                      </button>
                    ) : (
                      <a href={resource.src} target="_blank" rel="noreferrer" className="rounded bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                        Open
                      </a>
                    )}
                  </div>
                </article>
              ))}
              <div className="border-t border-slate-200 pt-4">
                <h3 className="mb-3 text-sm font-semibold text-slate-950">Classroom readiness and release</h3>
                <ResourceOperationsPanel
                  lessonPlanId={Number(controlData.run.lesson_plan)}
                  sectionId={Number(controlData.run.section)}
                />
              </div>
            </div>}
          </Dialog.Panel>
        </div>
      </Dialog>

      <StepJumpControl
        open={stepJumpOpen}
        steps={controlData.steps}
        currentStepId={currentStepId}
        stepNavLoadingId={stepNavLoadingId}
        onClose={() => setStepJumpOpen(false)}
        onSelectStep={(step) => {
          void jumpToStep(step);
          setStepJumpOpen(false);
        }}
      />

      <Dialog
        open={runSettingsOpen}
        onClose={() => {
          if (runSettingsSaving) return;
            setRunSettingsOpen(false);
            setRunSettingsError(null);
          }}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          <div className="fixed inset-0 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
            <Dialog.Panel className="mt-2 flex w-full max-w-2xl max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-xl bg-white shadow-xl sm:mt-4 sm:max-h-[calc(100dvh-3rem)]">
              <div className="min-h-0 flex-1 overflow-y-auto p-6 space-y-5">
                <div>
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Run Settings</Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    Configure test/reporting and make-up tracking for this live run.
                  </p>
                </div>

                <section className="rounded border border-gray-200 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Run Flags</p>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
	                    <input
	                      type="checkbox"
	                      className="rounded"
	                      checked={runSettingsDraftTestMode}
	                      onChange={(event) => setRunSettingsDraftTestMode(event.target.checked)}
	                      disabled={runSettingsSaving || (!runSettingsDraftTestMode && testModeBlockedByStudentWork)}
	                    />
	                    Exclude this run from reports (Test Mode)
	                  </label>
	                  {testModeBlockedByStudentWork ? (
	                    <p className="text-xs text-amber-700">
	                      This run has responses from {realStudentResponseCount} students, so it is treated as a live lesson and stays in reports.
	                    </p>
	                  ) : (
	                    <p className="text-xs text-gray-500">
	                      Use test mode only for rehearsals or demos before multiple students submit work.
	                    </p>
	                  )}
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={runSettingsDraftTrackAssignment}
                      onChange={(event) => setRunSettingsDraftTrackAssignment(event.target.checked)}
                      disabled={runSettingsSaving}
                    />
                    Track as make-up assignment
                  </label>
                </section>

                <section className={`rounded border border-gray-200 p-4 space-y-3 ${!runSettingsDraftTrackAssignment ? 'opacity-60' : ''}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Make-up Targeting</p>
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    Target scope
                    <select
                      className="mt-1 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-700"
                      value={runSettingsDraftTargetScope}
                      onChange={(event) => setRunSettingsDraftTargetScope(event.target.value as TrackTargetScope)}
                      disabled={!runSettingsDraftTrackAssignment || runSettingsSaving}
                    >
                      {TRACK_TARGET_SCOPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    Due date (optional)
                    <input
                      type="datetime-local"
                      className="mt-1 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-700"
                      value={runSettingsDraftDueAt}
                      onChange={(event) => setRunSettingsDraftDueAt(event.target.value)}
                      disabled={!runSettingsDraftTrackAssignment || runSettingsSaving}
                    />
                  </label>

                  {runSettingsDraftTrackAssignment && runSettingsDraftTargetScope === 'custom' && (
                    <div className="rounded border border-indigo-100 bg-indigo-50 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-indigo-700">
                          Selected students ({runSettingsDraftStudentIds.length})
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="rounded border border-indigo-200 bg-white px-2 py-1 text-xs text-indigo-700 disabled:opacity-50"
                            onClick={() => setRunSettingsDraftStudentIds(normalizeStudentIds((controlData?.students || []).map((student) => student.id)))}
                            disabled={runSettingsSaving}
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="rounded border border-indigo-200 bg-white px-2 py-1 text-xs text-indigo-700 disabled:opacity-50"
                            onClick={() => setRunSettingsDraftStudentIds([])}
                            disabled={runSettingsSaving}
                          >
                            Clear all
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 max-h-44 space-y-1 overflow-y-auto rounded border border-indigo-100 bg-white p-2">
                        {(controlData?.students || []).map((student) => (
                          <label key={`run-settings-student-${student.id}`} className="flex items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50">
                            <input
                              type="checkbox"
                              className="rounded"
                              checked={runSettingsDraftStudentIds.includes(student.id)}
                              onChange={() => toggleRunSettingsStudentSelection(student.id)}
                              disabled={runSettingsSaving}
                            />
                            <span>{student.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </section>

                <section className="rounded border border-gray-200 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Student Review Mode</p>
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    Review access
                    <select
                      className="mt-1 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-700"
                      value={runSettingsDraftReviewAccess}
                      onChange={(event) => setRunSettingsDraftReviewAccess(event.target.value as 'off' | 'after_end' | 'always')}
                      disabled={runSettingsSaving}
                    >
                      <option value="off">Off</option>
                      <option value="after_end">After class ends</option>
                      <option value="always">Always</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={runSettingsDraftAllowCatchup}
                      onChange={(event) => setRunSettingsDraftAllowCatchup(event.target.checked)}
                      disabled={runSettingsSaving}
                    />
                    Allow catch-up from review
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={runSettingsDraftShowReviewAnswers}
                      onChange={(event) => setRunSettingsDraftShowReviewAnswers(event.target.checked)}
                      disabled={runSettingsSaving}
                    />
                    Show assessment answers in review
                  </label>
                  {runSettingsDraftAllowCatchup && !runSettingsDraftTrackAssignment && (
                    <p className="text-xs text-amber-700">
                      Catch-up uses make-up assignments. Enable “Track as make-up assignment” to target students.
                    </p>
                  )}
                </section>

                <section className="rounded border border-gray-200 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Target Preview</p>
                  {!runSettingsDraftTrackAssignment ? (
                    <p className="text-sm text-gray-500">Make-up tracking is off.</p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-700">
                        {runSettingsPreviewTargetIds.length} student{runSettingsPreviewTargetIds.length === 1 ? '' : 's'} targeted by current settings.
                      </p>
                      {trackAssignment && (
                        <p className="text-xs text-gray-500">
                          Active make-up queue: {activeMakeupQueueStudentIds.length} student{activeMakeupQueueStudentIds.length === 1 ? '' : 's'}.
                        </p>
                      )}
                      {runSettingsPreviewTargetNames.length > 0 && (
                        <p className="text-xs text-gray-500">
                          {runSettingsPreviewTargetNames.join(', ')}
                          {runSettingsPreviewTargetIds.length > runSettingsPreviewTargetNames.length
                            ? ` +${runSettingsPreviewTargetIds.length - runSettingsPreviewTargetNames.length} more`
                            : ''}
                        </p>
                      )}
                      {runSettingsDraftTrackAssignment && (trackAssignment || runSettingsPreviewTargetRows.length > 0) && (
                        <div className="mt-2 rounded border border-rose-100 bg-rose-50 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-xs font-medium uppercase tracking-wide text-rose-700">
                                Queue Overrides
                              </p>
                              <p className="mt-1 text-[11px] text-rose-700">
                                {queueOverrideContextText}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                className="rounded border border-indigo-300 bg-white px-2 py-1 text-xs font-medium text-indigo-700 disabled:opacity-60"
                                onClick={() => navigate('/v2/teacher/queue')}
                                disabled={runSettingsSaving || trackLoading}
                              >
                                Open Make-up Queue
                              </button>
                              {trackAssignment ? (
                                <>
                                  <button
                                    type="button"
                                    className="rounded border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 disabled:opacity-60"
                                    onClick={handleSelectAllMakeupRemovalTargets}
                                    disabled={runSettingsSaving || trackLoading || queueOverrideTargetRows.length === 0}
                                  >
                                    Select all
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 disabled:opacity-60"
                                    onClick={handleClearMakeupRemovalSelection}
                                    disabled={runSettingsSaving || trackLoading || makeupRemovalSelection.size === 0}
                                  >
                                    Clear
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded border border-rose-400 bg-rose-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-60"
                                    onClick={handleExcuseSelectedStudentsFromMakeup}
                                    disabled={runSettingsSaving || trackLoading || makeupRemovalSelection.size === 0}
                                  >
                                    Remove selected ({makeupRemovalSelection.size})
                                  </button>
                                </>
                              ) : (
                                <span className="text-xs text-rose-700">Save settings to enable removals.</span>
                              )}
                            </div>
                          </div>
                          <p className="mt-1 text-xs text-rose-700">
                            {trackAssignment
                              ? 'This list shows students currently active in the make-up queue.'
                              : 'This run does not have an active make-up assignment yet. Save settings first.'}
                          </p>
                          <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded border border-rose-100 bg-white p-2">
                            {queueOverrideTargetRows.length === 0 ? (
                              <p className="px-2 py-1 text-xs text-gray-500">
                                {trackAssignment
                                  ? 'No students are currently active in the make-up queue.'
                                  : 'No students in this target preview yet.'}
                              </p>
                            ) : (
                              queueOverrideTargetRows.map((row) => {
                                const removing = makeupExcusingStudentIds.has(row.id);
                                const selected = makeupRemovalSelection.has(row.id);
                                return (
                                  <div
                                    key={`makeup-override-row-${row.id}`}
                                    className="flex items-center justify-between gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <label className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        className="rounded"
                                        checked={selected}
                                        onChange={() => handleToggleMakeupRemovalSelection(row.id)}
                                        disabled={!trackAssignment || runSettingsSaving || trackLoading || removing}
                                      />
                                      <span>
                                        <span>{row.name}</span>
                                        <span className="mt-0.5 block max-w-[32rem] text-[11px] text-gray-500">
                                          {row.reason}
                                        </span>
                                      </span>
                                    </label>
                                    <button
                                      type="button"
                                      className="rounded border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 disabled:opacity-60"
                                      onClick={() => {
                                        void handleExcuseStudentFromMakeup(row.id);
                                      }}
                                      disabled={!trackAssignment || runSettingsSaving || trackLoading || removing}
                                    >
                                      {trackAssignment ? (removing ? 'Removing…' : 'Remove') : 'Save first'}
                                    </button>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </section>

                {runSettingsDraftTrackAssignment && trackSummary && (
                  <section className="rounded border border-gray-200 p-4 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Progress Snapshot</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700">Completed: {trackSummary.completed}</span>
                      <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-700">In progress: {trackSummary.in_progress}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">Not started: {trackSummary.not_started}</span>
                      <span className="rounded-full bg-purple-100 px-2 py-1 text-purple-700">Excused: {trackSummary.excused}</span>
                      <span className="text-gray-500">Total: {trackSummary.total}</span>
                    </div>
                    {trackSummary.total > 0 && (
                      <div className="h-2 w-full rounded bg-gray-200">
                        <div
                          className="h-2 rounded bg-indigo-500"
                          style={{ width: `${Math.round((trackSummary.completed / trackSummary.total) * 100)}%` }}
                        />
                      </div>
                    )}
                  </section>
                )}

              </div>
              <div className="shrink-0 border-t border-gray-100 bg-white/95 p-4 backdrop-blur sm:p-5">
                {runSettingsError && (
                  <div className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {runSettingsError}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    className="rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                    onClick={resetRunSettingsDraft}
                    disabled={runSettingsSaving}
                  >
                    Reset to defaults
                  </button>
                  <div className="flex items-center gap-2">
                    {runSettingsLastSavedLabel && (
                      <span className="text-xs text-gray-500">Last saved {runSettingsLastSavedLabel}</span>
                    )}
                    <button
                      type="button"
                      className="rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                      onClick={() => {
                        setRunSettingsOpen(false);
                        setRunSettingsError(null);
                      }}
                      disabled={runSettingsSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                      onClick={() => void handleRunSettingsSave()}
                      disabled={runSettingsSaving}
                    >
                      {runSettingsSaving ? 'Saving…' : 'Save settings'}
                    </button>
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </div>
      </Dialog>

      <Dialog
        open={endRunDialogOpen}
        onClose={() => {
          if (endRunSubmitting) return;
          setEndRunDialogOpen(false);
          setEndRunError(null);
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-6">
          <Dialog.Panel className="w-full max-w-xl rounded-xl bg-white shadow-xl">
            <div className="p-6 space-y-4">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                End lesson run
              </Dialog.Title>
              <p className="text-sm text-gray-600">
                Confirm attendance before ending the live run. Students who never entered are suggested as absent, but you can correct each status.
              </p>
              <div className="max-h-72 space-y-2 overflow-y-auto rounded border border-gray-200 p-3">
                {endRunAttendanceRows.map((row) => (
                  <div key={row.studentId} className="flex flex-wrap items-center justify-between gap-3 rounded bg-gray-50 px-3 py-2">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{row.name}</div>
                      <div className="text-xs text-gray-500">
                        {row.dailyAttendanceStatus
                          ? `Carried from ${row.attendanceLocalDate || 'today'}: ${row.dailyAttendanceStatus}`
                          : row.inferredStatus === 'absent'
                          ? 'Suggested absent: did not enter this live lesson'
                          : row.inferredStatus === 'participated'
                            ? 'Participated in this live lesson'
                            : 'Entered live lesson'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        aria-label={`Attendance for ${row.name}`}
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-sm"
                        value={row.status}
                        onChange={(event) => {
                          const nextStatus = event.target.value as 'present' | 'absent';
                          const autoPrepareMakeup = controlData?.run.auto_makeup_for_absent_live_lessons !== false;
                          setEndRunAttendance((current) => ({
                            ...current,
                            [row.studentId]: {
                              status: nextStatus,
                              assignMakeup: nextStatus === 'absent' && autoPrepareMakeup,
                            },
                          }));
                          if (nextStatus === 'absent' && autoPrepareMakeup) setEndRunCreateMakeup(true);
                        }}
                        disabled={endRunSubmitting}
                      >
                        <option value="present">Present</option>
                        <option value="absent">Absent</option>
                      </select>
                      {row.status === 'absent' && (
                        <label className="flex items-center gap-1.5 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            aria-label={`Assign make-up to ${row.name}`}
                            checked={row.assignMakeup}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setEndRunAttendance((current) => ({
                                ...current,
                                [row.studentId]: {
                                  ...current[row.studentId],
                                  assignMakeup: checked,
                                },
                              }));
                              if (checked) setEndRunCreateMakeup(true);
                            }}
                            disabled={endRunSubmitting}
                          />
                          Make-up
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Section auto-preparation is {controlData?.run.auto_makeup_for_absent_live_lessons === false ? 'off' : 'on'}.
              </p>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded"
                  checked={endRunCreateMakeup}
                  onChange={(event) => {
                    setEndRunCreateMakeup(event.target.checked);
                    setEndRunError(null);
                  }}
                  disabled={endRunSubmitting}
                />
                <span>Create make-up assignment now</span>
              </label>
              {endRunCreateMakeup && (
                <div className="rounded border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-900 space-y-2">
                  <div>
                    Target preview: <span className="font-semibold">
                      {endRunAttendanceRows.filter((row) => row.status === 'absent' && row.assignMakeup).length} confirmed absent
                    </span>
                  </div>
                  <label className="block text-xs text-indigo-800">
                    Due date (optional)
                    <input
                      type="datetime-local"
                      className="mt-1 w-full rounded border border-indigo-200 bg-white px-2 py-1 text-sm text-gray-700"
                      value={endRunDueAt}
                      onChange={(event) => setEndRunDueAt(event.target.value)}
                      disabled={endRunSubmitting}
                    />
                  </label>
                </div>
              )}
              {endRunError && (
                <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {endRunError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                  onClick={() => {
                    setEndRunDialogOpen(false);
                    setEndRunError(null);
                  }}
                  disabled={endRunSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                  onClick={() => void handleConfirmEndRun()}
                  disabled={endRunSubmitting}
                >
                  {endRunSubmitting ? 'Ending run...' : 'End run'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={classAiModalOpen} onClose={() => setClassAiModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-6">
          <Dialog.Panel className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
            <div className="flex max-h-[90vh] flex-col">
              <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-white px-6 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">Explain to Class</Dialog.Title>
                    <p className="mt-1 text-xs text-gray-500">
                      Draft the student-facing live explain message, or ask AI about the current step before you send anything.
                    </p>
                  </div>
                  {!currentStepAnswerRevealed ? (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800">
                      Answer stays hidden until reveal
                    </span>
                  ) : (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
                      Answer already revealed
                    </span>
                  )}
                </div>
                {currentStep ? (
                  <p className="mt-2 text-xs text-gray-500">
                    Current step: <span className="font-medium text-gray-700">{currentStep.title}</span>
                  </p>
                ) : null}
                <div className="mt-3 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                  <button
                    type="button"
                    className={`rounded px-3 py-1 text-xs ${classAiTab === 'draft' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setClassAiTab('draft')}
                  >
                    Draft
                  </button>
                  <button
                    type="button"
                    className={`rounded px-3 py-1 text-xs ${classAiTab === 'ask' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setClassAiTab('ask')}
                  >
                    Ask AI
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/50 px-6 py-5">
                {classAiTab === 'draft' ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Draft mode</p>
                          <p className="mt-1 text-xs text-gray-500">Choose the right amount of support, then edit before sending.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {[
                            ['quick_explain', 'Quick explain'],
                            ['step_by_step', 'Step-by-step'],
                            ['hint_only', 'Hint only'],
                          ].map(([value, label]) => (
                            <button
                              key={`class-draft-mode-${value}`}
                              type="button"
                              className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                                classDraftMode === value
                                  ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                  : 'border-gray-200 bg-white text-gray-700'
                              }`}
                              onClick={() => setClassDraftMode(value as ClassDraftMode)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                          onClick={() => void handleGenerateClassDraft(classDraftMode)}
                          disabled={!currentStepId || classDraftLoading || classDraftSending || classDraftSaving}
                        >
                          {classDraftLoading ? 'Generating...' : 'Generate'}
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                          onClick={() => void handleGenerateClassDraft(classDraftMode)}
                          disabled={!currentStepId || classDraftLoading || classDraftSending || classDraftSaving}
                        >
                          Regenerate
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                          onClick={handleClearClassDraft}
                          disabled={classDraftLoading || classDraftSending || classDraftSaving}
                        >
                          Clear
                        </button>
                      </div>
                      <div className="mt-4 border-t border-gray-100 pt-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                            Saved versions
                          </label>
                          <select
                            className="min-w-[14rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                            value={activeClassDraftId ?? ''}
                            onChange={(event) => {
                              const nextId = Number(event.target.value);
                              if (Number.isInteger(nextId) && nextId > 0) {
                                handleLoadClassSavedDraft(nextId);
                              } else {
                                applyClassDraftFromSaved(null);
                              }
                            }}
                            disabled={stepAiDraftsLoading || classDraftSaving}
                          >
                            <option value="">Unsaved working draft</option>
                            {classSavedDrafts.map((draft) => (
                              <option key={`class-saved-draft-${draft.id}`} value={draft.id}>
                                {draft.title}{draft.is_default ? ' (default)' : ''}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                            onClick={() => void handleSaveClassDraft('current')}
                            disabled={classDraftSaving || classDraftLoading || !classDraft.trim()}
                          >
                            {classDraftSaving ? 'Saving...' : 'Save current'}
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                            onClick={() => void handleSaveClassDraft('new')}
                            disabled={classDraftSaving || classDraftLoading || !classDraft.trim()}
                          >
                            Save as new
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                            onClick={() => void handleSetClassDraftDefault()}
                            disabled={classDraftSaving || !activeClassSavedDraft}
                          >
                            Set default
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-rose-200 bg-white px-3 py-1.5 text-sm text-rose-700 disabled:opacity-50"
                            onClick={() => void handleDeleteClassDraft()}
                            disabled={classDraftSaving || !activeClassSavedDraft}
                          >
                            Delete
                          </button>
                        </div>
                        {classDraftLibraryNotice ? (
                          <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                            {classDraftLibraryNotice}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Class draft preview
                        </label>
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700"
                          onClick={() => setClassDraftEditorOpen((prev) => !prev)}
                        >
                          {classDraftEditorOpen ? 'Hide raw editor' : 'Edit raw text'}
                        </button>
                      </div>
                      <div className="mt-3">
                        <DraftPreviewBody markdown={classDraft} />
                      </div>
                      {classDraftEditorOpen ? (
                        <textarea
                          className="mt-4 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                          rows={12}
                          value={classDraft}
                          onChange={(event) => {
                            setClassDraft(event.target.value);
                            setClassAiError(null);
                            setClassAiSuccess(null);
                            setClassDraftLibraryNotice(null);
                          }}
                          placeholder="Generate a draft or write your own class-facing explanation."
                        />
                      ) : null}
                      {renderAssistFeedbackControls()}
                      {classAiError ? (
                        <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                          {classAiError}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Ask AI about this step</p>
                      <p className="mt-1 text-xs text-gray-500">
                        This chat stays scoped to the current step. Use a reply as your draft if it is class-ready.
                      </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="max-h-[22rem] space-y-4 overflow-y-auto pr-1">
                        {classAskThreadLoading ? (
                          <div className="rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
                            Loading Ask AI thread...
                          </div>
                        ) : null}
                        {!classAskThreadLoading && classAskThread.messages.length === 0 ? (
                          <div className="rounded border border-gray-200 bg-slate-50 px-3 py-3 text-sm text-gray-600">
                            Ask for a shorter explanation, a stronger hint, or a better way to say this to students.
                          </div>
                        ) : null}
                        {classAskThread.messages.map((message) => (
                          <div key={`class-ai-thread-${message.id}`} className="space-y-2">
                            <div className="ml-auto max-w-[80%] rounded-2xl bg-slate-900 px-4 py-3 text-sm text-white">
                              <div className="text-[11px] uppercase tracking-wide text-slate-300">Teacher</div>
                              <div className="mt-1 whitespace-pre-wrap">{message.request_text}</div>
                            </div>
                            <div className="max-w-[88%] rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-950">
                              <div className="flex items-start justify-between gap-3">
                                <div className="text-[11px] uppercase tracking-wide text-indigo-600">Ask AI reply</div>
                                <button
                                  type="button"
                                  className="rounded border border-indigo-200 bg-white px-2.5 py-1 text-[11px] font-medium text-indigo-700"
                                  onClick={() => handleUseClassReplyAsDraft(message.response_md)}
                                >
                                  Use as draft
                                </button>
                              </div>
                              <div className="mt-2">
                                <MarkdownAssistBody markdown={message.response_md} variant="chat" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {classAskThreadError ? (
                        <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                          {classAskThreadError}
                        </div>
                      ) : null}
                      <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Ask AI
                      </label>
                      <textarea
                        className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                        rows={4}
                        value={classAskPrompt}
                        onChange={(event) => {
                          setClassAskPrompt(event.target.value);
                          setClassAskThreadError(null);
                        }}
                        placeholder="Ask for a clearer live explanation or a better student-facing phrasing."
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
                {classAiTab === 'ask' ? (
                  <button
                    type="button"
                    className="rounded border border-indigo-200 bg-white px-4 py-2 text-sm text-indigo-700"
                    onClick={() => setClassAiTab('draft')}
                  >
                    Back to Draft
                  </button>
                ) : null}
                <button
                  type="button"
                  className="rounded border border-gray-200 px-4 py-2 text-sm text-gray-700"
                  onClick={() => setClassAiModalOpen(false)}
                  disabled={classDraftLoading || classDraftSending || classAskSending}
                >
                  Cancel
                </button>
                {classAiTab === 'ask' ? (
                  <button
                    type="button"
                    className="rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50"
                    onClick={() => void handleSendClassAsk()}
                    disabled={classAskSending || !classAskPrompt.trim() || !currentStepId}
                  >
                    {classAskSending ? 'Sending...' : 'Send'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50"
                    onClick={() => void handleSendClassDraft()}
                    disabled={classDraftLoading || classDraftSending || !classDraft.trim() || !currentStepId}
                  >
                    {classDraftSending ? 'Sending...' : 'Send to Class'}
                  </button>
                )}
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={teacherAiModalOpen} onClose={() => setTeacherAiModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-6">
          <Dialog.Panel className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
            <div className="flex max-h-[90vh] flex-col">
              <div className="border-b border-gray-200 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">Teacher AI</Dialog.Title>
                    <p className="mt-1 text-xs text-gray-500">Teacher-only workspace for understanding the step or asking anything.</p>
                  </div>
                  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                    <button
                      type="button"
                      className={`rounded px-3 py-1 text-xs ${teacherAiTab === 'walkthrough' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                      onClick={() => setTeacherAiTab('walkthrough')}
                    >
                      Walkthrough
                    </button>
                    <button
                      type="button"
                      className={`rounded px-3 py-1 text-xs ${teacherAiTab === 'chat' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                      onClick={() => setTeacherAiTab('chat')}
                    >
                      Ask a question
                    </button>
                  </div>
                </div>
                {currentStep ? (
                  <p className="mt-2 text-xs text-gray-500">
                    Current step: <span className="font-medium text-gray-700">{currentStep.title}</span>
                  </p>
                ) : null}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/50 px-6 py-5">
                {teacherAiTab === 'walkthrough' ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Walkthrough controls</p>
                          <p className="mt-1 text-xs text-gray-500">Generate a teacher explanation, then move the class-safe phrasing into the class draft if you want to send it.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                              teacherWalkthroughMode === 'teacher_deep'
                                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-700'
                            }`}
                            onClick={() => {
                              setTeacherWalkthroughMode('teacher_deep');
                              void handleGenerateTeacherWalkthrough('teacher_deep', teacherWalkthroughAdjustment);
                            }}
                          >
                            Teacher deep
                          </button>
                          <button
                            type="button"
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                              teacherWalkthroughMode === 'student_friendly'
                                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-700'
                            }`}
                            onClick={() => {
                              setTeacherWalkthroughMode('student_friendly');
                              void handleGenerateTeacherWalkthrough('student_friendly', teacherWalkthroughAdjustment);
                            }}
                          >
                            Student-friendly
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {[
                          ['standard', 'Standard'],
                          ['shorter', 'Shorter'],
                          ['more_scaffolding', 'More scaffolding'],
                        ].map(([value, label]) => (
                          <button
                            key={`teacher-adjustment-${value}`}
                            type="button"
                            className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                              teacherWalkthroughAdjustment === value
                                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-700'
                            }`}
                            onClick={() => {
                              const nextAdjustment = value as TeacherWalkthroughAdjustment;
                              setTeacherWalkthroughAdjustment(nextAdjustment);
                              void handleGenerateTeacherWalkthrough(teacherWalkthroughMode, nextAdjustment);
                            }}
                            disabled={teacherWalkthroughLoading || !currentStepId}
                          >
                            {label}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                          onClick={() => void handleGenerateTeacherWalkthrough()}
                          disabled={teacherWalkthroughLoading || !currentStepId}
                        >
                          {teacherWalkthroughLoading ? 'Generating...' : 'Regenerate'}
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                          onClick={() => {
                            void copyText(teacherWalkthroughExportMd);
                          }}
                          disabled={!teacherWalkthroughExportMd.trim()}
                        >
                          Copy all
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                          onClick={() => {
                            const blocks = extractCodeBlocks(teacherWalkthroughExportMd).join('\n\n').trim();
                            if (blocks) void copyText(blocks);
                          }}
                          disabled={extractCodeBlocks(teacherWalkthroughExportMd).length === 0}
                        >
                          Copy code only
                        </button>
                        <button
                          type="button"
                          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                          onClick={handleUseWalkthroughInClassDraft}
                          disabled={!teacherWalkthroughExportMd.trim()}
                        >
                          Use in class draft
                        </button>
                      </div>
                      <div className="mt-4 border-t border-gray-100 pt-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                            Saved walkthroughs
                          </label>
                          <select
                            className="min-w-[14rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                            value={activeTeacherWalkthroughDraftId ?? ''}
                            onChange={(event) => {
                              const nextId = Number(event.target.value);
                              if (Number.isInteger(nextId) && nextId > 0) {
                                handleLoadTeacherWalkthroughSavedDraft(nextId);
                              } else {
                                applyTeacherWalkthroughFromSaved(null);
                              }
                            }}
                            disabled={teacherWalkthroughSaving || stepAiDraftsLoading}
                          >
                            <option value="">Unsaved working walkthrough</option>
                            {teacherWalkthroughSavedDrafts.map((draft) => (
                              <option key={`teacher-saved-draft-${draft.id}`} value={draft.id}>
                                {draft.title}{draft.is_default ? ' (default)' : ''}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                            onClick={() => void handleSaveTeacherWalkthrough('current')}
                            disabled={teacherWalkthroughSaving || teacherWalkthroughLoading || !teacherWalkthroughExportMd.trim()}
                          >
                            {teacherWalkthroughSaving ? 'Saving...' : 'Save current'}
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                            onClick={() => void handleSaveTeacherWalkthrough('new')}
                            disabled={teacherWalkthroughSaving || teacherWalkthroughLoading || !teacherWalkthroughExportMd.trim()}
                          >
                            Save as new
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                            onClick={() => void handleSetTeacherWalkthroughDefault()}
                            disabled={teacherWalkthroughSaving || !activeTeacherWalkthroughSavedDraft}
                          >
                            Set default
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-rose-200 bg-white px-3 py-1.5 text-sm text-rose-700 disabled:opacity-50"
                            onClick={() => void handleDeleteTeacherWalkthroughSavedDraft()}
                            disabled={teacherWalkthroughSaving || !activeTeacherWalkthroughSavedDraft}
                          >
                            Delete
                          </button>
                        </div>
                        {teacherWalkthroughLibraryNotice ? (
                          <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                            {teacherWalkthroughLibraryNotice}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {teacherWalkthroughError ? (
                      <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        {teacherWalkthroughError}
                      </div>
                    ) : null}
                    {teacherWalkthroughLoading ? (
                      <div className="rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
                        Generating walkthrough...
                      </div>
                    ) : null}
                    {teacherWalkthrough ? (
                      <div className="space-y-4">
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          {renderAssistFeedbackControls()}
                          <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-900 px-5 py-4 text-slate-50">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">Final Answer</div>
                            <div className="mt-2 text-lg font-semibold leading-7">
                              <MarkdownAssistBody markdown={teacherWalkthrough.final_answer} tone="dark" />
                            </div>
                          </div>
                        </div>
                        <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr]">
                          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Walk It Through</div>
                            <ol className="mt-4 space-y-4">
                              {teacherWalkthrough.step_by_step.map((item, index) => (
                                <li key={`teacher-walkthrough-step-${index}`} className="flex items-start gap-3">
                                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
                                    {index + 1}
                                  </div>
                                  <div className="min-w-0 flex-1 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3">
                                    <MarkdownAssistBody markdown={item} />
                                  </div>
                                </li>
                              ))}
                            </ol>
                          </div>
                          <div className="space-y-4">
                            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
                              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">Why Students Miss It</div>
                              <div className="mt-3 text-sm text-amber-950">
                                <MarkdownAssistBody markdown={teacherWalkthrough.why_students_miss_it} />
                              </div>
                            </div>
                            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5 shadow-sm">
                              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">What to Say Out Loud</div>
                              <div className="mt-3 rounded-2xl border border-sky-200 bg-white/80 px-4 py-4 text-sm text-sky-950">
                                <MarkdownAssistBody markdown={teacherWalkthrough.what_to_say_out_loud} />
                              </div>
                            </div>
                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
                              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Quick Check</div>
                              <div className="mt-3 text-sm font-medium text-emerald-950">
                                <MarkdownAssistBody markdown={teacherWalkthrough.quick_check} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : !teacherWalkthroughLoading ? (
                      <div className="rounded border border-gray-200 bg-white px-3 py-3 text-sm text-gray-600">
                        Generate a walkthrough to see teacher-only guidance for this step.
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={teacherChatUseLessonContext}
                          onChange={(event) => setTeacherChatUseLessonContext(event.target.checked)}
                        />
                        Use lesson context
                      </label>
                      <p className="mt-2 text-xs text-gray-500">
                        This thread is run-scoped and can answer open-topic questions. Lesson context is included by default.
                      </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="max-h-[24rem] space-y-4 overflow-y-auto pr-1">
                        {teacherChatThreadLoading ? (
                          <div className="rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
                            Loading teacher chat...
                          </div>
                        ) : null}
                        {!teacherChatThreadLoading && teacherChatThread.messages.length === 0 ? (
                          <div className="rounded border border-gray-200 bg-slate-50 px-3 py-3 text-sm text-gray-600">
                            Ask anything. This thread persists for the whole run.
                          </div>
                        ) : null}
                        {teacherChatThread.messages.map((message) => (
                          <div key={`teacher-chat-${message.id}`} className="space-y-2">
                            <div className="ml-auto max-w-[80%] rounded-2xl bg-slate-900 px-4 py-3 text-sm text-white">
                              <div className="text-[11px] uppercase tracking-wide text-slate-300">Teacher</div>
                              <div className="mt-1 whitespace-pre-wrap">{message.request_text}</div>
                            </div>
                            <div className="max-w-[88%] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900">
                              <div className="text-[11px] uppercase tracking-wide text-slate-500">Teacher AI</div>
                              <div className="mt-2">
                                <MarkdownAssistBody markdown={message.response_md} variant="chat" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {teacherChatThreadError ? (
                        <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                          {teacherChatThreadError}
                        </div>
                      ) : null}
                      <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Ask a question
                      </label>
                      <textarea
                        className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                        rows={4}
                        value={teacherChatPrompt}
                        onChange={(event) => {
                          setTeacherChatPrompt(event.target.value);
                          setTeacherChatThreadError(null);
                        }}
                        placeholder="Ask about this step, teaching moves, pacing, or something unrelated."
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
                <button
                  type="button"
                  className="rounded border border-gray-200 px-4 py-2 text-sm text-gray-700"
                  onClick={() => setTeacherAiModalOpen(false)}
                  disabled={teacherWalkthroughLoading || teacherChatSending}
                >
                  Close
                </button>
                {teacherAiTab === 'chat' ? (
                  <button
                    type="button"
                    className="rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50"
                    onClick={() => void handleSendTeacherChat()}
                    disabled={teacherChatSending || !teacherChatPrompt.trim()}
                  >
                    {teacherChatSending ? 'Sending...' : 'Send'}
                  </button>
                ) : null}
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={stepByStepModalOpen} onClose={() => setStepByStepModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-6">
          <Dialog.Panel className="w-full max-w-6xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
            <div className="flex max-h-[90vh] flex-col">
              <div className="border-b border-gray-200 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">{stepByStepModalTitle}</Dialog.Title>
                    <p className="mt-1 text-xs text-gray-500">Teacher-only assist. Not visible to students.</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                        Mode: {stepByStepMode === 'teacher_deep' ? 'Teacher deep' : 'Student friendly'}
                      </span>
                      <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700">
                        Detail: {stepByStepAdjustment === 'more_scaffolding' ? 'More scaffolding' : stepByStepAdjustment === 'shorter' ? 'Shorter' : 'Standard'}
                      </span>
                    </div>
                    <div className="mt-3 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                      <button
                        type="button"
                        className={`rounded px-3 py-1 text-xs ${stepByStepMode === 'teacher_deep' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                        onClick={() => void handleSwitchStepByStepMode('teacher_deep')}
                        disabled={aiAssistLoading}
                      >
                        Teacher Deep Walkthrough
                      </button>
                      <button
                        type="button"
                        className={`rounded px-3 py-1 text-xs ${stepByStepMode === 'student_friendly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                        onClick={() => void handleSwitchStepByStepMode('student_friendly')}
                        disabled={aiAssistLoading}
                      >
                        Student-Friendly Walkthrough
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-700 disabled:opacity-50"
                        onClick={handleCopyStepByStepAll}
                        disabled={aiAssistLoading || (!aiStepByStep && aiCommonMistakes.length === 0)}
                      >
                        Copy all
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-700 disabled:opacity-50"
                        onClick={handleCopyStepByStepCode}
                        disabled={aiAssistLoading || !aiStepByStep}
                      >
                        Copy code only
                      </button>
                      <button
                        type="button"
                        className={`rounded-md border px-2.5 py-1 text-xs disabled:opacity-50 ${
                          stepByStepAdjustment === 'shorter'
                            ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 text-gray-700'
                        }`}
                        onClick={() => void handleStepByStepAdjustment('shorter')}
                        disabled={aiAssistLoading || !currentStepId}
                      >
                        Shorter
                      </button>
                      <button
                        type="button"
                        className={`rounded-md border px-2.5 py-1 text-xs disabled:opacity-50 ${
                          stepByStepAdjustment === 'more_scaffolding'
                            ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 text-gray-700'
                        }`}
                        onClick={() => void handleStepByStepAdjustment('more_scaffolding')}
                        disabled={aiAssistLoading || !currentStepId}
                      >
                        More scaffolding
                      </button>
                      {stepByStepCopyStatus === 'copied_all' ? (
                        <span className="text-xs text-emerald-700">Copied all.</span>
                      ) : null}
                      {stepByStepCopyStatus === 'copied_code' ? (
                        <span className="text-xs text-emerald-700">Copied code.</span>
                      ) : null}
                      {stepByStepCopyStatus === 'empty' ? (
                        <span className="text-xs text-amber-700">Nothing to copy.</span>
                      ) : null}
                      {stepByStepCopyStatus === 'failed' ? (
                        <span className="text-xs text-rose-700">Copy failed.</span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                    onClick={() => {
                      if (stepByStepActionType === 'coding_step_by_step') {
                        void requestStepByStepAssist('coding_step_by_step', 'Coding step-by-step', stepByStepMode, stepByStepAdjustment);
                        return;
                      }
                      void requestStepByStepAssist('explain_step_by_step', 'Step-by-step walkthrough', stepByStepMode, stepByStepAdjustment);
                    }}
                    disabled={aiAssistLoading || !currentStepId}
                  >
                    {aiAssistLoading ? 'Generating...' : 'Regenerate'}
                  </button>
                </div>
                {currentStep && (
                  <p className="mt-2 text-xs text-gray-500">
                    Current step: <span className="font-medium text-gray-700">{currentStep.title}</span>
                  </p>
                )}
                {currentStepPromptMd ? (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-white/80 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Question focus</p>
                    <p className="mt-1 max-h-10 overflow-hidden text-sm text-slate-700">{currentStepPromptMd.replace(/\s+/g, ' ')}</p>
                  </div>
                ) : null}
                {renderAssistFeedbackControls()}
              </div>
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto bg-slate-50/50 px-6 py-5">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Snippet Selector</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {sendableWalkthroughSections.map((section) => (
                      <label
                        key={`snippet-${section.key}`}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-slate-50 px-2.5 py-1 text-xs text-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={snippetSectionKeys.includes(section.key)}
                          onChange={() => toggleSnippetSection(section.key)}
                          className="rounded"
                          disabled={aiAssistLoading}
                        />
                        {section.label}
                      </label>
                    ))}
                    {sendableWalkthroughSections.length === 0 ? (
                      <span className="text-xs text-gray-500">Generate a walkthrough to select sections.</span>
                    ) : null}
                  </div>
                  <textarea
                    className="mt-3 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-inner"
                    rows={6}
                    value={snippetDraft}
                    onChange={(event) => {
                      setSnippetDraft(event.target.value);
                      setSnippetSendError(null);
                      setSnippetSendSuccess(null);
                    }}
                    placeholder="Selected snippet preview..."
                  />
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-gray-500">Edit before sending if needed. This posts as the live Explain message.</p>
                    <button
                      type="button"
                      className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                      onClick={handleSendSelectedSnippet}
                      disabled={aiAssistLoading || snippetUseLoading || !currentStepId || !snippetDraft.trim()}
                    >
                      {snippetUseLoading ? 'Using...' : 'Use in class draft'}
                    </button>
                  </div>
                  {snippetSendError ? (
                    <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
                      {snippetSendError}
                    </div>
                  ) : null}
                  {snippetSendSuccess ? (
                    <div className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-700">
                      {snippetSendSuccess}
                    </div>
                  ) : null}
                </div>
                {aiAssistError ? (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {aiAssistError}
                  </div>
                ) : null}
                {aiAssistLoading ? (
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
                    Generating step-by-step guidance...
                  </div>
                ) : null}
                {!aiAssistLoading && !aiStepByStep ? (
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
                    No step-by-step content yet.
                  </div>
                ) : null}
                {aiStepByStep ? (
                  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-600">Walkthrough Output</p>
                    <div className="prose prose-sm max-w-none text-gray-800">
                      <ReactMarkdown
                        components={{
                          code({ inline, children, ...props }: any) {
                            return !inline ? (
                              <pre className="overflow-x-auto rounded bg-slate-900 p-4 text-xs text-slate-100">
                                <code {...props}>{children}</code>
                              </pre>
                            ) : (
                              <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.9em] text-gray-800">
                                {children}
                              </code>
                            );
                          },
                        }}
                      >
                        {aiStepByStep}
                      </ReactMarkdown>
                    </div>
                  </div>
                ) : null}
                {aiCommonMistakes.length > 0 ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Common mistakes</p>
                    <ul className="mt-2 list-disc pl-5 text-sm text-amber-900">
                      {aiCommonMistakes.map((item, idx) => (
                        <li key={`modal-mistake-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <div className="flex justify-end border-t border-gray-200 px-6 py-4">
                <button
                  type="button"
                  className="rounded border border-gray-200 px-4 py-2 text-sm text-gray-700"
                  onClick={() => setStepByStepModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
    </div>
  );

  if (embeddedRunId || !redesignEnabled) return controlSurface;

  return (
    <AppShell
      role="teacher"
      title={isOpsSurface ? 'Lesson Ops' : 'Lesson Control'}
      className="max-w-none px-0 py-0"
    >
      {controlSurface}
    </AppShell>
  );
};
